import { createContext, useContext, useState, useEffect, useRef, useMemo } from "react";
// DISABLED: LogRocket subscription suspended
// import LogRocket from "logrocket";
// REMOVED: Realm SDK - MongoDB Atlas App Services reached EOL (Jan 13, 2026)
// import * as Realm from "realm-web";
import {
  MONGODB_DATASOURCE_TS,
  MONGODB_COLLECTION_ACTIVEQUEUE,
  MONGODB_COLLECTION_CANDIDATES,
  MONGODB_COLLECTION_ATTRIBUTES,
  MONGODB_COLLECTION_USERS,
  MONGODB_COLLECTION_OFFICES,
  MONGODB_COLLECTION_LOGS,
  MONGODB_AWSGW_APIKEY,
  MONGODB_AWSGW_QUERIES,
  MONGODB_AWSGW_MUTATIONS,
  MONGODB_MAXLIMIT_USERS,
  MONGODB_MAXLIMIT_PROJECTSBYCOMPANY,
  MONGODB_MAXLIMIT_CANDIDATESBYCOMPANY,
  MONGODB_MAXLIMIT_CANDIDATESBYPAYROLLNOLIST,
  MONGODB_MAXLIMIT_PROJECTSBYPAYROLLNOLIST,
  MONGODB_MAXLIMIT_ATTRIBUTESBYLOCATION,
  MONGODB_MAXLIMIT_LOGS,
  MONGODB_ATTRIBUTES_DAYS_PAST,
  MONGODB_POLLING_INTERVAL_PROJECTS,
  MONGODB_POLLING_INTERVAL_ATTRIBUTES,
  MONGODB_POLLING_LOG_BATCH_INTERVAL,
  MONGODB_POLLING_IDLE_TIMEOUT,
  // REMOVED: REALM_APP_ID - Realm SDK no longer used
  DOCUMENT_ATTRIBUTES_TEMPLATE,
  APP_VERSION,
} from "../utility/keys";
import { createWideEvent } from "../utility/wideEventLogger";
import {
  AppContext,
  defaultUImode,
  defaultJobTitleConfig,
  defaultCompanyCode,
  defaultLocationFilter,
  defaultStartDate,
  defaultTab,
  defaultFilters,
  defaultScreenAccessList,
} from "./app.context";
// ModelRwContext removed - Realm EOL Jan 13, 2026
// States migrated to this context (lines 156-161)
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { performLogoutClears } from "../utility/logoutClears";
import { formatLockedByFromProfile } from "../utility/formatLockedBy";
import { shouldCreateNewUser } from "../utility/shouldCreateNewUser";
import { buildStateAndEventsRequestBody } from "../utility/buildStateAndEventsRequestBody";
import { parseStateAndEventsResponse } from "../utility/parseStateAndEventsResponse";
import { replayState } from "../utility/replayState";
import { computeScheduleDiff } from "../utility/computeScheduleDiff";
import { isSliderAtLocalDayEnd } from "../utility/isSliderAtLocalDayEnd";
import {
  applyOptimisticAttributeFieldsByMongoId,
  applyOptimisticAttributeCreate,
  backfillOptimisticAttributeMongoId,
} from "../utility/applyOptimisticAttributeUpdate";

// - - - - - - - - - - - - EXPLANATIONS

// EXPLANATION OF CREWQUERYLIST/AdditionalCandidateQueryList:
    //    candidates are queried by location & additionalLocations fields
    //    jobs are queried by location field
    //    when a job is in the foundSet and assigned to an installer NOT included in the candidate foundSet,
    //       then another query would be performed to add that installer to the
    //       candidate foundSet so that the assigned, scheduled job(s) would be displayed correctly
    //
    //    find/handle previously "invisible" jobs
    //      i.e. scheduled, assigned job(s) for installers not in this location
//
//
// EXPLANATION OF getProjectsByPayrollNoList/projectQueryList:
//        add shared installer assigned jobs, from other locations, to current location's projectsList
//
//     candidatesResult has been mapped and shared installers marked
    //    and added their payrollNo's to this list.
    //    So now query the list for their assigned jobs/projects
    //    and add them to the projectsList
//
//
// EXPLANATION OF processSharedInstallerAttributeDefaults:
//        every installer has attributeDefaults for each location they work in
//        but shared installers work in multiple locations, so if this location
//        has not been used before by that shared installer, then there will be 
//        no attributeDefaults for that shared installer for this location
//
//        loops thru candidateslist to find marked shared installers
//        and if found, checks attributesList for existing attributeDefaults for that installer
//        if not found, creates default attributeDefaults for that shared installer


// - - - - - - - - - - - - - - - - - - - -

export const ModelTsContext = createContext();

// - - - - - - - - - -

export const ModelTsContextProvider = ({ children }) => {
  const {
    appEnvirons,
    companyCode,
    setCompanyCode,
    locationFilter,
    setLocationFilter,
    // locationArray, // DISABLED: TanStack-primary sets this directly (Dec 2025)
    setLocationArray,
    dataFormatting_payrollFields,
    createUuid,
    whichList,
    setWhichList,
    weekDatesArray,
    parameterLocation,
    setParameterLocation,
    parameterWeek,
    // (#79) setParameterWeek removed from destructure here — #105 phase 4
    // (commit cbf2f84, v04-28-2026-01) removed the setParameterWeek("")
    // clear from setupUserSettings so OptimizerScreen could consume the
    // value when it mounts. The setter is therefore no longer needed at
    // this destructure site.
    setMondayThisWeek,
    setScreenAccess,
    setLockAccess_unassigned,
    setLockAccess_assigned,
    setCurrentFilters,
    setDarkMode,
    setJobTitleConfig,
    // For setUserPrefsById mutation
    darkMode,
    currentFilters,
    countsToggle,
    jobTitleConfig,
    // Updated timestamp for "Updated: X ago" display
    setLatestChangeTS,
    // Modal state - pause polling when ANY modal is open to prevent race conditions
    isAnyModalOpen,
    // Time Machine #114 Phase 4.5 — read-only time-travel mode (gates live
    // queries off, time-travel queries on, and blocks all mutations).
    // timeTravelDate drives the per-day Lambda fetch (queryKey).
    // timeTravelSliderTime drives the in-memory replay derivation (useMemo).
    isTimeTraveling,
    timeTravelDate,
    timeTravelSliderTime,
    setTimeTravelSliderTime,
    // Time Machine #114 Phase 6 — Diff Pair view (Optimizer split-screen).
    // diffPairRightSliderTime is T2; auto-snapped here to the next event after
    // T1 whenever T1 changes (or when DiffPair flips on). User may also drag
    // T2 manually within [T1, end-of-day] (iter 6).
    isDiffPairView,
    diffPairRightSliderTime,
    setDiffPairRightSliderTime,
    // Default new user record template (for auto-creating new users)
    defaultNewUserRecord,
  } = useContext(AppContext);
  // ModelRwContext removed - Realm EOL Jan 13, 2026
  // States migrated to local useState declarations below
  // Aliases defined after state declarations (see line ~200)

  // mongoDatabase used to be stored in "keys.js" but now in ".env.xxx"; state= appEnvirons from appContext
  // (#79) setter never called — value is fixed by env at app startup. Underscore-
  // prefixed setter signals intentional retention if we ever need to set in tests.
  // eslint-disable-next-line no-unused-vars
  const [mongoDatabase, _setMongoDatabase] = useState(appEnvirons.mongoDb);

  // MongoDB User state (for MSAL authentication)
  // REMOVED: mongoApp - Realm SDK no longer used (Atlas App Services EOL Jan 13, 2026)
  // mongoUser is now a simple object with id, name, email extracted from JWT
  const [mongoUser, setMongoUser] = useState(null);
  const [userLoading, setUserLoading] = useState(false);
  const [projectQueryList, setProjectQueryList] = useState([]);
  const [candidatesAdditionalQueryList, setCandidatesAdditionalQueryList] = useState([]);
  const [projectListAfterMerge, setProjectListAfterMerge] = useState([]);
  const [projectListAfterMergeLocation, setProjectListAfterMergeLocation] = useState(""); // Track which location the data belongs to

  // States migrated from ModelRwContext (Realm EOL Jan 13, 2026)
  // (#79) officeDocument and requestedUsersList variables are written by
  // setter calls but never READ (gated only by `conversion !== "realmOnly"`,
  // and the read side was removed during Realm cleanup). Underscore-prefixed
  // variable names mark intentional retention; setters preserve the existing
  // write logic for ModelRW cleanup stages 5-9 to evaluate.
  // eslint-disable-next-line no-unused-vars
  const [_officeDocument, setOfficeDocument] = useState({});
  // eslint-disable-next-line no-unused-vars
  const [_requestedUsersList, setRequestedUsersList] = useState([]);
  const [projectsListBeforeMerge, setProjectsListBeforeMerge] = useState([]);
  // requestedProjectsList is the inverse: variable IS read by Realm-leftover
  // code paths (~line 7019) but the setter is unused. Underscore-prefixed
  // setter signals intentional retention.
  // eslint-disable-next-line no-unused-vars
  const [requestedProjectsList, _setRequestedProjectsList] = useState([]);
  const [attributesList, setAttributesList] = useState([]);

  const [awsgwUniqueLocationsResult, setAwsgwUniqueLocationsResult] = useState(
    {}
  );
  const [shouldFetchUniqueLocations, setShouldFetchUniqueLocations] =
    useState(false);

  const [awsgwOfficeMetaDataResult, setAwsgwOfficeMetaDataResult] = useState(
    {}
  );
  const [shouldFetchOfficeMetaData, setShouldFetchOfficeMetaData] =
    useState(false);

  const [awsgwUserResult, setAwsgwUserResult] = useState({});
  const [shouldFetchUser, setShouldFetchUser] = useState(false);
  const [tsUserDocument, setTsUserDocument] = useState({}); // TanStack version of userDocument (object, not array)

  const [awsgwUsersResult, setAwsgwUsersResult] = useState([]);
  const [shouldFetchUsers, setShouldFetchUsers] = useState(false);

  const [awsgwProjectResult, setAwsgwProjectResult] = useState({});
  const [shouldFetchProject, setShouldFetchProject] = useState(false);

  const [awsgwProjectsResult, setAwsgwProjectsResult] = useState([]);
  const [shouldFetchProjects, setShouldFetchProjects] = useState(false);

  const [awsgwCandidatesResult, setAwsgwCandidatesResult] = useState([]);
  const [awsgwCandidatesResultLocation, setAwsgwCandidatesResultLocation] = useState(""); // Track which location the candidates data belongs to
  const [shouldFetchCandidates, setShouldFetchCandidates] = useState(false);

  const [awsgwCandidatesAdditionalResult, setAwsgwCandidatesAdditionalResult] =
    useState([]);
  const [shouldFetchCandidatesAdditional, setShouldFetchCandidatesAdditional] =
    useState(false);

  const [awsgwProjectsAdditionalResult, setAwsgwProjectsAdditionalResult] =
    useState([]);
  const [shouldFetchProjectsAdditional, setShouldFetchProjectsAdditional] =
    useState(false);

  // Store Realm's shared installer projects before Realm clears them
  const [realmRequestedProjectsList, setRealmRequestedProjectsList] = useState(
    []
  );

  const [awsgwAttributesResult, setAwsgwAttributesResult] = useState([]);
  const [awsgwAttributesResultLocation, setAwsgwAttributesResultLocation] = useState(""); // Track which location the attributes data belongs to
  const [shouldFetchAttributes, setShouldFetchAttributes] = useState(false);

  // Time Machine #114 Phase 4.5 — no useState slots for historical caches;
  // see notes below the 3 useQueries. We derive everything via useMemo
  // directly from each useQuery's `data`. Reason: setState side effects in
  // queryFn don't re-fire on cached re-renders (TanStack reuses prior data
  // for identical queryKeys), so exit + re-entry with the same date would
  // leave the state empty. Reading from `data` makes the cache itself the
  // source of truth — re-entry is instant and shows cached data without
  // needing the queryFn to re-run.

  // Flag to indicate initial attributes have been loaded
  // After initial load, mutations update attributesList directly (like Realm did)
  // and THE BIG MOVE should not overwrite with polling data
  // (#79) variable is never read — only setter is called as a dead write. The
  // gate that used to read this was removed but the writes remained. Underscore-
  // prefixed name is the eslint convention for intentionally-unused.
  // eslint-disable-next-line no-unused-vars
  const [_attributesInitialLoadComplete, setAttributesInitialLoadComplete] = useState(false);

  // Aliases for backward compatibility with existing code (Realm EOL Jan 13, 2026)
  // These allow existing variable names to reference TanStack data
  const projectsList = projectListAfterMerge; // Alias: use TanStack data
  const candidatesList = awsgwCandidatesResult; // Alias: use TanStack data

  // Helper to get the correct attributes data source
  // Uses awsgwAttributesResult if it's loaded AND matches the current location
  // Falls back to attributesList only if awsgwAttributesResult isn't available for current location
  // This prevents using stale data from a different location during location switches
  const getAttributesSource = () => {
    const isAwsgwAttributesValidForLocation =
      awsgwAttributesResult.length > 0 &&
      awsgwAttributesResultLocation === locationFilter;
    return isAwsgwAttributesValidForLocation ? awsgwAttributesResult : attributesList;
  };

  // Polling control - separate from initial fetch
  const [shouldPollProjects, setShouldPollProjects] = useState(false);
  const [pollingResultsBuffer, setPollingResultsBuffer] = useState([]);
  const [lastPollingLogTime, setLastPollingLogTime] = useState(null);
  const lastUserActivityTime = useRef(Date.now());
  // Initialize idle state based on actual window focus/visibility
  // This prevents polling from starting when the app loads but window isn't focused
  const [isUserIdle, setIsUserIdle] = useState(() => {
    return document.hidden || !document.hasFocus();
  });

  // Temporary polling override - when a change stream event arrives while idle,
  // temporarily resume polling for verification (up to 5 polls, then pause again)
  const [changeStreamPollingOverride, setChangeStreamPollingOverride] = useState({
    active: false,
    remainingPolls: 0,
    triggeredAt: null,
  });

  // Temporary attributes polling override - similar to projects, for attributes change stream verification
  const [attributesChangeStreamPollingOverride, setAttributesChangeStreamPollingOverride] = useState({
    active: false,
    remainingPolls: 0,
    triggeredAt: null,
  });

  // Attributes polling control - separate from initial fetch (10-second interval)
  const [shouldPollAttributes, setShouldPollAttributes] = useState(false);
  const [attributesPollingResultsBuffer, setAttributesPollingResultsBuffer] = useState([]);
  const [lastAttributesPollingLogTime, setLastAttributesPollingLogTime] = useState(null);

  const [awsgwLogsResult, setAwsgwLogsResult] = useState([]);
  const [shouldFetchLogs, setShouldFetchLogs] = useState(false);
  const isInitialLogsFetch = useRef(true); // Track if this is the first fetch
  const hasLoggedUsers = useRef(false); // Track if users log entry created
  const hasLoggedLogs = useRef(false); // Track if logs log entry created
  const hasLoggedCandidatesAdditional = useRef(false); // Track if candidatesAdditional log entry created
  const hasLoggedUniqueLocations = useRef(false); // Track if uniqueLocations log entry created
  const hasLoggedOfficeMetaData = useRef(false); // Track if officeMetaData log entry created
  const hasCompletedUniqueLocations = useRef(false); // Track if uniqueLocations marked complete
  const hasCompletedOfficeMetaData = useRef(false); // Track if officeMetaData marked complete
  const hasCompletedProjectsBeforeMerge = useRef(false); // Track if projectsBeforeMerge marked complete
  const hasCompletedCandidatesList = useRef(false); // Track if candidatesList marked complete
  const hasCompletedAttributes = useRef(false); // Track if attributes marked complete
  const hasCompletedUser = useRef(false); // Track if user (getUserByEmail) marked complete
  const hasCompletedUsers = useRef(false); // Track if users marked complete (separate from log entry)
  const hasCompletedLogs = useRef(false); // Track if logs marked complete (separate from log entry)
  const hasAppliedUserSettings = useRef(false); // Track if user settings have been applied from DB (prevent overwriting on initial mount)
  const hasRunSetupUserSettings = useRef(false); // Track if setupUserSettings has run this session (prevent multiple runs per page load)
  const hasTriedCreateUser = useRef(false); // #90: Guards the auto-create-user path against the race that produced ~27,936 duplicate User docs. See useEffect[userResult] and performLogoutClears.

  // Function to reset all hasCompleted refs - called when starting new load cycle
  const resetQueryCompletionTracking = () => {
    console.log(
      "%cModelTsContext - Resetting all hasCompleted refs for new load cycle",
      "color: #FF6B6B; font-weight: bold; background-color: #1a1a1a; padding: 2px 4px;"
    );
    hasCompletedUniqueLocations.current = false;
    hasCompletedOfficeMetaData.current = false;
    hasCompletedProjectsBeforeMerge.current = false;
    hasCompletedCandidatesList.current = false;
    hasCompletedAttributes.current = false;
    // NOTE: hasCompletedUser is NOT reset - user query is user-specific, not location-specific
    // It completes before onScheduleNeedsModel is called, so resetting it would cause "user" to never be marked complete
    hasCompletedUsers.current = false;
    hasCompletedLogs.current = false;
    // Also reset hasLogged refs to allow fresh logging
    hasLoggedUsers.current = false;
    hasLoggedLogs.current = false;
    hasLoggedCandidatesAdditional.current = false;
    hasLoggedUniqueLocations.current = false;
    hasLoggedOfficeMetaData.current = false;
  };

  const realmDataLocation = useRef(null); // Track which location Realm data belongs to
  const tanstackBaseLocation = useRef(null); // Track which location TanStack base projects belong to
  const tanstackAdditionalLocation = useRef(null); // Track which location TanStack additional projects belong to
  const lastSetAdditionalTimestamp = useRef(null); // Track the timestamp of last set projectsAdditional to prevent infinite loop
  const hasHadLocationChange = useRef(false); // Track if location has changed at least once (to skip validation on initial load)
  const previousLocation = useRef(''); // Track previous location to detect actual changes vs initialization
  const expectedProjectQueryList = useRef(''); // Track the current projectQueryList to reject stale queries
  const projectQueryGeneration = useRef(0); // Increment each time projectQueryList is set to uniquely identify queries
  const expectedGeneration = useRef(0); // Track which generation we're expecting
  const [queryTimestamp, setQueryTimestamp] = useState(Date.now()); // Force unique queryKey each time query should run
  
  // TanStack Query client for cache invalidation
  const queryClient = useQueryClient();
  
  // Logs filters - controlled by logsScreen
  const [logsUserFilter, setLogsUserFilter] = useState("all");
  const [logsDateFilter, setLogsDateFilter] = useState("");
  const [logsDaysFilter, setLogsDaysFilter] = useState("7");
  
  // Logs pagination and sorting - controlled by logsScreen
  const [logsPage, setLogsPage] = useState(1);
  const [logsPageSize, setLogsPageSize] = useState(100);
  const [logsSortColumn, setLogsSortColumn] = useState("when");
  const [logsSortDirection, setLogsSortDirection] = useState("desc");
  const [logsTotalCount, setLogsTotalCount] = useState(0);

  // Track query execution times (in milliseconds)
  const [queryExecutionTimes, setQueryExecutionTimes] = useState({});

  // Consolidated log entry - array of query comparisons
  const [consolidatedLogQueries, setConsolidatedLogQueries] = useState([]);
  const [expectedQueries, setExpectedQueries] = useState(new Set());
  const [completedQueries, setCompletedQueries] = useState(new Set());
  const [consolidatedLogTimeout, setConsolidatedLogTimeout] = useState(null);

  // Track failed critical queries for retry logic and user feedback
  // Critical queries are those needed for UI data display (not just logging/admin)
  const [failedQueries, setFailedQueries] = useState(new Set());
  const [retryingQueries, setRetryingQueries] = useState(new Set());

  // REMOVED: userId no longer used after switching to getUserByEmail (Jan 13, 2026)
  // const userId = mongoUser?.id;
  const userEmail = mongoUser?._profile?.data?.email; // Used for getUserByEmail query
  const projectId = "";
  var payrollNoArray = [];

  //
  //
  // - - - - - - - - - - - - - - - - -  PROCESSES  - - - - - - - - - - - - - - - - - -
  //
  //

  const createAdditionalCandidateQueryList = () => {
    // find/handle "invisible" jobs
    //      i.e. scheduled, assigned job(s) for installers not in this location
    //
    // EXPLANATION OF CREWQUERYLIST/AdditionalCandidateQueryList:
    //    candidates are queried by location & additionalLocations fields
    //    jobs are queried by location field
    //    when a job is in the foundSet and assigned to an installer NOT included in the candidate foundSet,
    //       then it was agreed that another query would be performed to add that installer to the
    //       candidate foundSet so that the assigned, scheduled job(s) would be displayed correctly
    //
    // 2026-04-27 (#105 phase 4d): removed weekDatesArray date filter so the
    // query covers ALL projects regardless of week. With multi-grid each
    // grid can show a different week, and the previous week-filtered logic
    // missed installers from weeks not in AppContext.weekDatesArray. Cost
    // is a slightly larger initial fetch (more payroll IDs in the query)
    // — eliminates per-grid loading complexity and "missing installer row"
    // bugs when navigating to a week that wasn't the original AppContext
    // value.

    var queryPayrollList = new Set();

    // loop thru unfiltered project documents
    projectsList.forEach((document) => {
      const start =
        document.date_scheduled !== ""
          ? new Date(document.date_scheduled).toLocaleDateString("en-us")
          : "";
      const installer =
        document.installer !== ""
          ? dataFormatting_payrollFields(document.installer)
          : "";

      if (start !== "" && installer !== "") {
        // find applicable installer document
        const matchedCandidate = candidatesList.filter((candidate) => {
          const formattedPayrollNo = dataFormatting_payrollFields(
            candidate.payrollNo
          );
          return formattedPayrollNo === installer;
        });
        if (matchedCandidate.length === 0) {
          queryPayrollList.add(document.installer);
          const report =
            "Inv#" +
            document.invoiceNo +
            ", Installer:" +
            document.installer +
            ", Start:" +
            document.date_scheduled;
          console.log(
            "%cModelTsContext (" +
              mongoDatabase +
              ") createAdditionalCandidateQueryList ERROR - unable to match an assigned job to an installer in this list !!",
            "color: #00BFFF; font-weight: bold;",
            report
          );
        }
      }
    });

    //update list so that queries can begin
    // NOTE:  help with new Set type:  https://dev.to/clairecodes/how-to-create-an-array-of-unique-values-in-javascript-using-sets-5dg6
    const queryPayrollListArray = Array.from(queryPayrollList); // OR const queryPayrollListArray = [...new Set(queryPayrollList)];
    setCandidatesAdditionalQueryList(queryPayrollListArray);
    console.log(
      "%cModelTsContext (" +
        mongoDatabase +
        ") createAdditionalCandidateQueryList: ",
      "color: #00BFFF; font-weight: bold;",
      queryPayrollListArray
    );
  };

  // - - - - - - - - - -

  /**
   * Helper function to get timestamp in Eastern Time
   */
  const getEasternTimestamp = () => {
    const now = new Date();
    // Convert to Eastern Time
    const easternTime = new Date(now.toLocaleString("en-US", { timeZone: "America/New_York" }));
    
    // Zero-pad values for proper string sorting
    const month = String(easternTime.getMonth() + 1).padStart(2, '0');
    const day = String(easternTime.getDate()).padStart(2, '0');
    const year = easternTime.getFullYear();
    const hours = String(easternTime.getHours()).padStart(2, '0');
    const minutes = String(easternTime.getMinutes()).padStart(2, '0');
    const seconds = String(easternTime.getSeconds()).padStart(2, '0');
    
    return {
      when: `${month}/${day}/${year} ${hours}:${minutes}:${seconds}`, // String for display
      whenDate: now.toISOString() // ISO string for consistent MongoDB Date conversion
    };
  };

  /**
   * Handles verification or creation of attributeDefaults for shared installers
   */
  const processSharedInstallerAttributeDefaults = () => {
    const alreadyExistsPayrollNos = []; // Collect for batch logging

    // Use awsgwAttributesResult (TanStack data) as source of truth for checking existing defaults
    // This ensures tanstackOnly users check the correct data source
    // getAttributesSource() checks both that data exists AND matches current location
    const attributesSource = getAttributesSource();

    awsgwCandidatesResult.forEach((candidate) => {
      if (candidate.sharedInstallerFlag) {
        // check if attributeDefaults exist for this shared installer
        const sharedInstallerDefaults = attributesSource.find(
          (attribute) =>
            attribute.companyCode === companyCode &&
            attribute._kf_payrollNo ===
              dataFormatting_payrollFields(candidate.payrollNo) &&
            attribute.date === "" &&
            attribute.location === locationFilter &&
            attribute.availability !== null &&
            attribute.availability?.length !== 0
        );

        // if not found, create them
        if (
          !sharedInstallerDefaults &&
          candidate.cLocationCode !== locationFilter
        ) {
          // (#79) Scaffolding for future createAttribute mutation — see commented
          // line ~30 below. Underscore prefix marks intentional retention.
          // eslint-disable-next-line no-unused-vars
          const _newDefaults = {
            companyCode: companyCode,
            location: locationFilter,
            _kf_payrollNo: dataFormatting_payrollFields(candidate.payrollNo),
            date: "",
            id: createUuid(),
            availability: [
              {
                Mon: "OFF",
              },
              {
                Tue: "OFF",
              },
              {
                Wed: "OFF",
              },
              {
                Thr: "OFF",
              },
              {
                Fri: "OFF",
              },
              {
                Sat: "OFF",
              },
              {
                Sun: "OFF",
              },
            ],
          };

          // TODO: mutations - createAttribute
          // createNewAttribute(newDefaults);
          console.log(
            `%cModelTsContext (${mongoDatabase}) processSharedInstallerAttributeDefaults - CREATED defaults for shared installer ${candidate.payrollNo}`,
            "color: #00BFFF; font-weight: bold;"
          );
        } else {
          // Collect for batch logging instead of individual logs
          alreadyExistsPayrollNos.push(candidate.payrollNo);
        }
      }
    });

    // Log all "already exists" payrollNos in a single line
    if (alreadyExistsPayrollNos.length > 0) {
      console.log(
        `%cModelTsContext (${mongoDatabase}) processSharedInstallerAttributeDefaults - Defaults already exist for ${alreadyExistsPayrollNos.length} shared installers: ${alreadyExistsPayrollNos.join(", ")}`,
        "color: #00BFFF; font-weight: bold;"
      );
    }
  };

  //
  //
  // - - - - - - - - - - - - - - - - -  UTILITIES  - - - - - - - - - - - - - - - - - -
  //
  //

  // - - - - - - - - - -

  /**
   * Sorts an array of objects by a specified field.
   * @param {Array} incomingArray - The array to sort.
   * @param {string} sortFieldPath - The field path to sort by.
   * @param {string} type - The type of the field (Date/Timestamp, Number, String/Text).
   * @param {string} order - The order to sort (ascending, descending).
   * @returns {Array} - The sorted array.
   */
  const myJsonArraySort = (incomingArray, sortFieldPath, type, order) => {
    // from https://stackoverflow.com/questions/979256/sorting-an-array-of-objects-by-property-values
    //
    // - - - - -

    function getDescendantProp(obj, desc) {
      var arr = desc.split(".");
      while (arr.length && (obj = obj[arr.shift()]));
      return obj;
      // from https://stackoverflow.com/questions/8051975/access-object-child-properties-using-a-dot-notation-string
    }

    // - - - - -

    // var incomingArray = JSON.parse(theArray);
    var sortedData = [];
    if (type === "Date/Timestamp") {
      if (order === "ascending") {
        sortedData = incomingArray.sort(function (a, b) {
          return (
            new Date(getDescendantProp(a, sortFieldPath)) -
            new Date(getDescendantProp(b, sortFieldPath))
          );
        });
      } else {
        sortedData = incomingArray.sort(function (a, b) {
          return (
            new Date(getDescendantProp(b, sortFieldPath)) -
            new Date(getDescendantProp(a, sortFieldPath))
          );
        });
      }
    } else if (type === "Number") {
      if (order === "ascending") {
        sortedData = incomingArray.sort(function (a, b) {
          return (
            Number(getDescendantProp(a, sortFieldPath)) -
            Number(getDescendantProp(b, sortFieldPath))
          );
        });
      } else {
        sortedData = incomingArray.sort(function (a, b) {
          return (
            Number(getDescendantProp(b, sortFieldPath)) -
            Number(getDescendantProp(a, sortFieldPath))
          );
        });
      }
    } else if (type === "String/Text") {
      // example of this style of ascending uppercase sorting - [ A, a, B, b, C, c ]
      if (order === "ascending") {
        sortedData = incomingArray.sort(function (a, b) {
          return getDescendantProp(a, sortFieldPath).localeCompare(
            getDescendantProp(b, sortFieldPath),
            undefined,
            { caseFirst: "upper" }
          );
        });
      } else {
        sortedData = incomingArray.sort(function (a, b) {
          return getDescendantProp(b, sortFieldPath).localeCompare(
            getDescendantProp(a, sortFieldPath),
            undefined,
            { caseFirst: "upper" }
          );
        });
      }
    }

    // console.log("ModelTsContext (" + mongoDatabase + ") myJsonArraySort sortedArray: ", sortedData);
    return sortedData;
  };

  // - - - - - - - - - -

  // Normalize values for comparison: convert BSON ObjectId to hex string, recurse
  const normalizeForCompare = (value) => {
    if (value == null) return value;

    if (Array.isArray(value)) {
      return value.map(normalizeForCompare);
    }

    if (typeof value === "object") {
      // Realm/driver ObjectId detection
      const isObjId =
        value?.constructor?.name === "ObjectId" ||
        typeof value?.toHexString === "function" ||
        (typeof value?.toString === "function" &&
          /^[a-f0-9]{24}$/i.test(value.toString()));

      if (isObjId) {
        try {
          return typeof value.toHexString === "function"
            ? value.toHexString()
            : value.toString();
        } catch {
          return value.toString?.() ?? value;
        }
      }

      // Extended JSON {$oid:"..."}
      if (typeof value.$oid === "string") {
        return value.$oid;
      }

      const out = {};
      for (const k of Object.keys(value)) {
        out[k] = normalizeForCompare(value[k]);
      }
      return out;
    }

    return value;
  };

  // - - - - - - - - - -

  const deepCompare = (obj1, obj2, path = "") => {
    let identical = true;

    const allKeys = new Set([...Object.keys(obj1), ...Object.keys(obj2)]);
    allKeys.forEach((key) => {
      const currentPath = path ? `${path}.${key}` : key;
      const val1 = obj1[key];
      const val2 = obj2[key];

      if (!(key in obj1)) {
        console.log(
          `%cOffice Data Comparison - DIFFERENCE: Key "${currentPath}" missing in Realm data`,
          "color: #ff6600; font-weight: bold;"
        );
        console.log(`  TanStack value:`, val2);
        identical = false;
        return;
      }
      if (!(key in obj2)) {
        console.log(
          `%cOffice Data Comparison - DIFFERENCE: Key "${currentPath}" missing in TanStack data`,
          "color: #ff6600; font-weight: bold;"
        );
        console.log(`  Realm value:`, val1);
        identical = false;
        return;
      }

      if (val1 === null && val2 === null) return;
      if (val1 === undefined && val2 === undefined) return;

      if ((val1 == null) !== (val2 == null)) {
        console.log(
          `%cOffice Data Comparison - DIFFERENCE: "${currentPath}" - Realm: ${val1}, TanStack: ${val2}`,
          "color: #ff6600; font-weight: bold;"
        );
        identical = false;
        return;
      }

      if (typeof val1 !== typeof val2) {
        console.log(
          `%cOffice Data Comparison - TYPE DIFFERENCE: "${currentPath}" - Realm: ${typeof val1} (${val1}), TanStack: ${typeof val2} (${val2})`,
          "color: #ff6600; font-weight: bold;"
        );
        identical = false;
        return;
      }

      if (Array.isArray(val1) && Array.isArray(val2)) {
        if (val1.length !== val2.length) {
          console.log(
            `%cOffice Data Comparison - ARRAY LENGTH DIFFERENCE: "${currentPath}" - Realm: ${val1.length}, TanStack: ${val2.length}`,
            "color: #ff6600; font-weight: bold;"
          );
          identical = false;
          return;
        }

        // If both arrays are primitive (string/number/boolean/null) compare ORDER-INSENSITIVE
        const primitivesOnly =
          val1.every(
            (e) =>
              e === null || ["string", "number", "boolean"].includes(typeof e)
          ) &&
          val2.every(
            (e) =>
              e === null || ["string", "number", "boolean"].includes(typeof e)
          );

        // ORDER-INSENSITIVE compare for primitive arrays
        if (primitivesOnly) {
          const freq = (arr) => {
            const m = {};
            arr.forEach((v) => {
              const k = String(v);
              m[k] = (m[k] || 0) + 1;
            });
            return m;
          };
          const f1 = freq(val1);
          const f2 = freq(val2);

          const keys1 = Object.keys(f1);
          const keys2 = Object.keys(f2);

          // Fast identical check
          if (
            keys1.length === keys2.length &&
            keys1.every((k) => f1[k] === f2[k])
          ) {
            // Same multiset (ignore ordering) -> treat as identical
            return;
          }

          // Log differences
          keys1.forEach((k) => {
            if (!(k in f2)) {
              console.log(
                `%cOffice Data Comparison - ARRAY VALUE MISSING (TanStack): "${currentPath}" -> ${k}`,
                "color:#ff6600;font-weight:bold;"
              );
              identical = false;
            } else if (f1[k] !== f2[k]) {
              console.log(
                `%cOffice Data Comparison - ARRAY VALUE COUNT DIFF: "${currentPath}" -> ${k} Realm:${f1[k]} TanStack:${f2[k]}`,
                "color:#ff6600;font-weight:bold;"
              );
              identical = false;
            }
          });
          keys2.forEach((k) => {
            if (!(k in f1)) {
              console.log(
                `%cOffice Data Comparison - ARRAY VALUE MISSING (Realm): "${currentPath}" -> ${k}`,
                "color:#ff6600;font-weight:bold;"
              );
              identical = false;
            }
          });
          return;
        }

        // Fallback: positional deep compare for non-primitive element arrays
        for (let i = 0; i < val1.length; i++) {
          if (!deepCompare(val1[i], val2[i], `${currentPath}[${i}]`)) {
            identical = false;
          }
        }
        return identical;
      }

      if (
        typeof val1 === "object" &&
        val1 !== null &&
        typeof val2 === "object" &&
        val2 !== null
      ) {
        if (!deepCompare(val1, val2, currentPath)) {
          identical = false;
        }
        return;
      }

      if (val1 !== val2) {
        // Optional numeric coercion if needed
        if (
          ((typeof val1 === "string" && typeof val2 === "number") ||
            (typeof val1 === "number" && typeof val2 === "string")) &&
          Number.isFinite(+val1) &&
          Number.isFinite(+val2) &&
          +val1 === +val2
        ) {
          console.log(
            `%cOffice Data Comparison - TYPE COERCION MATCH: "${currentPath}" - Realm: ${val1}, TanStack: ${val2}`,
            "color: #00ccff; font-weight: bold;"
          );
          return;
        }

        console.log(
          `%cOffice Data Comparison - VALUE DIFFERENCE: "${currentPath}" - Realm: ${val1}, TanStack: ${val2}`,
          "color: #ff6600; font-weight: bold;"
        );
        identical = false;
      }
    });

    return identical;
  };

  // - - - - - - - - - -

  // (#79) Debug utility — kept for ad-hoc Realm/TanStack data comparison if needed
  // during ModelRW cleanup stages 5-9. Underscore prefix marks intentional retention.
  // eslint-disable-next-line no-unused-vars
  const _compareDataSets = (
    realmData,
    tanstackData,
    logPrefix = "Office Data Comparison",
    executionTimeMs = null,
    realmExecutionTimeMs = null,
    recordCount = 0,
    maxLimit = 0
  ) => {
    // Extract query name from logPrefix
    const queryMatch = logPrefix.match(/\(([^)]+)\)$/);
    const queryName = queryMatch ? queryMatch[1] : "unknown";

    // Guard: Don't run comparison if already completed
    if (completedQueries.has(queryName)) {
      console.log(
        `%c${logPrefix} - SKIPPED: Query already completed`,
        "color: #FFA500; font-weight: bold;"
      );
      return true;
    }

    // Array to collect all log messages
    const logMessages = [];

    // Helper to truncate large data for database storage
    const truncateForStorage = (data, maxItems = 10) => {
      if (!data) return data;

      if (Array.isArray(data)) {
        if (data.length <= maxItems) {
          return data;
        }
        return {
          truncated: true,
          totalCount: data.length,
          sample: data.slice(0, maxItems),
          message: `Showing first ${maxItems} of ${data.length} items`,
        };
      }

      return data;
    };

    // Helper function to add logs
    const addLog = (message, style, ...data) => {
      logMessages.push({ message, style, data });
      console.log(`%c${message}`, style, ...data);
    };

    // Helper to add log with truncation for database.
    // (#79) Currently no callers — kept as a debug utility for future use.
    // eslint-disable-next-line no-unused-vars
    const _addLogTruncated = (message, style, ...data) => {
      // Full data to console
      console.log(`%c${message}`, style, ...data);
      // Truncated data to database
      const truncatedData = data.map((d) => truncateForStorage(d));
      logMessages.push({ message, style, data: truncatedData });
    };

    // addLog(
    //   `${logPrefix} - Starting Comparison`,
    //   "color: #ffff00; font-weight: bold; background-color: #1a1a1a; padding: 2px 4px;"
    // );

    // Handle null/undefined cases
    if (!realmData && !tanstackData) {
      addLog(
        `${logPrefix} - Both datasets are null/undefined`,
        "color: #00ff00; font-weight: bold;"
      );
      return true;
    }
    if (!realmData) {
      addLog(
        `${logPrefix} - ERROR: Realm data is null/undefined but TanStack data exists:`,
        "color: #ff0000; font-weight: bold;",
        tanstackData
      );
      return false;
    }
    if (!tanstackData) {
      addLog(
        `${logPrefix} - ERROR: TanStack data is null/undefined but Realm data exists:`,
        "color: #ff0000; font-weight: bold;",
        realmData
      );
      return false;
    }

    // NEW: Handle top-level array comparison (for uniqueLocations)
    if (Array.isArray(realmData) && Array.isArray(tanstackData)) {
      let isIdentical = true;

      // Helper to get unique key from an object
      const getUniqueKey = (obj) => {
        if (!obj || typeof obj !== "object") return obj; // For primitives, return the value itself
        return (
          obj.id ||
          obj._id?.toString() ||
          obj.mongoId ||
          obj.invoiceNo ||
          obj.uuid
        );
      };

      // Detect if this is an array of objects vs primitives
      const isPrimitiveArray =
        realmData.length > 0 &&
        (typeof realmData[0] !== "object" || realmData[0] === null);

      if (realmData.length !== tanstackData.length) {
        addLog(
          `${logPrefix} - ARRAY LENGTH DIFFERENCE: Realm: ${realmData.length}, TanStack: ${tanstackData.length}`,
          "color: #ff6600; font-weight: bold;"
        );

        let inRealmNotTanstack, inTanstackNotRealm;

        if (isPrimitiveArray) {
          // For primitive arrays, use simple .includes()
          inRealmNotTanstack = realmData.filter(
            (item) => !tanstackData.includes(item)
          );
          inTanstackNotRealm = tanstackData.filter(
            (item) => !realmData.includes(item)
          );
        } else {
          // For object arrays, compare by unique key
          const realmMap = new Map();
          const tanstackMap = new Map();

          realmData.forEach((item) => {
            const key = getUniqueKey(item);
            if (key) realmMap.set(key, item);
          });

          tanstackData.forEach((item) => {
            const key = getUniqueKey(item);
            if (key) tanstackMap.set(key, item);
          });

          inRealmNotTanstack = [];
          realmMap.forEach((item, key) => {
            if (!tanstackMap.has(key)) {
              inRealmNotTanstack.push(item);
            }
          });

          inTanstackNotRealm = [];
          tanstackMap.forEach((item, key) => {
            if (!realmMap.has(key)) {
              inTanstackNotRealm.push(item);
            }
          });
        }

        if (inRealmNotTanstack.length > 0) {
          // Full data to console
          console.log(
            `%c${logPrefix} - In Realm but NOT in TanStack (${inRealmNotTanstack.length}):`,
            "color: #ff6600; font-weight: bold;",
            inRealmNotTanstack
          );
          // Truncated data to database (max 10 items)
          logMessages.push({
            message: `${logPrefix} - In Realm but NOT in TanStack (${inRealmNotTanstack.length}):`,
            style: "color: #ff6600; font-weight: bold;",
            data: [truncateForStorage(inRealmNotTanstack, 10)],
          });
        }

        if (inTanstackNotRealm.length > 0) {
          // Full data to console
          console.log(
            `%c${logPrefix} - In TanStack but NOT in Realm (${inTanstackNotRealm.length}):`,
            "color: #ff6600; font-weight: bold;",
            inTanstackNotRealm
          );
          // Truncated data to database (max 10 items)
          logMessages.push({
            message: `${logPrefix} - In TanStack but NOT in Realm (${inTanstackNotRealm.length}):`,
            style: "color: #ff6600; font-weight: bold;",
            data: [truncateForStorage(inTanstackNotRealm, 10)],
          });
        }

        isIdentical = false;
      } else {
        // For primitive arrays (strings/numbers), compare as sets (order-insensitive)
        const primitivesOnly =
          realmData.every(
            (e) =>
              e === null || ["string", "number", "boolean"].includes(typeof e)
          ) &&
          tanstackData.every(
            (e) =>
              e === null || ["string", "number", "boolean"].includes(typeof e)
          );

        if (primitivesOnly) {
          const freq = (arr) => {
            const m = {};
            arr.forEach((v) => {
              const k = String(v);
              m[k] = (m[k] || 0) + 1;
            });
            return m;
          };
          const f1 = freq(realmData);
          const f2 = freq(tanstackData);

          const keys1 = Object.keys(f1);
          const keys2 = Object.keys(f2);

          if (
            keys1.length === keys2.length &&
            keys1.every((k) => f1[k] === f2[k])
          ) {
            // addLog(
            //   `${logPrefix} - ✅ IDENTICAL: Both arrays match (order-insensitive)!`,
            //   "color: #00ff00; font-weight: bold; background-color: #1a1a1a; padding: 2px 4px;"
            // );
            isIdentical = true;
          } else {
            // Log differences
            isIdentical = false;
            keys1.forEach((k) => {
              if (!(k in f2)) {
                addLog(
                  `${logPrefix} - ARRAY VALUE MISSING (TanStack): "${k}"`,
                  "color:#ff6600;font-weight:bold;"
                );
              } else if (f1[k] !== f2[k]) {
                addLog(
                  `${logPrefix} - ARRAY VALUE COUNT DIFF: "${k}" Realm:${f1[k]} TanStack:${f2[k]}`,
                  "color:#ff6600;font-weight:bold;"
                );
              }
            });
            keys2.forEach((k) => {
              if (!(k in f1)) {
                addLog(
                  `${logPrefix} - ARRAY VALUE MISSING (Realm): "${k}"`,
                  "color:#ff6600;font-weight:bold;"
                );
              }
            });

            addLog(
              `${logPrefix} - ❌ DIFFERENCES FOUND in arrays`,
              "color: #ff0000; font-weight: bold; background-color: #1a1a1a; padding: 2px 4px;"
            );
            // DON'T log full arrays to database - only to console
            console.log(`%cRealm Data:`, "color: #888888;", realmData);
            console.log(`%cTanStack Data:`, "color: #888888;", tanstackData);
          }
        } else {
          // For non-primitive arrays (like attributes, users, projects), compare by unique identifier
          isIdentical = true;

          // Try to detect unique key (id, _id, mongoId, invoiceNo, etc.)
          const getUniqueKey = (obj) => {
            if (!obj) return null;
            return (
              obj.id ||
              obj._id?.toString() ||
              obj.mongoId ||
              obj.invoiceNo ||
              obj.uuid
            );
          };

          // Create maps by unique key for efficient lookup
          const realmMap = new Map();
          const tanstackMap = new Map();

          realmData.forEach((item) => {
            const key = getUniqueKey(item);
            if (key) {
              realmMap.set(key, item);
            } else {
              console.warn(
                `${logPrefix} - No unique key found for Realm item:`,
                item
              );
            }
          });

          tanstackData.forEach((item) => {
            const key = getUniqueKey(item);
            if (key) {
              tanstackMap.set(key, item);
            } else {
              console.warn(
                `${logPrefix} - No unique key found for TanStack item:`,
                item
              );
            }
          });

          // Find items in Realm but not in TanStack
          const inRealmNotTanstack = [];
          realmMap.forEach((item, key) => {
            if (!tanstackMap.has(key)) {
              inRealmNotTanstack.push(item);
            }
          });

          // Find items in TanStack but not in Realm
          const inTanstackNotRealm = [];
          tanstackMap.forEach((item, key) => {
            if (!realmMap.has(key)) {
              inTanstackNotRealm.push(item);
            }
          });

          if (inRealmNotTanstack.length > 0) {
            console.log(
              `%c${logPrefix} - In Realm but NOT in TanStack (${inRealmNotTanstack.length}):`,
              "color: #ff6600; font-weight: bold;",
              inRealmNotTanstack
            );
            logMessages.push({
              message: `${logPrefix} - In Realm but NOT in TanStack (${inRealmNotTanstack.length}):`,
              style: "color: #ff6600; font-weight: bold;",
              data: [truncateForStorage(inRealmNotTanstack, 10)],
            });
            isIdentical = false;
          }

          if (inTanstackNotRealm.length > 0) {
            console.log(
              `%c${logPrefix} - In TanStack but NOT in Realm (${inTanstackNotRealm.length}):`,
              "color: #ff6600; font-weight: bold;",
              inTanstackNotRealm
            );
            logMessages.push({
              message: `${logPrefix} - In TanStack but NOT in Realm (${inTanstackNotRealm.length}):`,
              style: "color: #ff6600; font-weight: bold;",
              data: [truncateForStorage(inTanstackNotRealm, 10)],
            });
            isIdentical = false;
          }

          // addLog(
          //   isIdentical 
          //     ? `${logPrefix} - ✅ IDENTICAL: Both object arrays match perfectly!`
          //     : `${logPrefix} - ❌ DIFFERENCES FOUND in object arrays`,
          //   isIdentical
          //     ? "color: #00ff00; font-weight: bold; background-color: #1a1a1a; padding: 2px 4px;"
          //     : "color: #ff0000; font-weight: bold; background-color: #1a1a1a; padding: 2px 4px;"
          // );
        }
      }

      // Create log entry for array comparison
    const queryNameMatch = logPrefix.match(/\(([^)]+)\)$/);
      const queryNameFromLogPrefix = queryNameMatch
        ? queryNameMatch[1]
        : "unknown";
      // (#79) timestamp const removed — never referenced in this scope.

      // Calculate payload size for this query's data
      const tanstackPayloadSize = tanstackData ? new Blob([JSON.stringify(tanstackData)]).size : 0;
      const capacityPercent = maxLimit > 0 ? Math.round((recordCount / maxLimit) * 100) : null;

      const queryComparison = {
        query: queryNameFromLogPrefix,
        logs: logMessages,
        identical: isIdentical,
        tanstackExecutionTimeMs: executionTimeMs,
        realmExecutionTimeMs: realmExecutionTimeMs,
        payloadSizeBytes: tanstackPayloadSize,
        recordCount: recordCount,
        maxLimit: maxLimit,
        capacityPercent: capacityPercent,
      };

      // (#79) timingInfo const removed — was only used in the commented-out
      // console.log below; if we re-enable it, regenerate timingInfo locally.
      // console.log(
      //   `%cQuery Comparison: ${queryNameFromLogPrefix} | ${logMessages.length} messages | Identical: ${isIdentical} | TanStack: ${executionTimeMs}ms | Realm: ${realmExecutionTimeMs}ms`,
      //   "color: #00BFFF; font-weight: bold;"
      // );

      // Append to consolidated log array
      setConsolidatedLogQueries((prev) => [...prev, queryComparison]);

      // Mark this query as completed (only if it's in expectedQueries)
      if (expectedQueries.has(queryNameFromLogPrefix)) {
        setCompletedQueries((prev) => {
          const updated = new Set(prev);
          updated.add(queryNameFromLogPrefix);
          return updated;
        });
      }

      return isIdentical;
    }

    // Normalize both snapshots ONCE so _id (and any nested ObjectIds) become strings
    const realmNorm = normalizeForCompare(realmData);
    const tanstackNorm = normalizeForCompare(tanstackData);

    // - - - - -

    const isIdentical = deepCompare(realmNorm, tanstackNorm); // ← compare normalized objects

    // if (isIdentical) {
    //   addLog(
    //     `${logPrefix} - ✅ IDENTICAL: Both datasets match perfectly!`,
    //     "color: #00ff00; font-weight: bold; background-color: #1a1a1a; padding: 2px 4px;"
    //   );
    // } else {
    //   addLog(
    //     `${logPrefix} - ❌ DIFFERENCES FOUND: See details above`,
    //     "color: #ff0000; font-weight: bold; background-color: #1a1a1a; padding: 2px 4px;"
    //   );
    //   // DON'T log full objects to database - only to console
    //   console.log(`%cRealm Data:`, "color: #888888;", realmData);
    //   console.log(`%cTanStack Data:`, "color: #888888;", tanstackData);
    // }

    // Create ONE log entry with all collected messages for this entire run
    const queryNameMatch = logPrefix.match(/\(([^)]+)\)$/);
    const queryNameFromLogPrefix = queryNameMatch
      ? queryNameMatch[1]
      : "unknown";
    // (#79) timestamp const removed — never referenced in this scope.

    const payloadSizeBytes = tanstackData ? new Blob([JSON.stringify(tanstackData)]).size : 0;
    const capacityPercent = maxLimit > 0 ? Math.round((recordCount / maxLimit) * 100) : null;
    const queryComparison = {
      query: queryNameFromLogPrefix,
      logs: logMessages,
      identical: isIdentical,
      tanstackExecutionTimeMs: executionTimeMs,
      realmExecutionTimeMs: realmExecutionTimeMs,
      payloadSizeBytes: payloadSizeBytes,
      recordCount: recordCount,
      maxLimit: maxLimit,
      capacityPercent: capacityPercent,
    };

    // Log summary instead of full entry to avoid console bloat
    const timingInfo =
      executionTimeMs && realmExecutionTimeMs
        ? ` | TanStack: ${executionTimeMs}ms | Realm: ${realmExecutionTimeMs}ms`
        : executionTimeMs
        ? ` | TanStack: ${executionTimeMs}ms`
        : "";
    console.log(
      `%cQuery Comparison: ${queryNameFromLogPrefix} | ${logMessages.length} messages | Identical: ${isIdentical}${timingInfo}`,
      "color: #00BFFF; font-weight: bold;"
    );

    // Append to consolidated log array
    setConsolidatedLogQueries((prev) => [...prev, queryComparison]);

    // Mark this query as completed (only if it's in expectedQueries)
    if (expectedQueries.has(queryNameFromLogPrefix)) {
      setCompletedQueries((prev) => {
        const updated = new Set(prev);
        updated.add(queryNameFromLogPrefix);
        return updated;
      });
    }

    return isIdentical;
  };

  //
  //
  // - - - - - - - - - - - - - - - - -  QUERY HOOKS  - - - - - - - - - - - - - - - - - -
  //
  //

  // - - - - - - - - - - - - - - - - -  USEQUERY getUniqueLocations  - - - - - - - - - - - - - - - - - -

  const {
    data: uniqueLocationsResult,
    isLoading: uniqueLocationsLoading,
    error: uniqueLocationsError,
    isFetching: uniqueLocationsFetching,
    refetch: refetchUniqueLocations,
  } = useQuery({
    queryKey: ["getUniqueLocations", companyCode, locationFilter],
    queryFn: async () => {
      const startTime = performance.now();

      const dynamicBody = {
        method: "getUniqueLocations",
        dataSource: MONGODB_DATASOURCE_TS,
        database: mongoDatabase,
        collection: MONGODB_COLLECTION_ACTIVEQUEUE,
        companyCode: companyCode,
        // maxLimit: MONGODB_MAXLIMIT_UNIQUELOCATIONS,  // currently ignored
      };

      const response = await fetch(MONGODB_AWSGW_QUERIES, {
        method: "POST",
        mode: "cors",
        credentials: "omit",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "x-api-key": MONGODB_AWSGW_APIKEY,
        },
        body: JSON.stringify(dynamicBody),
      });

      if (!response.ok) {
        throw new Error(
          `ModelTsContext (${mongoDatabase}) AWSGW_getUniqueLocations(${companyCode}) ERROR: ${response.status} ${response.statusText}`
        );
      }

      // Read the response body only once
      const result = await response.json();

      // Check for error in result
      if (result.errorType || result.errorMessage) {
        const errorMsg = result.errorMessage || result.errorType || "Unknown error";
        console.error(
          "%cModelTsContext (" +
            mongoDatabase +
            ") AWSGW_getUniqueLocations(" +
            companyCode +
            ") ERROR: ",
          "color: #ff0000; font-weight: bold; background-color: #1a1a1a; padding: 2px 4px;",
          errorMsg,
          result
        );
        throw new Error(`getUniqueLocations: ${errorMsg}`);
      }

      const executionTime = Math.round(performance.now() - startTime);
      setQueryExecutionTimes((prev) => ({
        ...prev,
        getUniqueLocations: executionTime,
      }));

      // Attach execution time to result for immediate access
      result._executionTime = executionTime;

      console.log(
        "%cModelTsContext (" +
          mongoDatabase +
          ") AWSGW_getUniqueLocations() fetchResult: ",
        "color: #00ff00; font-weight: bold; background-color: #1a1a1a; padding: 2px 4px;",
        result
      );

      // Consolidated logging: execution time, payload size, and result
      const responseSizeBytes = new Blob([JSON.stringify(result)]).size;
      console.log(
        `%cModelTsContext [getUniqueLocations] ${executionTime}ms | ${responseSizeBytes} bytes (${(responseSizeBytes / 1024).toFixed(2)} KB)`,
        "color: #00BFFF; font-weight: bold;",
        result
      );

      return result;
    },
    enabled: !!companyCode && shouldFetchUniqueLocations,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    // Only expose the piece you care about; structural sharing keeps reference stable when unchanged
    select: (raw) => raw?.message?.result ?? null,
  });

  // - - - - - - - - - - - - - - - - -  USEQUERY getOfficeMetaDataByLocation  - - - - - - - - - - - - - - - - - -

  const {
    data: officeMetaDataResult,
    isLoading: officeMetaDataLoading,
    error: officeMetaDataError,
    isFetching: officeMetaDataFetching,
    refetch: refetchOfficeMetaData,
  } = useQuery({
    queryKey: ["getOfficeMetaDataByLocation", locationFilter],
    queryFn: async () => {
      const startTime = performance.now();

      const dynamicBody = {
        method: "getOfficeMetaDataByLocation",
        dataSource: MONGODB_DATASOURCE_TS,
        database: mongoDatabase,
        collection: MONGODB_COLLECTION_OFFICES,
        locationCode: locationFilter,
        // maxLimit: MONGODB_MAXLIMIT_OFFICEMETADATA,  // currently findOne
      };

      const response = await fetch(MONGODB_AWSGW_QUERIES, {
        method: "POST",
        mode: "cors",
        credentials: "omit",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "x-api-key": MONGODB_AWSGW_APIKEY,
        },
        body: JSON.stringify(dynamicBody),
      });

      if (!response.ok) {
        throw new Error(
          `ModelTsContext (${mongoDatabase}) AWSGW_getOfficeMetaDataByLocation(${locationFilter}) ERROR: ${response.status} ${response.statusText}`
        );
      }

      // Read the response body only once
      const result = await response.json();

      // Check for error in result
      if (result.errorType || result.errorMessage) {
        const errorMsg = result.errorMessage || result.errorType || "Unknown error";
        console.error(
          "%cModelTsContext (" +
            mongoDatabase +
            ") AWSGW_getOfficeMetaDataByLocation(" +
            locationFilter +
            ") ERROR: ",
          "color: #ff0000; font-weight: bold; background-color: #1a1a1a; padding: 2px 4px;",
          errorMsg,
          result
        );
        throw new Error(`getOfficeMetaDataByLocation: ${errorMsg}`);
      }

      const executionTime = Math.round(performance.now() - startTime);
      setQueryExecutionTimes((prev) => ({
        ...prev,
        getOfficeMetaDataByLocation: executionTime,
      }));

      // Attach execution time to result for immediate access
      result._executionTime = executionTime;

      console.log(
        "%cModelTsContext (" +
          mongoDatabase +
          ") AWSGW_getOfficeMetaDataByLocation() fetchResult: ",
        "color: #00ff00; font-weight: bold; background-color: #1a1a1a; padding: 2px 4px;",
        result
      );

      // Consolidated logging: execution time, payload size, and result
      const responseSizeBytes = new Blob([JSON.stringify(result)]).size;
      console.log(
        `%cModelTsContext [getOfficeMetaDataByLocation] ${executionTime}ms | ${responseSizeBytes} bytes (${(responseSizeBytes / 1024).toFixed(2)} KB)`,
        "color: #00BFFF; font-weight: bold;",
        result
      );

      return result;
    },
    enabled: !!locationFilter && shouldFetchOfficeMetaData,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    // Only expose the piece you care about; structural sharing keeps reference stable when unchanged
    select: (raw) => raw?.message?.result ?? null,
  });

  // - - - - - - - - - - - - - - - - -  USEQUERY getUserByEmail  - - - - - - - - - - - - - - - - - -

  // NOTE: Jan 13, 2026 - Changed from getUserById to getUserByEmail (Realm EOL)
  // The old Realm user.id doesn't exist anymore. Now we look up users by email from JWT.
  const {
    data: userResult,
    isLoading: userByIdLoading,
    error: userError,
    isFetching: userFetching,
  } = useQuery({
    queryKey: ["getUserByEmail", userEmail],
    queryFn: async () => {
      const startTime = performance.now();

      const dynamicBody = {
        method: "getUserByEmail",
        dataSource: MONGODB_DATASOURCE_TS,
        database: mongoDatabase,
        collection: MONGODB_COLLECTION_USERS,
        email: userEmail,
      };

      const response = await fetch(MONGODB_AWSGW_QUERIES, {
        method: "POST",
        mode: "cors",
        credentials: "omit",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "x-api-key": MONGODB_AWSGW_APIKEY,
        },
        body: JSON.stringify(dynamicBody),
      });

      if (!response.ok) {
        throw new Error(
          `ModelTsContext (${mongoDatabase}) AWSGW_getUserByEmail(${userEmail}) ERROR: ${response.status} ${response.statusText}`
        );
      }

      // Read the response body only once
      const result = await response.json();

      // Check for error in result
      if (result.errorType || result.errorMessage) {
        const errorMsg = result.errorMessage || result.errorType || "Unknown error";
        console.error(
          "%cModelTsContext (" +
            mongoDatabase +
            ") AWSGW_getUserByEmail(" +
            userEmail +
            ") ERROR: ",
          "color: #ff0000; font-weight: bold; background-color: #1a1a1a; padding: 2px 4px;",
          errorMsg,
          result
        );
        throw new Error(`getUserByEmail: ${errorMsg}`);
      }

      const executionTime = Math.round(performance.now() - startTime);
      setQueryExecutionTimes((prev) => ({
        ...prev,
        getUserByEmail: executionTime,
      }));

      // Attach execution time to result for immediate access
      result._executionTime = executionTime;

      console.log(
        "%cModelTsContext (" +
          mongoDatabase +
          ") AWSGW_getUserByEmail() fetchResult: ",
        "color: #00ff00; font-weight: bold; background-color: #1a1a1a; padding: 2px 4px;",
        result
      );

      // Consolidated logging: execution time, payload size, and result
      const responseSizeBytes = new Blob([JSON.stringify(result)]).size;
      console.log(
        `%cModelTsContext [getUserByEmail] ${executionTime}ms | ${responseSizeBytes} bytes (${(responseSizeBytes / 1024).toFixed(2)} KB)`,
        "color: #00BFFF; font-weight: bold;",
        result
      );

      return result;
    },
    enabled: !!userEmail && shouldFetchUser,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    // Retry logic: try up to 3 times with 1 second delay between attempts
    retry: 3,
    retryDelay: 1000,
    // Only expose the piece you care about; structural sharing keeps reference stable when unchanged
    select: (raw) => raw?.message?.result ?? null,
  });

  // - - - - - - - - - - - - - - - - -  USEQUERY getUsers  - - - - - - - - - - - - - - - - - -

  const {
    data: usersResult,
    isLoading: usersLoading,
    error: usersError,
    isFetching: usersFetching,
  } = useQuery({
    queryKey: ["getUsers", locationFilter],
    queryFn: async () => {
      const startTime = performance.now();

      const dynamicBody = {
        method: "getUsers",
        dataSource: MONGODB_DATASOURCE_TS,
        database: mongoDatabase,
        collection: MONGODB_COLLECTION_USERS,
        maxLimit: MONGODB_MAXLIMIT_USERS,
      };

      const response = await fetch(MONGODB_AWSGW_QUERIES, {
        method: "POST",
        mode: "cors",
        credentials: "omit",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "x-api-key": MONGODB_AWSGW_APIKEY,
        },
        body: JSON.stringify(dynamicBody),
      });

      if (!response.ok) {
        throw new Error(
          `ModelTsContext (${mongoDatabase}) AWSGW_getUsers() ERROR: ${response.status} ${response.statusText}`
        );
      }

      // Read the response body only once
      const result = await response.json();

      // Log response payload size
      const responseSizeBytes = new Blob([JSON.stringify(result)]).size;
      console.log(
        `%cModelTsContext [getUsers] Response: ${responseSizeBytes} bytes (${(responseSizeBytes / 1024).toFixed(2)} KB)`,
        "color: #00BFFF; font-weight: bold;"
      );

      // Check for error in result
      if (result.errorType || result.errorMessage) {
        const errorMsg = result.errorMessage || result.errorType || "Unknown error";
        console.error(
          "%cModelTsContext (" +
            mongoDatabase +
            ") AWSGW_getUsers() ERROR: ",
          "color: #ff0000; font-weight: bold; background-color: #1a1a1a; padding: 2px 4px;",
          errorMsg,
          result
        );
        throw new Error(`getUsers: ${errorMsg}`);
      }

      const executionTime = Math.round(performance.now() - startTime);
      setQueryExecutionTimes((prev) => ({ ...prev, getUsers: executionTime }));

      // Attach execution time to result for immediate access
      result._executionTime = executionTime;

      console.log(
        "%cModelTsContext (" +
          mongoDatabase +
          ") AWSGW_getUsers() fetchResult: ",
        "color: #00ff00; font-weight: bold; background-color: #1a1a1a; padding: 2px 4px;",
        result
      );

      return result;
    },
    enabled: shouldFetchUsers,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    // Only expose the piece you care about; structural sharing keeps reference stable when unchanged
    select: (raw) => {
      const list = raw?.message?.result ?? [];
      const executionTime = raw?._executionTime ?? 0;
      return {
        users: list.map((doc) => {
          const mongoId =
            doc?._id?.toString?.() ??
            (typeof doc?._id === "string" ? doc._id : "");
          return { ...doc, mongoId }; // do not mutate doc
        }),
        _executionTime: executionTime,
      };
    },
  });

  // - - - - - - - - - - - - - - - - -  USEQUERY getProjectById  - - - - - - - - - - - - - - - - - -

  const {
    data: projectResult,
    isLoading: projectLoading,
    error: projectError,
    isFetching: projectFetching,
  } = useQuery({
    queryKey: ["getProjectById"],
    queryFn: async () => {
      const startTime = performance.now();

      const dynamicBody = {
        method: "getProjectById",
        dataSource: MONGODB_DATASOURCE_TS,
        database: mongoDatabase,
        collection: MONGODB_COLLECTION_ACTIVEQUEUE,
        id: projectId,
        // maxLimit: MONGODB_MAXLIMIT_PROJECTBYID,  // currently findOne
      };

      const response = await fetch(MONGODB_AWSGW_QUERIES, {
        method: "POST",
        mode: "cors",
        credentials: "omit",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "x-api-key": MONGODB_AWSGW_APIKEY,
        },
        body: JSON.stringify(dynamicBody),
      });

      if (!response.ok) {
        throw new Error(
          `ModelTsContext (${mongoDatabase}) AWSGW_getProjectById(${projectId}) ERROR: ${response.status} ${response.statusText}`
        );
      }

      // Read the response body only once
      const result = await response.json();

      // Check for error in result
      if (result.errorType || result.errorMessage) {
        const errorMsg = result.errorMessage || result.errorType || "Unknown error";
        console.error(
          "%cModelTsContext (" +
            mongoDatabase +
            ") AWSGW_getProjectById(" +
            projectId +
            ") ERROR: ",
          "color: #ff0000; font-weight: bold; background-color: #1a1a1a; padding: 2px 4px;",
          errorMsg,
          result
        );
        throw new Error(`getProjectById: ${errorMsg}`);
      }

      const executionTime = Math.round(performance.now() - startTime);
      setQueryExecutionTimes((prev) => ({
        ...prev,
        getProjectById: executionTime,
      }));

      // Attach execution time to result for immediate access
      result._executionTime = executionTime;

      console.log(
        "%cModelTsContext (" +
          mongoDatabase +
          ") AWSGW_getProjectById() fetchResult: ",
        "color: #00ff00; font-weight: bold; background-color: #1a1a1a; padding: 2px 4px;",
        result
      );

      // Consolidated logging: execution time, payload size, and result
      const responseSizeBytes = new Blob([JSON.stringify(result)]).size;
      console.log(
        `%cModelTsContext [getProjectById] ${executionTime}ms | ${responseSizeBytes} bytes (${(responseSizeBytes / 1024).toFixed(2)} KB)`,
        "color: #00BFFF; font-weight: bold;",
        result
      );

      return result;
    },
    enabled: !!projectId && shouldFetchProject,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    select: (raw) => raw?.message?.result ?? null,
  });

  // - - - - - - - - - - - - - - - - -  USEQUERY getProjectsByCompany  - - - - - - - - - - - - - - - - - -

  const {
    data: projectsResult,
    isLoading: projectsLoading,
    error: projectsError,
    isFetching: projectsFetching,
    refetch: refetchProjects,
  } = useQuery({
    queryKey: ["getProjectsByCompany", companyCode, locationFilter],
    queryFn: async () => {
      const startTime = performance.now();

      const dynamicBody = {
        method: "getProjectsByCompany",
        dataSource: MONGODB_DATASOURCE_TS,
        database: mongoDatabase,
        collection: MONGODB_COLLECTION_ACTIVEQUEUE,
        companyCode: companyCode,
        locationFilter: locationFilter,
        maxLimit: MONGODB_MAXLIMIT_PROJECTSBYCOMPANY,
      };

      const response = await fetch(MONGODB_AWSGW_QUERIES, {
        method: "POST",
        mode: "cors",
        credentials: "omit",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "x-api-key": MONGODB_AWSGW_APIKEY,
        },
        body: JSON.stringify(dynamicBody),
      });

      if (!response.ok) {
        throw new Error(
          `ModelTsContext (${mongoDatabase}) AWSGW_getProjectsByCompany(${companyCode}) ERROR: ${response.status} ${response.statusText}`
        );
      }

      // Read the response body only once
      const result = await response.json();

      // Check for error in result
      if (result.errorType || result.errorMessage) {
        const errorMsg = result.errorMessage || result.errorType || "Unknown error";
        console.error(
          "%cModelTsContext (" +
            mongoDatabase +
            ") AWSGW_getProjectsByCompany(" +
            companyCode +
            ") ERROR: ",
          "color: #ff0000; font-weight: bold; background-color: #1a1a1a; padding: 2px 4px;",
          errorMsg,
          result
        );
        throw new Error(`getProjectsByCompany: ${errorMsg}`);
      }

      const executionTime = Math.round(performance.now() - startTime);
      setQueryExecutionTimes((prev) => ({
        ...prev,
        getProjectsByCompany: executionTime,
      }));

      // Attach execution time to result for immediate access
      result._executionTime = executionTime;

      console.log(
        "%cModelTsContext (" +
          mongoDatabase +
          ") AWSGW_getProjectsByCompany() fetchResult: ",
        "color: #00ff00; font-weight: bold; background-color: #1a1a1a; padding: 2px 4px;",
        result
      );

      // Consolidated logging: execution time, payload size, and result
      const responseSizeBytes = new Blob([JSON.stringify(result)]).size;
      console.log(
        `%cModelTsContext [getProjectsByCompany] ${executionTime}ms | ${responseSizeBytes} bytes (${(responseSizeBytes / 1024).toFixed(2)} KB)`,
        "color: #00BFFF; font-weight: bold;",
        result
      );

      return result;
    },
    enabled: !!companyCode && !!locationFilter && shouldFetchProjects,
    // DISABLED POLLING: getProjectsByCompanyWithShared now handles polling for comparison
    // This query is now initial-fetch only (for comparison with Realm's initial getProjectsByCompany)
    refetchInterval: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    select: (raw) => {
      const list = raw?.message?.result ?? [];
      return list.map((doc) => {
        const mongoId =
          doc?._id?.toString?.() ??
          (typeof doc?._id === "string" ? doc._id : "");
        // Add mongoId for MongoDB _id reference, but preserve existing FileMaker 'id' field if present
        return { ...doc, mongoId };
      });
    },
  });

  // - - - - - - - - - - - - - - - - -  USEQUERY getProjectsByCompanyWithShared (POLLING ONLY)  - - - - - - - - - - - - - - - - - -
  //
  // This query is used ONLY for polling comparison against Realm change stream-updated data.
  // It fetches base projects + shared installer projects in a single query from Lambda.
  // Results are compared with Realm's projectsList (which includes change stream updates).
  //
  // The comparison results are included in the polling batch logs for monitoring data consistency.
  //

  const {
    data: projectsWithSharedResult,
    isFetching: projectsWithSharedFetching,
  } = useQuery({
    // Include isUserIdle in queryKey so refetchInterval re-evaluates when idle state changes
    queryKey: ["getProjectsByCompanyWithShared", companyCode, locationFilter, isUserIdle],
    queryFn: async () => {
      const startTime = performance.now();

      const dynamicBody = {
        method: "getProjectsByCompanyWithShared",
        dataSource: MONGODB_DATASOURCE_TS,
        database: mongoDatabase,
        collection: MONGODB_COLLECTION_ACTIVEQUEUE, // Note: Lambda internally queries both ActiveQueue and Candidates
        companyCode: companyCode,
        locationFilter: locationFilter,
        maxLimit: MONGODB_MAXLIMIT_PROJECTSBYCOMPANY,
      };

      const response = await fetch(MONGODB_AWSGW_QUERIES, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-api-key": MONGODB_AWSGW_APIKEY,
        },
        body: JSON.stringify(dynamicBody),
      });
      const result = await response.json();
      if (result?.message?.errorType || result?.errorType) {
        const errorMsg =
          result?.message?.errorMessage ||
          result?.errorMessage ||
          "Unknown error";
        console.error(
          "%cModelTsContext AWSGW_getProjectsByCompanyWithShared() ERROR: ",
          "color: #FF0000; font-weight: bold;",
          errorMsg,
          result
        );
        throw new Error(`getProjectsByCompanyWithShared: ${errorMsg}`);
      }

      const executionTime = Math.round(performance.now() - startTime);

      // Attach execution time to result for immediate access
      result._executionTime = executionTime;

      // Log errors always, but gate verbose debug logging behind development mode
      const DEBUG_POLLING = process.env.NODE_ENV === "development";

      if (result?.statusCode === 500) {
        // Always log 500 errors, but redact sensitive data
        console.error(
          "%cModelTsContext AWSGW_getProjectsByCompanyWithShared() 500 ERROR: ",
          "color: #FF0000; font-weight: bold; background-color: #1a1a1a; padding: 2px 4px;",
          {
            error: result?.message?.error,
            details: typeof result?.message?.details === "string"
              ? result.message.details.substring(0, 200)
              : result?.message?.details,
            _debugVersion: result?._debugVersion,
          }
        );
      } else if (DEBUG_POLLING) {
        // Development-only: log redacted response summary (no full payload)
        console.log(
          "%cModelTsContext AWSGW_getProjectsByCompanyWithShared() RAW RESPONSE: ",
          "color: #22c55e; font-weight: bold; background-color: #1a1a1a; padding: 2px 4px;",
          {
            _debugVersion: result?._debugVersion,
            statusCode: result?.statusCode,
            hasMessage: !!result?.message,
            messageKeys: result?.message ? Object.keys(result.message) : [],
            hasResult: !!result?.message?.result,
            resultKeys: result?.message?.result ? Object.keys(result.message.result) : [],
            projectCount: result?.message?.result?.projects?.length,
            metaKeys: result?.message?.result?.meta ? Object.keys(result.message.result.meta) : [],
            // Redacted: no fullResult, no raw data
          }
        );
      }

      // Always log lightweight summary for monitoring
      console.log(
        "%cModelTsContext AWSGW_getProjectsByCompanyWithShared() fetchResult: ",
        "color: #9370DB; font-weight: bold; background-color: #1a1a1a; padding: 2px 4px;",
        {
          projectCount: result?.message?.result?.projects?.length,
          sharedInstallerCount: result?.message?.result?.meta?.sharedInstallerCount,
          executionTime,
        }
      );

      return result;
    },
    // Time Machine #114: also gate `enabled` on `!isAnyModalOpen` (which folds
    // in isTimeTraveling). Previously only refetchInterval gated on it, but
    // TanStack still ran the initial enabled-fetch and any deps-change refetch.
    // Disabling at the `enabled` layer fully halts the query while traveling.
    enabled: !!companyCode && !!locationFilter && shouldPollProjects && !isAnyModalOpen,
    // POLLING ONLY - use refetchInterval to poll every 5 seconds on Scheduler/Optimizer tabs
    refetchInterval: (query) => {
      // Don't schedule next poll if still fetching (prevents request pile-up on slow connections)
      if (query.state.fetchStatus === 'fetching') {
        console.log(
          "%c⏸️ PROJECTS POLLING SKIPPED - previous query still fetching",
          "color: #FFA500; font-weight: bold; background-color: #1a1a1a; padding: 2px 4px;"
        );
        return false;
      }
      // Don't poll during initial load or retry scenarios - prevents compounding failed requests
      if (failedQueries.size > 0 || retryingQueries.size > 0) {
        return false;
      }
      // Check if we should poll:
      // 1. Normal case: user is active and on scheduler/optimizer tabs
      // 2. Override case: change stream event triggered temporary polling for verification
      // 3. Pause when ANY modal is open to prevent race conditions with optimistic updates
      const shouldPollNormally = shouldPollProjects && !isUserIdle && !isAnyModalOpen && (whichList === "optimizer" || whichList === "schedule");
      const shouldPollForVerification = shouldPollProjects && !isAnyModalOpen && changeStreamPollingOverride.active && changeStreamPollingOverride.remainingPolls > 0;
      return (shouldPollNormally || shouldPollForVerification)
        ? MONGODB_POLLING_INTERVAL_PROJECTS * 1000
        : false;
    },
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    select: (raw) => {
      // Extract projects array and meta from Lambda response
      const responseData = raw?.message?.result || {};
      const projects = responseData.projects ?? [];
      const meta = responseData.meta || {};

      // Process projects to add mongoId, but preserve existing FileMaker 'id' field
      const processedProjects = projects.map((doc) => {
        const mongoId =
          doc?._id?.toString?.() ??
          (typeof doc?._id === "string" ? doc._id : "");
        return { ...doc, mongoId };
      });

      return {
        projects: processedProjects,
        meta: meta,
        _executionTime: raw?._executionTime,
      };
    },
  });

  // - - - - - - - - - - - - - - - - -  USEQUERY getCandidatesByCompany  - - - - - - - - - - - - - - - - - -

  const {
    data: candidatesResult,
    isLoading: candidatesLoading,
    error: candidatesError,
    isFetching: candidatesFetching,
    refetch: refetchCandidates,
  } = useQuery({
    queryKey: ["getCandidatesByCompany", companyCode, locationFilter],
    queryFn: async () => {
      const startTime = performance.now();

      const dynamicBody = {
        method: "getCandidatesByCompany",
        dataSource: MONGODB_DATASOURCE_TS,
        database: mongoDatabase,
        collection: MONGODB_COLLECTION_CANDIDATES,
        companyCode: companyCode,
        locationFilter: locationFilter,
        maxLimit: MONGODB_MAXLIMIT_CANDIDATESBYCOMPANY,
      };

      const response = await fetch(MONGODB_AWSGW_QUERIES, {
        method: "POST",
        mode: "cors",
        credentials: "omit",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "x-api-key": MONGODB_AWSGW_APIKEY,
        },
        body: JSON.stringify(dynamicBody),
      });

      if (!response.ok) {
        throw new Error(
          `ModelTsContext (${mongoDatabase}) AWSGW_getCandidatesByCompany(${companyCode}) ERROR: ${response.status} ${response.statusText}`
        );
      }

      // Read the response body only once
      const result = await response.json();

      // Check for error in result
      if (result.errorType || result.errorMessage) {
        const errorMsg = result.errorMessage || result.errorType || "Unknown error";
        console.error(
          "%cModelTsContext (" +
            mongoDatabase +
            ") AWSGW_getCandidatesByCompany(" +
            companyCode +
            ") ERROR: ",
          "color: #ff0000; font-weight: bold; background-color: #1a1a1a; padding: 2px 4px;",
          errorMsg,
          result
        );
        throw new Error(`getCandidatesByCompany: ${errorMsg}`);
      }

      const executionTime = Math.round(performance.now() - startTime);
      setQueryExecutionTimes((prev) => ({
        ...prev,
        getCandidatesByCompany: executionTime,
      }));

      // Attach execution time to result for immediate access
      result._executionTime = executionTime;

      console.log(
        "%cModelTsContext (" +
          mongoDatabase +
          ") AWSGW_getCandidatesByCompany() fetchResult: ",
        "color: #00ff00; font-weight: bold; background-color: #1a1a1a; padding: 2px 4px;",
        result
      );

      // Consolidated logging: execution time, payload size, and result
      const responseSizeBytes = new Blob([JSON.stringify(result)]).size;
      console.log(
        `%cModelTsContext [getCandidatesByCompany] ${executionTime}ms | ${responseSizeBytes} bytes (${(responseSizeBytes / 1024).toFixed(2)} KB)`,
        "color: #00BFFF; font-weight: bold;",
        result
      );

      // 2025-12-16: Log debug info from Lambda response (only runs once per fetch)
      const lambdaResult = result?.message?.result;
      if (lambdaResult?._debugVersion) {
        console.log(
          `%c[getCandidatesByCompany] Lambda Debug: version=${lambdaResult._debugVersion}, main=${lambdaResult._mainCount}, additional=${lambdaResult._additionalCount}, total=${lambdaResult._totalCount}`,
          "color: #FFA500; font-weight: bold;"
        );
      }

      return result;
    },
    enabled: !!companyCode && !!locationFilter && shouldFetchCandidates,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    select: (raw) => {
      // 2025-12-16: Lambda now returns object with debug info and candidates array
      const result = raw?.message?.result;
      const list = result?.candidates ?? result ?? []; // Handle both new object format and legacy array format

      var sortedList = [];

      // 2025-02-13 - flag shared installers (additionalLocations)
      //     NOTE: this bit reveals shared installers in the candidatesList, so
      //        we need to add their assigned jobs from other locations to the projectList (getProjectsByPayrollNoList)
      //        and query/add their attributes (getAttributesByPayrollNoList)
      var queryPayrollListArray = [];
      const processedList = list.map((currentDocument) => {
        const additionalLocations = currentDocument.Additional_Locations;
        // Create a copy to avoid mutating the original
        const docCopy = { ...currentDocument };

        // 2025-12-16: Match Realm's logic - flag as shared if Additional_Locations field EXISTS
        // (even if empty string ""). The Lambda returns installers with { $exists: true },
        // so we need to check for field existence, not just truthy value.
        // Previously: if (additionalLocations) - missed installers with "" value
        const hasAdditionalLocationsField = additionalLocations !== undefined;
        if (hasAdditionalLocationsField) {
          docCopy.sharedInstallerFlag = true;
          const formattedPayrollNo = dataFormatting_payrollFields(
            currentDocument.payrollNo
          );
          queryPayrollListArray.push(formattedPayrollNo);
        }
        return docCopy;
      });

      // sort the resulting list
      sortedList = myJsonArraySort(
        processedList,
        "payrollNo",
        "Number",
        "ascending"
      );
      sortedList = myJsonArraySort(
        sortedList,
        "Scheduling_Surface",
        "String/Text",
        "descending"
      );

      // Return both the sorted list and the queryPayrollListArray for processing in useEffect
      return {
        sortedList,
        queryPayrollListArray,
      };
    },
  });

  // - - - - - - - - - - - - - - - - -  USEQUERY getCandidatesByPayrollNoList  - - - - - - - - - - - - - - - - - -

  const {
    data: candidatesAdditionalResult,
    isLoading: candidatesAdditionalLoading,
    error: candidatesAdditionalError,
    isFetching: candidatesAdditionalFetching,
  } = useQuery({
    queryKey: ["getCandidatesByPayrollNoList"],
    queryFn: async () => {
      const startTime = performance.now();

      const dynamicBody = {
        method: "getCandidatesByPayrollNoList",
        dataSource: MONGODB_DATASOURCE_TS,
        database: mongoDatabase,
        collection: MONGODB_COLLECTION_CANDIDATES,
        payrollNoArray: candidatesAdditionalQueryList,
        companyCode: companyCode,
        maxLimit: MONGODB_MAXLIMIT_CANDIDATESBYPAYROLLNOLIST,
      };

      const response = await fetch(MONGODB_AWSGW_QUERIES, {
        method: "POST",
        mode: "cors",
        credentials: "omit",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "x-api-key": MONGODB_AWSGW_APIKEY,
        },
        body: JSON.stringify(dynamicBody),
      });

      if (!response.ok) {
        throw new Error(
          `ModelTsContext (${mongoDatabase}) AWSGW_getCandidatesByPayrollNoList(${candidatesAdditionalQueryList.length}) ERROR: ${response.status} ${response.statusText}`
        );
      }

      // Read the response body only once
      const result = await response.json();

      // Check for error in result
      if (result.errorType || result.errorMessage) {
        const errorMsg = result.errorMessage || result.errorType || "Unknown error";
        console.error(
          "%cModelTsContext (" +
            mongoDatabase +
            ") AWSGW_getCandidatesByPayrollNoList(" +
            candidatesAdditionalQueryList.length +
            ") ERROR: ",
          "color: #ff0000; font-weight: bold; background-color: #1a1a1a; padding: 2px 4px;",
          errorMsg,
          result
        );
        throw new Error(`getCandidatesByPayrollNoList: ${errorMsg}`);
      }

      const executionTime = Math.round(performance.now() - startTime);
      setQueryExecutionTimes((prev) => ({
        ...prev,
        getCandidatesByPayrollNoList: executionTime,
      }));

      // Attach execution time to result for immediate access
      result._executionTime = executionTime;

      console.log(
        "%cModelTsContext (" +
          mongoDatabase +
          ") AWSGW_getCandidatesByPayrollNoList() fetchResult: ",
        "color: #00ff00; font-weight: bold; background-color: #1a1a1a; padding: 2px 4px;",
        result
      );

      // Consolidated logging: execution time, payload size, and result
      const responseSizeBytes = new Blob([JSON.stringify(result)]).size;
      console.log(
        `%cModelTsContext [getCandidatesByPayrollNoList] ${executionTime}ms | ${responseSizeBytes} bytes (${(responseSizeBytes / 1024).toFixed(2)} KB)`,
        "color: #00BFFF; font-weight: bold;",
        result
      );

      return result;
    },
    enabled: candidatesAdditionalQueryList.length > 0 && shouldFetchCandidatesAdditional,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    select: (raw) => raw?.message?.result ?? null,
  });

  // - - - - - - - - - - - - - - - - -  USEQUERY getProjectsByPayrollNoList  - - - - - - - - - - - - - - - - - -

  const {
    data: projectsAdditionalResult,
    isLoading: projectsAdditionalLoading,
    error: projectsAdditionalError,
    isFetching: projectsAdditionalFetching,
  } = useQuery({
    queryKey: ["getProjectsByPayrollNoList", locationFilter, projectQueryList.join(','), queryTimestamp],
    queryFn: async () => {
      const startTime = performance.now();
      const capturedGeneration = projectQueryGeneration.current;
      const capturedLocation = locationFilter;
      
      const dynamicBody = {
        method: "getProjectsByPayrollNoList",
        dataSource: MONGODB_DATASOURCE_TS,
        database: mongoDatabase,
        collection: MONGODB_COLLECTION_ACTIVEQUEUE,
        payrollNoArray: projectQueryList,
        companyCode: companyCode,
        locationFilter: locationFilter,
        maxLimit: MONGODB_MAXLIMIT_PROJECTSBYPAYROLLNOLIST,
        _cacheBuster: `${locationFilter}_${queryTimestamp}`,
      };

      const response = await fetch(MONGODB_AWSGW_QUERIES, {
        method: "POST",
        mode: "cors",
        credentials: "omit",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "x-api-key": MONGODB_AWSGW_APIKEY,
        },
        body: JSON.stringify(dynamicBody),
      });

      if (!response.ok) {
        throw new Error(
          `ModelTsContext (${mongoDatabase}) AWSGW_getProjectsByPayrollNoList(${payrollNoArray.length}) ERROR: ${response.status} ${response.statusText}`
        );
      }

      // Read the response body only once
      const result = await response.json();

      // Check for error in result
      if (result.errorType || result.errorMessage) {
        const errorMsg = result.errorMessage || result.errorType || "Unknown error";
        console.error(
          "%cModelTsContext (" +
            mongoDatabase +
            ") AWSGW_getProjectsByPayrollNoList(" +
            projectQueryList.length +
            ") ERROR: ",
          "color: #ff0000; font-weight: bold; background-color: #1a1a1a; padding: 2px 4px;",
          errorMsg,
          result
        );
        throw new Error(`getProjectsByPayrollNoList: ${errorMsg}`);
      }

      const executionTime = Math.round(performance.now() - startTime);
      setQueryExecutionTimes((prev) => ({
        ...prev,
        getProjectsByPayrollNoList: executionTime,
      }));

      // Attach execution time to result for immediate access
      result._executionTime = executionTime;

      console.log(
        "%cModelTsContext (" +
          mongoDatabase +
          ") AWSGW_getProjectsByPayrollNoList() fetchResult: ",
        "color: #00ff00; font-weight: bold; background-color: #1a1a1a; padding: 2px 4px;",
        result
      );

      // Consolidated logging: execution time, payload size, and result
      const responseSizeBytes = new Blob([JSON.stringify(result)]).size;
      console.log(
        `%cModelTsContext [getProjectsByPayrollNoList] ${executionTime}ms | ${responseSizeBytes} bytes (${(responseSizeBytes / 1024).toFixed(2)} KB)`,
        "color: #00BFFF; font-weight: bold;",
        result
      );

      // Attach metadata to result for tracking
      return { 
        ...result, 
        _queryMetadata: { 
          generation: capturedGeneration, 
          expectedLocation: capturedLocation,
          timestamp: queryTimestamp
        } 
      };
    },
    enabled: projectQueryList.length > 0 && 
             shouldFetchProjectsAdditional && 
             projectQueryList.join(',') === expectedProjectQueryList.current && // Only run if projectQueryList matches what we expect
             expectedProjectQueryList.current !== '' && // Don't run if we haven't set a projectQueryList yet
             projectQueryGeneration.current === expectedGeneration.current, // Only run if generation matches
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    select: (raw) => {
      const list = raw?.message?.result ?? [];
      const metadata = raw?._queryMetadata;
      
      // Return the list with metadata attached for tracking
      // Add mongoId for MongoDB _id reference, but preserve existing FileMaker 'id' field
      const mappedList = list.map((doc) => {
        const mongoId =
          doc?._id?.toString?.() ??
          (typeof doc?._id === "string" ? doc._id : "");
        return { ...doc, mongoId };
      });
      
      // Attach metadata to the array itself
      if (metadata) {
        mappedList._queryMetadata = metadata;
      }
      
      return mappedList;
    },
  });

  // - - - - - - - - - - - - - - - - -  USEQUERY getAttributesByLocation  - - - - - - - - - - - - - - - - - -

  const {
    data: attributesResult,
    isLoading: attributesLoading,
    error: attributesError,
    isFetching: attributesFetching,
  } = useQuery({
    queryKey: ["getAttributesByLocation", companyCode, locationFilter],
    queryFn: async () => {
      const startTime = performance.now();

      const dynamicBody = {
        method: "getAttributesByLocation",
        dataSource: MONGODB_DATASOURCE_TS,
        database: mongoDatabase,
        collection: MONGODB_COLLECTION_ATTRIBUTES,
        companyCode: companyCode,
        locationFilter: locationFilter,
        maxLimit: MONGODB_MAXLIMIT_ATTRIBUTESBYLOCATION,
        daysPast: MONGODB_ATTRIBUTES_DAYS_PAST, // Filter to last X days of historical data
      };

      const response = await fetch(MONGODB_AWSGW_QUERIES, {
        method: "POST",
        mode: "cors",
        credentials: "omit",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "x-api-key": MONGODB_AWSGW_APIKEY,
        },
        body: JSON.stringify(dynamicBody),
      });

      if (!response.ok) {
        throw new Error(
          `ModelTsContext (${mongoDatabase}) AWSGW_getAttributesByLocation(${locationFilter}) ERROR: ${response.status} ${response.statusText}`
        );
      }

      // Read the response body only once
      const result = await response.json();

      // Check for error in result
      if (result.errorType || result.errorMessage) {
        const errorMsg = result.errorMessage || result.errorType || "Unknown error";
        console.error(
          "%cModelTsContext (" +
            mongoDatabase +
            ") AWSGW_getAttributesByLocation(" +
            locationFilter +
            ") ERROR: ",
          "color: #ff0000; font-weight: bold; background-color: #1a1a1a; padding: 2px 4px;",
          errorMsg,
          result
        );
        throw new Error(`getAttributesByLocation: ${errorMsg}`);
      }

      const executionTime = Math.round(performance.now() - startTime);
      setQueryExecutionTimes((prev) => ({
        ...prev,
        getAttributesByLocation: executionTime,
      }));

      // Attach execution time to result for immediate access
      result._executionTime = executionTime;

      console.log(
        "%cModelTsContext (" +
          mongoDatabase +
          ") AWSGW_getAttributesByLocation() fetchResult: ",
        "color: #00ff00; font-weight: bold; background-color: #1a1a1a; padding: 2px 4px;",
        result
      );

      // Consolidated logging: execution time, payload size, and result
      const responseSizeBytes = new Blob([JSON.stringify(result)]).size;
      console.log(
        `%cModelTsContext [getAttributesByLocation] ${executionTime}ms | ${responseSizeBytes} bytes (${(responseSizeBytes / 1024).toFixed(2)} KB)`,
        "color: #00BFFF; font-weight: bold;",
        result
      );

      return result;
    },
    enabled: !!companyCode && !!locationFilter && shouldFetchAttributes,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    select: (raw) => raw?.message?.result ?? null,
  });

  // - - - - - - - - - - - - - - - - -  USEQUERY getAttributesByLocationPolling (POLLING ONLY)  - - - - - - - - - - - - - - - - - -

  const {
    data: attributesPollingResult,
    isFetching: attributesPollingFetching,
  } = useQuery({
    // Include isUserIdle in queryKey so refetchInterval re-evaluates when idle state changes
    queryKey: ["getAttributesByLocationPolling", companyCode, locationFilter, isUserIdle],
    queryFn: async () => {
      const startTime = performance.now();

      const dynamicBody = {
        method: "getAttributesByLocation",
        dataSource: MONGODB_DATASOURCE_TS,
        database: mongoDatabase,
        collection: MONGODB_COLLECTION_ATTRIBUTES,
        companyCode: companyCode,
        locationFilter: locationFilter,
        maxLimit: MONGODB_MAXLIMIT_ATTRIBUTESBYLOCATION,
        daysPast: MONGODB_ATTRIBUTES_DAYS_PAST, // Filter to last X days of historical data
      };

      const response = await fetch(MONGODB_AWSGW_QUERIES, {
        method: "POST",
        mode: "cors",
        credentials: "omit",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "x-api-key": MONGODB_AWSGW_APIKEY,
        },
        body: JSON.stringify(dynamicBody),
      });

      if (!response.ok) {
        throw new Error(
          `ModelTsContext (${mongoDatabase}) AWSGW_getAttributesByLocationPolling ERROR: ${response.status} ${response.statusText}`
        );
      }

      const result = await response.json();

      if (result.errorType || result.errorMessage) {
        const errorMsg = result.errorMessage || result.errorType || "Unknown error";
        throw new Error(`getAttributesByLocationPolling: ${errorMsg}`);
      }

      const executionTime = Math.round(performance.now() - startTime);
      result._executionTime = executionTime;

      return result;
    },
    // Time Machine #114: same gate as the projects-polling query above.
    enabled: !!companyCode && !!locationFilter && shouldPollAttributes && !isAnyModalOpen,
    // POLLING ONLY - use refetchInterval to poll every 10 seconds on Scheduler/Optimizer tabs
    // Use function form to check if still fetching (prevents request pile-up on slow connections)
    refetchInterval: (query) => {
      // Don't schedule next poll if still fetching (prevents request pile-up)
      if (query.state.fetchStatus === 'fetching') {
        console.log(
          "%c⏸️ ATTRIBUTES POLLING SKIPPED - previous query still fetching",
          "color: #FFA500; font-weight: bold; background-color: #1a1a1a; padding: 2px 4px;"
        );
        return false;
      }
      // Don't poll during initial load or retry scenarios - prevents compounding failed requests
      if (failedQueries.size > 0 || retryingQueries.size > 0) {
        return false;
      }
      // Normal polling: active when on Scheduler/Optimizer tabs and user is not idle
      // Pause when ANY modal is open to prevent race conditions with optimistic updates
      const shouldPollNormally = shouldPollAttributes && !isUserIdle && !isAnyModalOpen && (whichList === "optimizer" || whichList === "schedule");
      // Override polling: temporarily resume when change stream event arrived while idle (for verification)
      const shouldPollForVerification = shouldPollAttributes && !isAnyModalOpen && attributesChangeStreamPollingOverride.active && attributesChangeStreamPollingOverride.remainingPolls > 0;
      return (shouldPollNormally || shouldPollForVerification)
        ? MONGODB_POLLING_INTERVAL_ATTRIBUTES * 1000
        : false;
    },
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    select: (raw) => {
      const result = raw?.message?.result ?? [];
      // Pass through execution time
      if (raw?._executionTime) {
        result._executionTime = raw._executionTime;
      }
      return result;
    },
  });

  // - - - - - - - - - - - - - - - - -  USEQUERY getLogs  - - - - - - - - - - - - - - - - - -

  const {
    data: logsResult,
    isLoading: logsLoading,
    error: logsError,
    isFetching: logsFetching,
  } = useQuery({
    queryKey: ["getLogs", locationFilter, logsUserFilter, logsDateFilter, logsDaysFilter, logsPage, logsPageSize, logsSortColumn, logsSortDirection],
    queryFn: async () => {
      const startTime = performance.now();

      // Calculate date range for filtering
      let dateFilterValue = null;
      let daysFilterValue = null;
      
      if (logsDateFilter) {
        // Specific date selected - only send dateFilter
        const [year, month, day] = logsDateFilter.split('-');
        dateFilterValue = `${parseInt(month)}/${parseInt(day)}/${year}`;
      } else if (logsDaysFilter !== 'all') {
        // Days range filter - only send daysFilter as number
        daysFilterValue = parseInt(logsDaysFilter);
      }

      // Build query with filters, pagination, and sorting
      const dynamicBody = {
        method: "getLogs",
        dataSource: MONGODB_DATASOURCE_TS,
        database: mongoDatabase,
        collection: MONGODB_COLLECTION_LOGS,
        maxLimit: MONGODB_MAXLIMIT_LOGS,
        userFilter: logsUserFilter !== "all" ? logsUserFilter : null,
        dateFilter: dateFilterValue,
        daysFilter: daysFilterValue,
        page: logsPage,
        pageSize: logsPageSize,
        sortColumn: logsSortColumn,
        sortDirection: logsSortDirection,
      };

      // console.error(
      //       "%cModelTsContext (" +
      //         mongoDatabase +
      //         ") AWSGW_getLogs() RAW filters: ",
      //       "color: #ff00ff; font-weight: bold; background-color: #1a1a1a; padding: 2px 4px;",
      //       {
      //         logsUserFilter,
      //         logsDateFilter, 
      //         logsDaysFilter,
      //         logsPage,
      //         logsPageSize,
      //         logsSortColumn,
      //         logsSortDirection
      //       }
      //     );

      // console.error(
      //       "%cModelTsContext (" +
      //         mongoDatabase +
      //         ") AWSGW_getLogs() params: ",
      //       "color: #eeff00ff; font-weight: bold; background-color: #1a1a1a; padding: 2px 4px;",
      //       dynamicBody
      //     );

      const response = await fetch(MONGODB_AWSGW_QUERIES, {
        method: "POST",
        mode: "cors",
        credentials: "omit",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "x-api-key": MONGODB_AWSGW_APIKEY,
        },
        body: JSON.stringify(dynamicBody),
      });

      if (!response.ok) {
        throw new Error(
          `ModelTsContext (${mongoDatabase}) AWSGW_getLogs(${locationFilter}) ERROR: ${response.status} ${response.statusText}`
        );
      }

      // Read the response body only once
      const result = await response.json();

      // Check for error in result
      if (result.errorType || result.errorMessage) {
        const errorMsg = result.errorMessage || result.errorType || "Unknown error";
        // Only log on initial fetch to avoid duplicates
        if (isInitialLogsFetch.current) {
          console.error(
            "%cModelTsContext (" +
              mongoDatabase +
              ") AWSGW_getLogs() ERROR: ",
            "color: #ff0000; font-weight: bold; background-color: #1a1a1a; padding: 2px 4px;",
            errorMsg,
            result
          );
          isInitialLogsFetch.current = false;
        }
        throw new Error(`getLogs: ${errorMsg}`);
      }

      const executionTime = Math.round(performance.now() - startTime);
      setQueryExecutionTimes((prev) => ({ ...prev, getLogs: executionTime }));

      // Attach execution time to result for immediate access
      result._executionTime = executionTime;

      // Only log on initial fetch, not during polling
      if (isInitialLogsFetch.current) {
        // Consolidated logging: execution time, payload size, and result
        const responseSizeBytes = new Blob([JSON.stringify(result)]).size;
        console.log(
          `%cModelTsContext [getLogs] ${executionTime}ms | ${responseSizeBytes} bytes (${(responseSizeBytes / 1024).toFixed(2)} KB)`,
          "color: #00BFFF; font-weight: bold;",
          result
        );
        isInitialLogsFetch.current = false;
      }

      return result;
    },
    enabled: true, // Always enabled to allow manual refetches when filters/sort change
    retry: false, // Don't retry on error to prevent duplicate error logs
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    // Use function form to prevent polling pile-up when queries take longer than interval
    refetchInterval: (query) => {
      if (query.state.fetchStatus === 'fetching') return false;
      return shouldFetchLogs && whichList === "logs" ? 10000 : false;
    },
    select: (raw) => {
      const logs = raw?.message?.result ?? [];
      const totalCount = raw?.message?.totalCount ?? 0;
      const executionTime = raw?._executionTime ?? 0;
      
      // Update total count for pagination UI
      if (totalCount !== logsTotalCount) {
        setLogsTotalCount(totalCount);
      }
      
      return {
        logs,
        totalCount,
        _executionTime: executionTime,
      };
    },
  });

  // - - - - - - - - - - - - - - - - -  USEQUERY getLogsUniqueUsers  - - - - - - - - - - - - - - - - - -
  // Fetches all unique users who have ever created log entries
  // Used to populate the User filter dropdown in the Logs screen

  const {
    data: logsUniqueUsersResult,
    // isLoading + error intentionally not destructured (#79) — UI doesn't surface them today
  } = useQuery({
    queryKey: ["getLogsUniqueUsers"],
    queryFn: async () => {
      const startTime = performance.now();

      const dynamicBody = {
        method: "getLogsUniqueUsers",
        dataSource: MONGODB_DATASOURCE_TS,
        database: mongoDatabase,
        collection: MONGODB_COLLECTION_LOGS,
      };

      const response = await fetch(MONGODB_AWSGW_QUERIES, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-api-key": MONGODB_AWSGW_APIKEY,
        },
        body: JSON.stringify(dynamicBody),
      });
      const result = await response.json();
      if (result?.message?.errorType || result?.errorType) {
        const errorMsg =
          result?.message?.errorMessage ||
          result?.errorMessage ||
          "Unknown error";
        console.error(
          "%cModelTsContext AWSGW_getLogsUniqueUsers() ERROR: ",
          "color: #FF0000; font-weight: bold;",
          errorMsg,
          result
        );
        throw new Error(`getLogsUniqueUsers: ${errorMsg}`);
      }

      const executionTime = Math.round(performance.now() - startTime);
      const users = result?.message?.result ?? [];

      console.log(
        `%cModelTsContext [getLogsUniqueUsers] ${executionTime}ms | ${users.length} unique users`,
        "color: #00BFFF; font-weight: bold;",
        users
      );

      return users;
    },
    // Only fetch once when logs screen is accessed (staleTime keeps it cached)
    enabled: shouldFetchLogs,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    refetchOnWindowFocus: false,
  });

  // - - - - - - - - - - - - - - - - -  USEQUERY getLogsChartData  - - - - - - - - - - - - - - - - - -

  const [shouldFetchLogsChartData, setShouldFetchLogsChartData] = useState(false);

  const {
    data: logsChartDataResult,
    // isLoading + error intentionally not destructured (#79) — UI doesn't surface them today
    isFetching: logsChartDataFetching,
  } = useQuery({
    queryKey: ["getLogsChartData", logsUserFilter, logsDateFilter, logsDaysFilter],
    queryFn: async () => {
      const startTime = performance.now();

      // Calculate date range for filtering (same as getLogs)
      let dateFilterValue = null;
      let daysFilterValue = null;

      if (logsDateFilter) {
        const [year, month, day] = logsDateFilter.split('-');
        dateFilterValue = `${parseInt(month)}/${parseInt(day)}/${year}`;
      } else if (logsDaysFilter !== 'all') {
        daysFilterValue = parseInt(logsDaysFilter);
      }

      const dynamicBody = {
        method: "getLogsChartData",
        dataSource: MONGODB_DATASOURCE_TS,
        database: mongoDatabase,
        collection: MONGODB_COLLECTION_LOGS,
        userFilter: logsUserFilter !== "all" ? logsUserFilter : null,
        dateFilter: dateFilterValue,
        daysFilter: daysFilterValue,
      };

      const response = await fetch(MONGODB_AWSGW_QUERIES, {
        method: "POST",
        mode: "cors",
        credentials: "omit",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "x-api-key": MONGODB_AWSGW_APIKEY,
        },
        body: JSON.stringify(dynamicBody),
      });

      if (!response.ok) {
        throw new Error(
          `ModelTsContext (${mongoDatabase}) AWSGW_getLogsChartData() ERROR: ${response.status} ${response.statusText}`
        );
      }

      const result = await response.json();

      if (result.errorType || result.errorMessage) {
        const errorMsg = result.errorMessage || result.errorType || "Unknown error";
        console.error(
          "%cModelTsContext (" +
            mongoDatabase +
            ") AWSGW_getLogsChartData() ERROR: ",
          "color: #ff0000; font-weight: bold; background-color: #1a1a1a; padding: 2px 4px;",
          errorMsg,
          result
        );
        throw new Error(`getLogsChartData: ${errorMsg}`);
      }

      const executionTime = Math.round(performance.now() - startTime);

      console.log(
        `%cModelTsContext [getLogsChartData] ${executionTime}ms`,
        "color: #00BFFF; font-weight: bold;",
        result
      );

      return result;
    },
    enabled: shouldFetchLogsChartData,
    // Use function form to prevent polling pile-up when queries take longer than interval
    refetchInterval: (query) => {
      if (query.state.fetchStatus === 'fetching') return false;
      return shouldFetchLogsChartData ? 10000 : false;
    },
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    select: (raw) => raw?.message?.result ?? [],
  });

  // - - - - - - - - - - - - - - - - -  USEQUERY getLogsUserActivityTime  - - - - - - - - - - - - - - - - - -

  const [shouldFetchLogsActivityTime, setShouldFetchLogsActivityTime] = useState(false);
  const [activityTimeParams, setActivityTimeParams] = useState({
    topN: 10,
    sortOrder: 'desc',
    specificUser: null,
    skip: 0
  });

  const {
    data: logsActivityTimeResult,
    // isLoading + error intentionally not destructured (#79) — UI doesn't surface them today
    isFetching: logsActivityTimeFetching,
  } = useQuery({
    queryKey: ["getLogsUserActivityTime", logsUserFilter, logsDateFilter, logsDaysFilter, activityTimeParams.topN, activityTimeParams.sortOrder, activityTimeParams.specificUser, activityTimeParams.skip],
    queryFn: async () => {
      const startTime = performance.now();

      // Calculate date range for filtering (same as getLogs)
      let dateFilterValue = null;
      let daysFilterValue = null;

      if (logsDateFilter) {
        const [year, month, day] = logsDateFilter.split('-');
        dateFilterValue = `${parseInt(month)}/${parseInt(day)}/${year}`;
      } else if (logsDaysFilter !== 'all') {
        daysFilterValue = parseInt(logsDaysFilter);
      }

      const dynamicBody = {
        method: "getLogsUserActivityTime",
        dataSource: MONGODB_DATASOURCE_TS,
        database: mongoDatabase,
        collection: MONGODB_COLLECTION_LOGS,
        userFilter: logsUserFilter !== "all" ? logsUserFilter : null,
        dateFilter: dateFilterValue,
        daysFilter: daysFilterValue,
        topN: activityTimeParams.topN,
        sortOrder: activityTimeParams.sortOrder,
        specificUser: activityTimeParams.specificUser,
        skip: activityTimeParams.skip,
      };

      const response = await fetch(MONGODB_AWSGW_QUERIES, {
        method: "POST",
        mode: "cors",
        credentials: "omit",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "x-api-key": MONGODB_AWSGW_APIKEY,
        },
        body: JSON.stringify(dynamicBody),
      });

      if (!response.ok) {
        throw new Error(
          `ModelTsContext (${mongoDatabase}) AWSGW_getLogsUserActivityTime() ERROR: ${response.status} ${response.statusText}`
        );
      }

      const result = await response.json();

      if (result.errorType || result.errorMessage) {
        const errorMsg = result.errorMessage || result.errorType || "Unknown error";
        console.error(
          "%cModelTsContext (" +
            mongoDatabase +
            ") AWSGW_getLogsUserActivityTime() ERROR: ",
          "color: #ff0000; font-weight: bold; background-color: #1a1a1a; padding: 2px 4px;",
          errorMsg,
          result
        );
        throw new Error(`getLogsUserActivityTime: ${errorMsg}`);
      }

      const executionTime = Math.round(performance.now() - startTime);

      console.log(
        `%cModelTsContext [getLogsUserActivityTime] ${executionTime}ms`,
        "color: #00BFFF; font-weight: bold;",
        result
      );

      return result;
    },
    enabled: shouldFetchLogsActivityTime,
    // Use function form to prevent polling pile-up when queries take longer than interval
    refetchInterval: (query) => {
      if (query.state.fetchStatus === 'fetching') return false;
      return shouldFetchLogsActivityTime ? 10000 : false;
    },
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    select: (raw) => raw?.message?.result ?? [],
  });

  // - - - - - - - - - - - - - - - - -  USEQUERY getLogsAttributesPerformance  - - - - - - - - - - - - - - - - - -

  const [shouldFetchAttributesPerformance, setShouldFetchAttributesPerformance] = useState(false);
  const [attributesPerformanceParams, setAttributesPerformanceParams] = useState({
    daysFilter: 30,
    locationFilter: null, // null = all locations, or array of location strings
  });

  const {
    data: attributesPerformanceResult,
    // isLoading + error intentionally not destructured (#79) — UI doesn't surface them today
    isFetching: attributesPerformanceFetching,
  } = useQuery({
    queryKey: ["getLogsAttributesPerformance", attributesPerformanceParams.daysFilter, attributesPerformanceParams.locationFilter],
    queryFn: async () => {
      const startTime = performance.now();

      const dynamicBody = {
        method: "getLogsAttributesPerformance",
        dataSource: MONGODB_DATASOURCE_TS,
        database: mongoDatabase,
        collection: MONGODB_COLLECTION_LOGS,
        daysFilter: attributesPerformanceParams.daysFilter,
        locationFilter: attributesPerformanceParams.locationFilter,
      };

      const response = await fetch(MONGODB_AWSGW_QUERIES, {
        method: "POST",
        mode: "cors",
        credentials: "omit",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "x-api-key": MONGODB_AWSGW_APIKEY,
        },
        body: JSON.stringify(dynamicBody),
      });

      if (!response.ok) {
        throw new Error(
          `ModelTsContext (${mongoDatabase}) AWSGW_getLogsAttributesPerformance() ERROR: ${response.status} ${response.statusText}`
        );
      }

      const result = await response.json();

      if (result.errorType || result.errorMessage) {
        const errorMsg = result.errorMessage || result.errorType || "Unknown error";
        console.error(
          "%cModelTsContext (" +
            mongoDatabase +
            ") AWSGW_getLogsAttributesPerformance() ERROR: ",
          "color: #ff0000; font-weight: bold; background-color: #1a1a1a; padding: 2px 4px;",
          errorMsg,
          result
        );
        throw new Error(`getLogsAttributesPerformance: ${errorMsg}`);
      }

      const executionTime = Math.round(performance.now() - startTime);

      console.log(
        `%cModelTsContext [getLogsAttributesPerformance] ${executionTime}ms`,
        "color: #00BFFF; font-weight: bold;",
        result
      );

      return result;
    },
    enabled: shouldFetchAttributesPerformance,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    select: (raw) => raw?.message?.result ?? { data: [], locations: [] },
  });

  //
  //
  // - - - - - - - - - - - - - - - - -  TIME MACHINE useQueries + replay (#114 Phase 4.5)  - - - - - - - - - - - - - - - - - -
  //
  // Three queries (Attributes / ActiveQueue / Candidates) call the Lambda's
  // `getStateAndEventsForDate` method (v05-08-2026-04-QRY01+). Each returns:
  //   - baselineState[]: full reconstructed state at 00:00 UTC of the picked date
  //   - events[]: every EventLog entry for that collection+location during the date
  //
  // Frontend caches both per collection. As the user scrubs the time slider
  // (timeTravelSliderTime), useMemo blocks below replay events at-or-before
  // the slider time against the baseline — entirely in memory, zero API calls.
  //
  // Offices NOT included — see Phase 4 architectural decision in docs/time-machine.md.
  //
  // Live queries stay paused via `isAnyModalOpen` (which folds in
  // `isTimeTraveling`). Mutations blocked at the data layer (mutation hooks
  // each throw if `isTimeTraveling`).

  // Detects the layer-3 time-travel guard error so its 4 mutation onError
  // handlers can suppress the user-facing alert. The guard's throw uses a
  // distinctive marker ("blocked while time-traveling (#114)"); any other
  // mutation failure (real Lambda/network error) still alerts as before.
  const isTimeTravelGuardError = (error) =>
    typeof error?.message === "string" &&
    error.message.includes("blocked while time-traveling");

  const callDayBundleLambda = async ({ targetCollection, location, dateUtc }) => {
    const body = buildStateAndEventsRequestBody({
      targetCollection,
      database: mongoDatabase,
      location,
      date: dateUtc,
    });
    // Lambda v04+ post-filters by companyCode for live-handler-mirror parity.
    if (companyCode) body.companyCode = companyCode;
    const startTime = performance.now();
    const response = await fetch(MONGODB_AWSGW_QUERIES, {
      method: "POST",
      mode: "cors",
      credentials: "omit",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        "x-api-key": MONGODB_AWSGW_APIKEY,
      },
      body: JSON.stringify(body),
    });
    if (!response.ok) {
      throw new Error(
        `ModelTsContext getStateAndEventsForDate(${targetCollection}) HTTP ${response.status} ${response.statusText}`
      );
    }
    const result = await response.json();
    const executionTime = Math.round(performance.now() - startTime);
    console.log(
      `%cModelTsContext [getStateAndEventsForDate ${targetCollection}/${location}] ${executionTime}ms`,
      "color: #DAA520; font-weight: bold;",
      { baselineCount: result?.message?.result?.baselineState?.length, eventsCount: result?.message?.result?.events?.length }
    );
    return result;
  };

  const isTimeTravelDateValid =
    isTimeTraveling &&
    timeTravelDate instanceof Date &&
    !isNaN(timeTravelDate.getTime());

  // YYYY-MM-DD key for stable queryKey identity. Two Date objects representing
  // the same UTC day produce the same key — no spurious refetches.
  const timeTravelDateKey = isTimeTravelDateValid
    ? timeTravelDate.toISOString().slice(0, 10)
    : null;

  // - - - - - - - Attributes (per location, full day) - - - - - - -
  // Note: NO setState side effects. Caller reads `data` and derives via
  // useMemo below. Reason: setState in queryFn doesn't re-fire on cached
  // re-renders, breaking exit + re-entry. The select fn parses; the cached
  // `data` IS the parsed result.
  const { data: attrsQueryData, isFetching: timeTravelAttributesFetching } = useQuery({
    queryKey: ["timeTravelDay", "Attributes", locationFilter, timeTravelDateKey],
    queryFn: async () =>
      callDayBundleLambda({
        targetCollection: "Attributes",
        location: locationFilter,
        dateUtc: timeTravelDate,
      }),
    select: (raw) => parseStateAndEventsResponse(raw),
    enabled: isTimeTravelDateValid && !!locationFilter,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    staleTime: Infinity, // historical bundle for a given (date, location) is immutable
  });

  // - - - - - - - ActiveQueue (per location, full day) - - - - - - -
  const { data: projectsQueryData, isFetching: timeTravelProjectsFetching } = useQuery({
    queryKey: ["timeTravelDay", "ActiveQueue", locationFilter, timeTravelDateKey],
    queryFn: async () =>
      callDayBundleLambda({
        targetCollection: "ActiveQueue",
        location: locationFilter,
        dateUtc: timeTravelDate,
      }),
    select: (raw) => parseStateAndEventsResponse(raw),
    enabled: isTimeTravelDateValid && !!locationFilter,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    staleTime: Infinity,
  });

  // - - - - - - - Candidates (per location, full day) - - - - - - -
  const { data: candidatesQueryData, isFetching: timeTravelCandidatesFetching } = useQuery({
    queryKey: ["timeTravelDay", "Candidates", locationFilter, timeTravelDateKey],
    queryFn: async () =>
      callDayBundleLambda({
        targetCollection: "Candidates",
        location: locationFilter,
        dateUtc: timeTravelDate,
      }),
    select: (raw) => parseStateAndEventsResponse(raw),
    enabled: isTimeTravelDateValid && !!locationFilter,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    staleTime: Infinity,
  });

  // - - - - - - - Derived state (everything below is pure useMemo) - - - - - - -
  //
  // Each query's `data` is the parsed response. We compute baseline (Map) +
  // events (Array) per collection, plus aggregate error and meta. All pure;
  // re-renders re-derive from cached query data. Exit + re-entry with same
  // queryKey hits TanStack's cache → same `data` → same derivations. No
  // empty-screen-on-re-entry bug.

  const buildBaselineMap = (parsed) => {
    if (!parsed?.ok) return new Map();
    const m = new Map();
    for (const doc of parsed.baselineState) {
      const id = doc?._id?.toString?.() ?? doc?._id;
      if (id != null) m.set(String(id), doc);
    }
    return m;
  };

  const timeTravelAttributesBaseline = useMemo(
    () => buildBaselineMap(attrsQueryData),
    [attrsQueryData]
  );
  const timeTravelAttributesEvents = useMemo(
    () => (attrsQueryData?.ok ? attrsQueryData.events : []),
    [attrsQueryData]
  );
  const timeTravelProjectsBaseline = useMemo(
    () => buildBaselineMap(projectsQueryData),
    [projectsQueryData]
  );
  const timeTravelProjectsEvents = useMemo(
    () => (projectsQueryData?.ok ? projectsQueryData.events : []),
    [projectsQueryData]
  );
  const timeTravelCandidatesBaseline = useMemo(
    () => buildBaselineMap(candidatesQueryData),
    [candidatesQueryData]
  );
  const timeTravelCandidatesEvents = useMemo(
    () => (candidatesQueryData?.ok ? candidatesQueryData.events : []),
    [candidatesQueryData]
  );

  // Aggregate error: surface the first failed query (most likely the same
  // failure mode for all three — floor refusal, network, etc.). If all 3 OK,
  // null.
  const timeTravelError = useMemo(() => {
    for (const q of [attrsQueryData, projectsQueryData, candidatesQueryData]) {
      if (q && q.ok === false) return q;
    }
    return null;
  }, [attrsQueryData, projectsQueryData, candidatesQueryData]);

  // Meta from the Attributes query (largest, representative). Useful for
  // debug surfaces; not on critical path.
  const timeTravelMeta = useMemo(() => {
    if (!attrsQueryData?.ok) return null;
    return {
      baselineSnapshot: attrsQueryData.baselineSnapshot,
      seedToStartReplayWindow: attrsQueryData.seedToStartReplayWindow,
      dayWindow: attrsQueryData.dayWindow,
      meta: attrsQueryData.meta,
      lambdaVersion: attrsQueryData.lambdaVersion,
    };
  }, [attrsQueryData]);

  const timeTravelLoading =
    isTimeTraveling &&
    (timeTravelAttributesFetching ||
      timeTravelProjectsFetching ||
      timeTravelCandidatesFetching);

  // - - - - - - - In-memory replay derivations - - - - - - -
  //
  // These useMemo blocks recompute every time the slider moves. The work is:
  //   1. Filter cached events to those at-or-before sliderTime
  //   2. Run replayState(baseline, filtered) — pure, O(events) per call
  //   3. Convert resulting Map to Array for ScheduleContext consumers
  //
  // Typical day at one location: ~50 events × ~3 KB each → sub-millisecond replay.
  //
  // ScheduleContext reads timeTravelAttributesResult / timeTravelProjectsResult /
  // timeTravelCandidatesResult — same names as Phase 4. Downstream is oblivious
  // that the data now comes from in-memory replay vs a fresh API call.

  const timeTravelSliderTimeMs =
    timeTravelSliderTime instanceof Date ? timeTravelSliderTime.getTime() : null;

  const replayedAttributesArray = useMemo(() => {
    if (!isTimeTraveling || timeTravelSliderTimeMs == null) return [];
    const cutoff = timeTravelSliderTimeMs;
    const eligible = timeTravelAttributesEvents.filter(
      (e) => new Date(e.timestamp).getTime() <= cutoff
    );
    const { state } = replayState(timeTravelAttributesBaseline, eligible);
    return Array.from(state.values());
  }, [
    isTimeTraveling,
    timeTravelSliderTimeMs,
    timeTravelAttributesBaseline,
    timeTravelAttributesEvents,
  ]);

  const replayedProjectsArray = useMemo(() => {
    if (!isTimeTraveling || timeTravelSliderTimeMs == null) return [];
    const cutoff = timeTravelSliderTimeMs;
    const eligible = timeTravelProjectsEvents.filter(
      (e) => new Date(e.timestamp).getTime() <= cutoff
    );
    const { state } = replayState(timeTravelProjectsBaseline, eligible);
    return Array.from(state.values());
  }, [
    isTimeTraveling,
    timeTravelSliderTimeMs,
    timeTravelProjectsBaseline,
    timeTravelProjectsEvents,
  ]);

  const replayedCandidatesArray = useMemo(() => {
    if (!isTimeTraveling || timeTravelSliderTimeMs == null) return [];
    const cutoff = timeTravelSliderTimeMs;
    const eligible = timeTravelCandidatesEvents.filter(
      (e) => new Date(e.timestamp).getTime() <= cutoff
    );
    const { state } = replayState(timeTravelCandidatesBaseline, eligible);
    return Array.from(state.values());
  }, [
    isTimeTraveling,
    timeTravelSliderTimeMs,
    timeTravelCandidatesBaseline,
    timeTravelCandidatesEvents,
  ]);

  // Backward-compat names exposed via context (ScheduleContext keeps reading these).
  const timeTravelAttributesResult = replayedAttributesArray;
  const timeTravelProjectsResult = replayedProjectsArray;
  const timeTravelCandidatesResult = replayedCandidatesArray;

  // Slider tick source: union of Attributes + ActiveQueue events sorted by
  // timestamp. Per Phase 4.5 design, Candidates events are excluded from the
  // tick set (rare changes, less crowded slider). Sorting is stable on
  // (timestamp, _id) so ticks at the exact same millisecond have a defined order.
  const timeTravelSliderEvents = useMemo(() => {
    if (!isTimeTraveling) return [];
    const merged = [
      ...timeTravelAttributesEvents,
      ...timeTravelProjectsEvents,
    ];
    merged.sort((a, b) => {
      const ta = new Date(a.timestamp).getTime();
      const tb = new Date(b.timestamp).getTime();
      if (ta !== tb) return ta - tb;
      const ia = String(a._id ?? "");
      const ib = String(b._id ?? "");
      return ia.localeCompare(ib);
    });
    return merged;
  }, [isTimeTraveling, timeTravelAttributesEvents, timeTravelProjectsEvents]);

  // No exit-cleanup useEffect needed in Phase 4.5: baseline/events/error/meta
  // are all useMemo derivations from the three useQuery `data` slots. When
  // isTimeTraveling becomes false:
  //   - The 3 queries are disabled (enabled: isTimeTravelDateValid && ...)
  //     so they won't fire new requests, but TanStack KEEPS the last cached
  //     response in the query cache.
  //   - timeTravel*Result useMemos read isTimeTraveling and return [] when
  //     false, so the schedule grid renders live data correctly.
  //   - On re-entry with same date+location, TanStack returns the cached
  //     response → same data → useMemos rebuild baseline/events instantly.
  //     No empty-screen-on-re-entry.

  // Auto-snap slider to time of LAST EVENT when the day's events arrive
  // (per Phase 4.5 design: "no empty space to the right of the slider").
  // Idempotent: only fires when sliderTime is still at exactly LOCAL
  // end-of-day (the value enterTimeTravel seeded — see app.context.js).
  // Once the user manually scrubs, the condition no longer holds and this
  // effect doesn't re-fire — letting the user's intent stick. Re-entering
  // a date (via DayPicker prev/next) calls enterTimeTravel again which
  // re-seeds sliderTime to local end-of-day, so the snap re-fires on the
  // next batch of events. Clean.
  //
  // The "is slider still at the seeded position?" check lives in the pure
  // helper isSliderAtLocalDayEnd — extracted in PR #118 follow-up after
  // code review caught a UTC-vs-LOCAL mismatch (the prior inline code used
  // Date.UTC(...) which never matched the LOCAL value enterTimeTravel
  // produced for non-UTC users — silently breaking the snap promise for
  // every PROD user since Keller is US-based). See
  // src/__tests__/isSliderAtLocalDayEnd.test.js for the regression suite.
  useEffect(() => {
    if (!isTimeTraveling) return;
    if (timeTravelSliderEvents.length === 0) return;
    if (!isSliderAtLocalDayEnd(timeTravelSliderTime, timeTravelDate)) return;

    const lastEventMs = new Date(
      timeTravelSliderEvents[timeTravelSliderEvents.length - 1].timestamp
    ).getTime();
    if (isNaN(lastEventMs)) return;

    setTimeTravelSliderTime(new Date(lastEventMs));
  }, [
    isTimeTraveling,
    timeTravelDate,
    timeTravelSliderTime,
    timeTravelSliderEvents,
    setTimeTravelSliderTime,
  ]);

  // - - - - - - - - - - - - - - - - -  TIME MACHINE Phase 6 — Diff Pair right state  - - - - - - - - - - - - - - - - - -
  //
  // When isDiffPairView is true, the Optimizer renders a split-screen grid
  // pair: LEFT shows state at T1 (timeTravelSliderTime), RIGHT shows state at
  // T2 (diffPairRightSliderTime). Both grids replay against the SAME baseline
  // and SAME cached event list — only the cutoff differs. So the right-state
  // derivations mirror the left-state ones, just with a different cutoff and
  // gated on `isDiffPairView` so the work doesn't run when not needed.
  //
  // T2 auto-snaps to the next event after T1 (spec: docs/time-machine.md § Phase 6
  // bullet "Slider behavior"). Auto-snap fires whenever T1 changes or DiffPair
  // flips on. Manual user drag of T2 (iter 6) sets diffPairRightSliderTime
  // directly; this effect's dependency array intentionally OMITS T2, so manual
  // drags don't re-fire the snap. When T1 next moves, the snap re-overrides
  // the user's manual T2 — that matches spec.

  const diffPairRightSliderTimeMs =
    diffPairRightSliderTime instanceof Date
      ? diffPairRightSliderTime.getTime()
      : null;

  const replayedAttributesArrayRight = useMemo(() => {
    if (!isTimeTraveling || !isDiffPairView) return [];
    if (diffPairRightSliderTimeMs == null) return [];
    const cutoff = diffPairRightSliderTimeMs;
    const eligible = timeTravelAttributesEvents.filter(
      (e) => new Date(e.timestamp).getTime() <= cutoff
    );
    const { state } = replayState(timeTravelAttributesBaseline, eligible);
    return Array.from(state.values());
  }, [
    isTimeTraveling,
    isDiffPairView,
    diffPairRightSliderTimeMs,
    timeTravelAttributesBaseline,
    timeTravelAttributesEvents,
  ]);

  const replayedProjectsArrayRight = useMemo(() => {
    if (!isTimeTraveling || !isDiffPairView) return [];
    if (diffPairRightSliderTimeMs == null) return [];
    const cutoff = diffPairRightSliderTimeMs;
    const eligible = timeTravelProjectsEvents.filter(
      (e) => new Date(e.timestamp).getTime() <= cutoff
    );
    const { state } = replayState(timeTravelProjectsBaseline, eligible);
    return Array.from(state.values());
  }, [
    isTimeTraveling,
    isDiffPairView,
    diffPairRightSliderTimeMs,
    timeTravelProjectsBaseline,
    timeTravelProjectsEvents,
  ]);

  const replayedCandidatesArrayRight = useMemo(() => {
    if (!isTimeTraveling || !isDiffPairView) return [];
    if (diffPairRightSliderTimeMs == null) return [];
    const cutoff = diffPairRightSliderTimeMs;
    const eligible = timeTravelCandidatesEvents.filter(
      (e) => new Date(e.timestamp).getTime() <= cutoff
    );
    const { state } = replayState(timeTravelCandidatesBaseline, eligible);
    return Array.from(state.values());
  }, [
    isTimeTraveling,
    isDiffPairView,
    diffPairRightSliderTimeMs,
    timeTravelCandidatesBaseline,
    timeTravelCandidatesEvents,
  ]);

  const timeTravelAttributesResultRight = replayedAttributesArrayRight;
  const timeTravelProjectsResultRight = replayedProjectsArrayRight;
  const timeTravelCandidatesResultRight = replayedCandidatesArrayRight;

  // Phase 7 review feedback (2026-05-11) — Diff Pair seeds at whole-day:
  // T1 = 00:00 local, T2 = 23:59 local of timeTravelDate. The previous
  // "auto-snap T2 to next event after T1 whenever T1 changes" behavior
  // was removed because users were manually expanding the window to the
  // whole day on every entry — the auto-snap fought the actual usage.
  //
  // This effect fires on Diff Pair TOGGLE-ON (the user clicks Add Grid to
  // re-enable the pair view after collapsing). enterTimeTravel handles
  // first-entry and day-nav seeding directly; that path leaves T2 as a
  // Date when Diff Pair is active. So the guard `T2 == null` here means
  // "Diff Pair just got toggled back on" — re-seed both sliders to the
  // day boundaries. Manual drags of T1 or T2 do not trigger re-seed
  // (T2 is a Date during normal use; only null during the post-toggle-on
  // moment).
  useEffect(() => {
    if (!isTimeTraveling || !isDiffPairView) return;
    if (!(timeTravelDate instanceof Date) || isNaN(timeTravelDate.getTime())) return;
    if (diffPairRightSliderTime != null) return; // already seeded — leave it.
    const yyyy = timeTravelDate.getFullYear();
    const mm = timeTravelDate.getMonth();
    const dd = timeTravelDate.getDate();
    const dayStart = new Date(yyyy, mm, dd, 0, 0, 0, 0);
    const dayEnd = new Date(yyyy, mm, dd, 23, 59, 59, 999);
    setTimeTravelSliderTime(dayStart);
    setDiffPairRightSliderTime(dayEnd);
  }, [
    isTimeTraveling,
    isDiffPairView,
    timeTravelDate,
    diffPairRightSliderTime,
    setTimeTravelSliderTime,
    setDiffPairRightSliderTime,
  ]);

  // - - - - - - - Phase 6 diff result - - - - - - -
  //
  // computeScheduleDiff is pure (tested in src/__tests__/computeScheduleDiff.test.js).
  // It needs the events between T1 (exclusive) and T2 (inclusive) so the bubble
  // stack renders only changes that landed in the diff window.
  //
  // Off-mode (single-grid time-travel or live mode): return null — the
  // Optimizer's diff-prop pipeline checks for null and renders normally.

  const timeTravelDiff = useMemo(() => {
    if (!isTimeTraveling || !isDiffPairView) return null;
    if (timeTravelSliderTimeMs == null || diffPairRightSliderTimeMs == null) {
      return null;
    }
    const t1 = timeTravelSliderTimeMs;
    const t2 = diffPairRightSliderTimeMs;
    // T2 < T1 should never happen if the auto-snap and slider clamp work, but
    // be defensive — render no diff if the invariant is violated.
    if (t2 < t1) return null;
    const eventsBetween = timeTravelSliderEvents.filter((e) => {
      const ts = new Date(e.timestamp).getTime();
      return ts > t1 && ts <= t2;
    });
    return computeScheduleDiff({
      leftProjects: replayedProjectsArray,
      rightProjects: replayedProjectsArrayRight,
      leftAttributes: replayedAttributesArray,
      rightAttributes: replayedAttributesArrayRight,
      eventsBetween,
    });
  }, [
    isTimeTraveling,
    isDiffPairView,
    timeTravelSliderTimeMs,
    diffPairRightSliderTimeMs,
    timeTravelSliderEvents,
    replayedProjectsArray,
    replayedProjectsArrayRight,
    replayedAttributesArray,
    replayedAttributesArrayRight,
  ]);

  //
  //
  // - - - - - - - - - - - - - - - - -  MUTATION HOOKS  - - - - - - - - - - - - - - - - - -
  //
  //

  // - - - - - - - - - - - - - - - - -  USEMUTATION createLogEntry  - - - - - - - - - - - - - - - - - -

  const createLogEntry = useMutation({
    mutationFn: async (newLogEntry) => {
      const dynamicBody = {
        method: "createLogEntry",
        dataSource: MONGODB_DATASOURCE_TS,
        database: mongoDatabase,
        collection: MONGODB_COLLECTION_LOGS,
        document: newLogEntry,
      };

      // Calculate payload size for monitoring (silent)
      const payloadString = JSON.stringify(dynamicBody);
      const payloadSizeBytes = new Blob([payloadString]).size;
      const payloadSizeMB = (payloadSizeBytes / 1024 / 1024).toFixed(2);

      // // Log payload size (commented out for production)
      // const payloadSizeKB = (payloadSizeBytes / 1024).toFixed(2);
      // console.log(
      //   "%cLog Entry Payload Size:",
      //   "color: #FFA500; font-weight: bold; background-color: #1a1a1a; padding: 2px 4px;",
      //   `${payloadSizeBytes} bytes (${payloadSizeKB} KB / ${payloadSizeMB} MB)`
      // );

      // Warn if payload is getting large (approaching Lambda's 6MB payload limit)
      if (payloadSizeBytes > 4 * 1024 * 1024) {
        // 4MB warning threshold (Lambda limit is 6MB)
        console.warn(
          "%c⚠️ WARNING: Large log entry payload!",
          "color: #FF0000; font-weight: bold; font-size: 14px; background-color: #FFFF00; padding: 4px 8px;",
          `Size: ${payloadSizeMB} MB - Approaching Lambda payload limit (6MB)`
        );
      }

      const response = await fetch(MONGODB_AWSGW_MUTATIONS, {
        method: "POST",
        mode: "cors",
        credentials: "omit",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "x-api-key": MONGODB_AWSGW_APIKEY,
        },
        body: payloadString,
      });

      if (!response.ok) {
        const errorMsg = `ModelTsContext (${mongoDatabase}) createLogEntry ERROR: ${response.status} ${response.statusText}`;

        // Special handling for 413 Payload Too Large
        if (response.status === 413) {
          console.error(
            "%c🚫 PAYLOAD TOO LARGE (413):",
            "color: #FF0000; font-weight: bold; font-size: 16px; background-color: #FFE5E5; padding: 6px 12px;",
            `\nPayload size: ${payloadSizeMB} MB (${payloadSizeBytes} bytes)` +
              `\nLambda payload limit: 6 MB` +
              `\nLog entry document:`,
            newLogEntry
          );
        }

        throw new Error(errorMsg);
      }

      const result = await response.json();

      console.log(
        "%cModelTsContext (" + mongoDatabase + ") createLogEntry result: ",
        "color: #00ff00; font-weight: bold; background-color: #1a1a1a; padding: 2px 4px;",
        result
      );

      return result;
    },
    onSuccess: (data) => {
      console.log(
        "%cLog entry created successfully:",
        "color: #0032f8ff; font-weight: bold;",
        data
      );
    },
    onError: (error) => {
      console.log(
        "%cLog entry creation ERROR:",
        "color: #ff0000; font-weight: bold;",
        error
      );
    },
  });

  // - - - - - - - - - - - - - - - - -  USEMUTATION setUserById  - - - - - - - - - - - - - - - - - -
  // Unified mutation for updating user documents in the Users collection
  // Used by:
  //   1. Auto-save user prefs when current user changes tabs, filters, location, etc.
  //   2. Admin UserDetailModal when editing another user's settings
  //
  // Parameters: { filter, update }
  //   - filter: MongoDB filter object (e.g., { id: "user-id" } or { _id: "mongo-object-id" })
  //   - update: MongoDB update object (e.g., { $set: { ... } })

  const setUserById = useMutation({
    mutationFn: async ({ filter, update }) => {
      const dynamicBody = {
        method: "setUserById",
        dataSource: MONGODB_DATASOURCE_TS,
        database: mongoDatabase,
        collection: MONGODB_COLLECTION_USERS,
        filter: filter,
        update: update,
      };

      const response = await fetch(MONGODB_AWSGW_MUTATIONS, {
        method: "POST",
        mode: "cors",
        credentials: "omit",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "x-api-key": MONGODB_AWSGW_APIKEY,
        },
        body: JSON.stringify(dynamicBody),
      });

      if (!response.ok) {
        const errorMsg = `ModelTsContext (${mongoDatabase}) setUserById ERROR: ${response.status} ${response.statusText}`;
        throw new Error(errorMsg);
      }

      const result = await response.json();

      console.log(
        "%cModelTsContext (" + mongoDatabase + ") setUserById(",
        "color: #00BFFF; font-weight: bold;",
        { filter, update },
        "): ",
        result
      );

      return result;
    },
    onSuccess: (data) => {
      // Check if the update actually matched a document
      const result = data?.message?.result;
      if (result?.matchedCount === 0) {
        console.log(
          "%c⚠️ WARNING: No document matched filter - update did NOT occur!",
          "color: #ff8800; font-weight: bold; background-color: #1a1a1a; padding: 2px 4px;",
          data
        );
      } else {
        console.log(
          "%cUser updated successfully:",
          "color: #00ff00; font-weight: bold;",
          data
        );
      }
    },
    onError: (error) => {
      console.log(
        "%cUser update ERROR:",
        "color: #ff0000; font-weight: bold;",
        error
      );
      alert("Update User record error!");
    },
    // Retry logic: try up to 3 times with 1 second delay between attempts
    retry: 3,
    retryDelay: 1000,
  });

  // - - - - - - - - - - - - - - - - -  USEMUTATION createNewUser  - - - - - - - - - - - - - - - - - -
  // Create new user document when user logs in via MSAL but doesn't exist in MongoDB
  // Uses defaultNewUserRecord from AppContext with id/username from JWT
  //
  // Parameters: { document }
  //   - document: Full user document to insert (includes id, username, defaults)

  const createNewUser = useMutation({
    mutationFn: async ({ document }) => {
      const dynamicBody = {
        method: "createNewUser",
        dataSource: MONGODB_DATASOURCE_TS,
        database: mongoDatabase,
        collection: MONGODB_COLLECTION_USERS,
        document: document,
      };

      const response = await fetch(MONGODB_AWSGW_MUTATIONS, {
        method: "POST",
        mode: "cors",
        credentials: "omit",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "x-api-key": MONGODB_AWSGW_APIKEY,
        },
        body: JSON.stringify(dynamicBody),
      });

      if (!response.ok) {
        const errorMsg = `ModelTsContext (${mongoDatabase}) createNewUser ERROR: ${response.status} ${response.statusText}`;
        throw new Error(errorMsg);
      }

      const result = await response.json();

      console.log(
        "%cModelTsContext (" + mongoDatabase + ") createNewUser(",
        "color: #00BFFF; font-weight: bold;",
        { document },
        "): ",
        result
      );

      return result;
    },
    onSuccess: (data) => {
      const result = data?.message?.result;
      if (result?.acknowledged) {
        console.log(
          "%c✅ New user created successfully:",
          "color: #00ff00; font-weight: bold;",
          data
        );
        // Re-fetch user data to get the full document with _id
        setShouldFetchUser(true);
      } else {
        console.log(
          "%c⚠️ WARNING: User creation may have failed:",
          "color: #ff8800; font-weight: bold;",
          data
        );
      }
    },
    onError: (error) => {
      console.log(
        "%c❌ Create new user ERROR:",
        "color: #ff0000; font-weight: bold;",
        error
      );
      alert("New user record creation error! Please contact support.");
    },
    retry: 3,
    retryDelay: 1000,
  });

  // - - - - - - - - - - - - - - - - -  USEMUTATION setScheduleById  - - - - - - - - - - - - - - - - - -
  // Update project schedule (drag/drop, job detail changes)
  // Used for: assigning installer, setting scheduled date, arrival time, etc.
  //
  // Parameters: { id, updateFields }
  //   - id: MongoDB ObjectId string for the project
  //   - updateFields: Object with fields to update (installer, date_scheduled, etc.)

  const setScheduleById = useMutation({
    mutationFn: async ({ id, updateFields }) => {
      // Time Machine #114 Phase 4 — block all mutating writes while time-traveling.
      // Defense-in-depth: the UI hides drag handles + sets modals read-only, but a
      // structural guard at the mutation layer makes the invariant impossible to
      // violate (matches the three-layer-defense pattern from #99/#90).
      if (isTimeTraveling) {
        const err = new Error(
          "ModelTsContext setScheduleById: blocked while time-traveling (#114)"
        );
        console.warn("%c" + err.message, "color: #DAA520; font-weight: bold;");
        throw err;
      }
      const dynamicBody = {
        method: "setScheduleById",
        dataSource: MONGODB_DATASOURCE_TS,
        database: mongoDatabase,
        collection: MONGODB_COLLECTION_ACTIVEQUEUE,
        id: id,
        updateFields: updateFields,
        // EventLog source-attribution (#111 / #112) — populates the
        // Lambda's `event.changedBy` first-fallback. Without this, the
        // Lambda walks down to the legacy history-field extraction
        // which usually works for setScheduleById, but explicit is
        // better than implicit and matches the other handlers' wiring.
        changedBy: userEmail,
      };

      const response = await fetch(MONGODB_AWSGW_MUTATIONS, {
        method: "POST",
        mode: "cors",
        credentials: "omit",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "x-api-key": MONGODB_AWSGW_APIKEY,
        },
        body: JSON.stringify(dynamicBody),
      });

      if (!response.ok) {
        const errorMsg = `ModelTsContext (${mongoDatabase}) setScheduleById ERROR: ${response.status} ${response.statusText}`;
        throw new Error(errorMsg);
      }

      const result = await response.json();

      console.log(
        "%cModelTsContext (" + mongoDatabase + ") setScheduleById(",
        "color: #00BFFF; font-weight: bold;",
        { id, updateFields },
        "): ",
        result
      );

      return result;
    },
    onSuccess: (data) => {
      const result = data?.message?.result;
      if (result?.matchedCount === 0) {
        console.log(
          "%c⚠️ WARNING: No project matched id - schedule update did NOT occur!",
          "color: #ff8800; font-weight: bold;",
          data
        );
      } else {
        console.log(
          "%cSchedule updated successfully:",
          "color: #00ff00; font-weight: bold;",
          data
        );
        // Invalidate project queries to refresh UI for tanstackOnly users
        // This triggers a refetch which updates projectListAfterMerge
        queryClient.invalidateQueries({
          predicate: (query) =>
            query.queryKey[0] === "getProjectsByCompany" ||
            query.queryKey[0] === "getProjectsByCompanyWithShared" ||
            query.queryKey[0] === "getProjectsByPayrollNoList"
        });
        console.log(
          "%c🔄 TanStack queries invalidated - refetching project data...",
          "color: #00BFFF; font-weight: bold;"
        );
      }
    },
    onError: (error) => {
      console.log(
        "%cSchedule update ERROR:",
        "color: #ff0000; font-weight: bold;",
        error
      );
      // Suppress alert when the throw is the layer-3 time-travel guard.
      // The UI already prevents the user from saving in read-only mode;
      // any path that still reaches the mutation is a defensive catch.
      if (isTimeTravelGuardError(error)) return;
      alert("Update schedule record error!");
    },
  });

  // - - - - - - - - - - - - - - - - -  USEMUTATION setSuppressionById  - - - - - - - - - - - - - - - - - -
  // Update project suppression status
  // Used for: suppress button in UI
  //
  // Parameters: { id, updateFields }
  //   - id: MongoDB ObjectId string for the project
  //   - updateFields: Object with suppression fields (statusCode, date_suppressed, FileMakerSuppressNotes)

  const setSuppressionById = useMutation({
    mutationFn: async ({ id, updateFields }) => {
      if (isTimeTraveling) {
        const err = new Error(
          "ModelTsContext setSuppressionById: blocked while time-traveling (#114)"
        );
        console.warn("%c" + err.message, "color: #DAA520; font-weight: bold;");
        throw err;
      }
      const dynamicBody = {
        method: "setSuppressionById",
        dataSource: MONGODB_DATASOURCE_TS,
        database: mongoDatabase,
        collection: MONGODB_COLLECTION_ACTIVEQUEUE,
        id: id,
        updateFields: updateFields,
        // EventLog source-attribution (#111 / #112).
        changedBy: userEmail,
      };

      const response = await fetch(MONGODB_AWSGW_MUTATIONS, {
        method: "POST",
        mode: "cors",
        credentials: "omit",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "x-api-key": MONGODB_AWSGW_APIKEY,
        },
        body: JSON.stringify(dynamicBody),
      });

      if (!response.ok) {
        const errorMsg = `ModelTsContext (${mongoDatabase}) setSuppressionById ERROR: ${response.status} ${response.statusText}`;
        throw new Error(errorMsg);
      }

      const result = await response.json();

      console.log(
        "%cModelTsContext (" + mongoDatabase + ") setSuppressionById(",
        "color: #00BFFF; font-weight: bold;",
        { id, updateFields },
        "): ",
        result
      );

      return result;
    },
    onSuccess: (data) => {
      const result = data?.message?.result;
      if (result?.matchedCount === 0) {
        console.log(
          "%c⚠️ WARNING: No project matched id - suppression update did NOT occur!",
          "color: #ff8800; font-weight: bold;",
          data
        );
      } else {
        console.log(
          "%cSuppression updated successfully:",
          "color: #00ff00; font-weight: bold;",
          data
        );
        // Invalidate project queries to refresh UI for tanstackOnly users
        queryClient.invalidateQueries({
          predicate: (query) =>
            query.queryKey[0] === "getProjectsByCompany" ||
            query.queryKey[0] === "getProjectsByCompanyWithShared" ||
            query.queryKey[0] === "getProjectsByPayrollNoList"
        });
        console.log(
          "%c🔄 TanStack queries invalidated - refetching project data...",
          "color: #00BFFF; font-weight: bold;"
        );
      }
    },
    onError: (error) => {
      console.log(
        "%cSuppression update ERROR:",
        "color: #ff0000; font-weight: bold;",
        error
      );
      if (isTimeTravelGuardError(error)) return;
      alert("Update suppression record error!");
    },
  });

  // - - - - - - - - - - - - - - - - -  USEMUTATION setAttributeById  - - - - - - - - - - - - - - - - - -
  // Update attribute document
  // Used for: changing installer attributes, defaults, etc.
  //
  // Parameters: { id, updateFields }
  //   - id: MongoDB ObjectId string for the attribute
  //   - updateFields: Object with attribute fields to update

  const setAttributeById = useMutation({
    mutationFn: async ({ id, updateFields }) => {
      if (isTimeTraveling) {
        const err = new Error(
          "ModelTsContext setAttributeById: blocked while time-traveling (#114)"
        );
        console.warn("%c" + err.message, "color: #DAA520; font-weight: bold;");
        throw err;
      }
      const dynamicBody = {
        method: "setAttributeById",
        dataSource: MONGODB_DATASOURCE_TS,
        database: mongoDatabase,
        collection: MONGODB_COLLECTION_ATTRIBUTES,
        id: id,
        updateFields: updateFields,
        // EventLog source-attribution (#111 / #112).
        changedBy: userEmail,
      };

      const response = await fetch(MONGODB_AWSGW_MUTATIONS, {
        method: "POST",
        mode: "cors",
        credentials: "omit",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "x-api-key": MONGODB_AWSGW_APIKEY,
        },
        body: JSON.stringify(dynamicBody),
      });

      if (!response.ok) {
        const errorMsg = `ModelTsContext (${mongoDatabase}) setAttributeById ERROR: ${response.status} ${response.statusText}`;
        throw new Error(errorMsg);
      }

      const result = await response.json();

      console.log(
        "%cModelTsContext (" + mongoDatabase + ") setAttributeById(",
        "color: #00BFFF; font-weight: bold;",
        { id, updateFields },
        "): ",
        result
      );

      return result;
    },
    onSuccess: (data) => {
      const result = data?.message?.result;
      if (result?.matchedCount === 0) {
        console.log(
          "%c⚠️ WARNING: No attribute matched id - update did NOT occur!",
          "color: #ff8800; font-weight: bold;",
          data
        );
      } else {
        console.log(
          "%cAttribute updated successfully:",
          "color: #00ff00; font-weight: bold;",
          data
        );
        // Invalidate attribute queries to refresh UI for tanstackOnly users
        queryClient.invalidateQueries({
          predicate: (query) =>
            query.queryKey[0] === "getAttributesByLocation" ||
            query.queryKey[0] === "getAttributesByLocationPolling"
        });
        console.log(
          "%c🔄 TanStack attribute queries invalidated - refetching...",
          "color: #00BFFF; font-weight: bold;"
        );
      }
    },
    onError: (error) => {
      console.log(
        "%cAttribute update ERROR:",
        "color: #ff0000; font-weight: bold;",
        error
      );
      if (isTimeTravelGuardError(error)) return;
      alert("Update attribute record error!");
    },
  });

  // - - - - - - - - - - - - - - - - -  USEMUTATION createNewAttribute  - - - - - - - - - - - - - - - - - -
  // Insert new attribute document
  // Used for: creating new attributes (job attributes, extra cells, crew defaults)
  //
  // Parameters: { document }
  //   - document: The new attribute document to insert

  const createNewAttribute = useMutation({
    mutationFn: async ({ document }) => {
      if (isTimeTraveling) {
        const err = new Error(
          "ModelTsContext createNewAttribute: blocked while time-traveling (#114)"
        );
        console.warn("%c" + err.message, "color: #DAA520; font-weight: bold;");
        throw err;
      }
      const dynamicBody = {
        method: "createNewAttribute",
        dataSource: MONGODB_DATASOURCE_TS,
        database: mongoDatabase,
        collection: MONGODB_COLLECTION_ATTRIBUTES,
        document: document,
        // EventLog source-attribution (#111 / #112) — load-bearing here
        // because the new Attributes doc has no `username` field and no
        // history field, so without this the Lambda's fallback chain
        // bottoms out at "unknown" for every createNewAttribute call.
        changedBy: userEmail,
      };

      const response = await fetch(MONGODB_AWSGW_MUTATIONS, {
        method: "POST",
        mode: "cors",
        credentials: "omit",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "x-api-key": MONGODB_AWSGW_APIKEY,
        },
        body: JSON.stringify(dynamicBody),
      });

      if (!response.ok) {
        const errorMsg = `ModelTsContext (${mongoDatabase}) createNewAttribute ERROR: ${response.status} ${response.statusText}`;
        throw new Error(errorMsg);
      }

      const result = await response.json();

      console.log(
        "%cModelTsContext (" + mongoDatabase + ") createNewAttribute(",
        "color: #00BFFF; font-weight: bold;",
        { document },
        "): ",
        result
      );

      return result;
    },
    onSuccess: (data) => {
      const result = data?.message?.result;
      if (result?.insertedId) {
        console.log(
          "%cAttribute created successfully, insertedId:",
          "color: #00ff00; font-weight: bold;",
          result.insertedId
        );
        // Invalidate attribute queries to refresh UI for tanstackOnly users
        queryClient.invalidateQueries({
          predicate: (query) =>
            query.queryKey[0] === "getAttributesByLocation" ||
            query.queryKey[0] === "getAttributesByLocationPolling"
        });
        console.log(
          "%c🔄 TanStack attribute queries invalidated - refetching...",
          "color: #00BFFF; font-weight: bold;"
        );
      } else {
        console.log(
          "%c⚠️ WARNING: Attribute creation may have failed",
          "color: #ff8800; font-weight: bold;",
          data
        );
      }
    },
    onError: (error) => {
      console.log(
        "%cAttribute creation ERROR:",
        "color: #ff0000; font-weight: bold;",
        error
      );
      if (isTimeTravelGuardError(error)) return;
      alert("New attribute record error!");
    },
  });

  //
  //
  // - - - - - - - - - - - - - - - - -  ATTRIBUTE WRAPPER FUNCTIONS  - - - - - - - - - - - - - - - - - -
  //
  // These functions are TanStack versions of the attribute wrapper functions from modelRW.context.js
  // They are used by tanstackOnly users to create/update attributes without going through Realm
  //
  // NOTE: myJsonArraySort already exists earlier in this file (around line 519) and is exported
  //

  /**
   * Creates a new job attribute (TanStack version).
   * @param {Object} theJob - The job object.
   * @returns {Object} - The new attribute.
   */
  const tsCreateJobAttribute = (theJob) => {
    var newAttribute = { ...DOCUMENT_ATTRIBUTES_TEMPLATE };
    newAttribute.companyCode = theJob.companyCode;
    newAttribute.location = theJob.location;
    newAttribute.date = theJob.start;
    newAttribute.id = createUuid();
    newAttribute.invoiceNo = theJob.invoiceNo;

    console.log(
      "%cModelTsContext (" + mongoDatabase + ") tsCreateJobAttribute, newAttribute: ",
      "color: #00BFFF; font-weight: bold;",
      newAttribute
    );

    // Send to MongoDB via TanStack mutation
    createNewAttribute.mutate({ document: newAttribute });

    return newAttribute;
  };

  // - - - - - - - - - -

  /**
   * Creates a new extra cell attribute (TanStack version).
   * @param {string} date - The date for the attribute.
   * @param {string} surface - The surface type.
   * @param {string} customText - The custom text.
   * @returns {Object} - The new attribute.
   */
  const tsCreateExtraCellAttribute = (date, surface, customText) => {
    console.log(
      `%cModelTsContext (${mongoDatabase}) tsCreateExtraCellAttribute date: `,
      "color: #00BFFF; font-weight: bold;",
      date,
      ", surface: ",
      surface,
      ", customText: ",
      customText
    );
    var newAttribute = { ...DOCUMENT_ATTRIBUTES_TEMPLATE };
    newAttribute.companyCode = companyCode;
    newAttribute.location = locationFilter;
    newAttribute.id = createUuid();
    newAttribute.date = date;
    newAttribute.extra = { surface: surface, customText: customText };

    // Send to MongoDB via TanStack mutation
    createNewAttribute.mutate({ document: newAttribute });

    return newAttribute;
  };

  // - - - - - - - - - -

  /**
   * Removes an extra cell attribute by ID (TanStack version).
   * Soft-deletes by setting deleted=true and clearing fields.
   * @param {string} id - The ID of the attribute to remove.
   */
  const tsRemoveExtraCellAttribute = (id) => {
    // Find the extra cell in awsgwAttributesResult (TanStack's attributes data)
    const currentExtraCellAttribute = awsgwAttributesResult.find(
      (document) => document.id === id
    );

    if (!currentExtraCellAttribute) {
      console.log(
        "%cModelTsContext tsRemoveExtraCellAttribute: attribute not found for id:",
        "color: #ff8800; font-weight: bold;",
        id
      );
      return;
    }

    const currentMongoId = currentExtraCellAttribute._id?.toString();
    const updateFields = {
      date: "",
      deleted: true,
      _kf_payrollNo: "",
      extra: false,
      invoiceNo: "",
      availability: {},
    };

    console.log(
      "%cModelTsContext tsRemoveExtraCellAttribute currentMongoId:",
      "color: #00BFFF; font-weight: bold;",
      currentMongoId,
      ", updateFields:",
      updateFields
    );

    // Send to MongoDB via TanStack mutation
    setAttributeById.mutate({ id: currentMongoId, updateFields });
  };

  // - - - - - - - - - -

  /**
   * Creates or updates crew defaults attribute (TanStack version).
   * @param {string} payrollNo - The payroll number.
   * @param {Array} dayAttributesDefault - The default day attributes.
   * @param {Array} customValuesDefault - The default custom values.
   * @returns {Object} - The new/updated attribute.
   */
  const tsCreateCrewDefaultsAttribute = (
    payrollNo,
    dayAttributesDefault,
    customValuesDefault
  ) => {
    // Convert CrewDetailModal state values to Attribute Document values
    var newArray = [];
    const days = ["Mon", "Tue", "Wed", "Thr", "Fri", "Sat", "Sun"];
    customValuesDefault.forEach((element, index) => {
      if (dayAttributesDefault[index]) {
        if (dayAttributesDefault[index].status === "Custom") {
          // Wrap custom values in flame emojis for visibility in UI
          // (matches tsUpdateAttributeList behavior at line ~3956)
          newArray.push({ [days[index]]: "🔥" + element + "🔥" });
        } else {
          newArray.push({
            [days[index]]: dayAttributesDefault[index].status,
          });
        }
      }
    });

    // Look for existing defaultAttribute (like Realm did at line 2323)
    // Use awsgwAttributesResult as source of truth - attributesList in modelTS.context is a separate
    // state that only gets updated by mutations, NOT populated from query results like Realm's attributesList was
    // In Realm, attributesList was set from query results (line 3753: setAttributesList(newList))
    // In TanStack, query results go to awsgwAttributesResult, so we must search there
    // CRITICAL: Use findLast because CrewDetailModal.js line 475 uses newAvailability[newAvailability.length - 1]
    // If there are duplicate defaults for an installer, the UI shows the LAST one, so we must update the LAST one
    // getAttributesSource() checks both that data exists AND matches current location
    const attributesSource = getAttributesSource();
    const existingDefaultAttribute = attributesSource.findLast(
      (document) =>
        document.companyCode === companyCode &&
        document.location === locationFilter &&
        document.date === "" &&
        document._kf_payrollNo === dataFormatting_payrollFields(payrollNo)
    );

    console.log(
      `%cModelTsContext (${mongoDatabase}) tsCreateCrewDefaultsAttribute, existingDefaultAttribute: `,
      "color: #00BFFF; font-weight: bold;",
      existingDefaultAttribute
    );

    if (existingDefaultAttribute) {
      // Found existing Attribute Document - update it
      const currentMongoId = existingDefaultAttribute._id?.toString();
      const updateFields = { availability: newArray };

      console.log(
        "%cModelTsContext tsCreateCrewDefaultsAttribute (UPDATE), _id:",
        "color: #00BFFF; font-weight: bold;",
        currentMongoId,
        ", updateFields:",
        updateFields
      );

      // Find the index in attributesSource - use findLastIndex to match CrewDetailModal behavior
      // CrewDetailModal takes the LAST matching attribute (line 475: newAvailability[newAvailability.length - 1])
      // Must match the same criteria as the findLast above (date === "" for defaults)
      const originalIndex = attributesSource.findLastIndex(
        (document) =>
          document.companyCode === companyCode &&
          document.location === locationFilter &&
          document.date === "" &&
          document._kf_payrollNo === dataFormatting_payrollFields(payrollNo)
      );

      // Update awsgwAttributesResult for optimistic UI (Realm did setAttributesList at line 2354-2389)
      // In TanStack, awsgwAttributesResult is what the UI reads from (via schedule.context alias)
      // Polling is paused when CrewDetailModal is open, so no race condition
      if (originalIndex !== -1) {
        // Create updated attribute with new availability
        const updatedAttribute = { ...existingDefaultAttribute, availability: newArray };
        // Build new list replacing the old attribute at the found index
        const newAttributesList = attributesSource.map((doc, index) =>
          index === originalIndex ? updatedAttribute : doc
        );
        // Update the actual source that UI reads from
        if (awsgwAttributesResult.length > 0) {
          setAwsgwAttributesResult(newAttributesList);
        } else {
          setAttributesList(newAttributesList);
        }
      }

      setAttributeById.mutate({ id: currentMongoId, updateFields });
      return { ...existingDefaultAttribute, availability: newArray };
    } else {
      // Not found - create a new one
      var newAttribute = { ...DOCUMENT_ATTRIBUTES_TEMPLATE };
      newAttribute.companyCode = companyCode;
      newAttribute.location = locationFilter;
      newAttribute.id = createUuid();
      newAttribute._kf_payrollNo = dataFormatting_payrollFields(payrollNo);
      newAttribute.date = "";
      newAttribute.availability = newArray;

      console.log(
        "%cModelTsContext tsCreateCrewDefaultsAttribute (CREATE), newAttribute:",
        "color: #00BFFF; font-weight: bold;",
        newAttribute
      );

      // Update awsgwAttributesResult for optimistic UI (Realm did setAttributesList at line 2389)
      // In TanStack, awsgwAttributesResult is what the UI reads from (via schedule.context alias)
      // Polling is paused when CrewDetailModal is open, so no race condition
      if (awsgwAttributesResult.length > 0) {
        setAwsgwAttributesResult([...awsgwAttributesResult, newAttribute]);
      } else {
        setAttributesList([...attributesList, newAttribute]);
      }

      createNewAttribute.mutate({ document: newAttribute });
      return newAttribute;
    }
  };

  // - - - - - - - - - -

  /**
   * Updates attribute list for a specific date/reference (TanStack version).
   * Used for crew availability updates in CrewDetailModal.
   * @param {string} date - The date (formatted as locale string).
   * @param {string} reference - The payroll number reference.
   * @param {string} type - The status value: "Available", "OFF", "BUFFER", or "Custom".
   * @param {string} value - The custom text (only used when type === "Custom").
   */
  const tsUpdateAttributeList = (date, reference, type, value) => {
    // type is the status value: "Available", "OFF", "BUFFER", "Custom"
    // value is only used when type === "Custom" (contains the custom text)

    // Find attribute in TanStack's data
    // Filter for matching date & installer, exclude defaults (availability.length === 0)
    // BUG FIX 2026-01-21: Convert document.date to locale string for comparison
    // (matches Realm's approach - dates may be stored in different formats)
    const existingAttribute = awsgwAttributesResult.find((document) => {
      const docDate = new Date(Date.parse(document.date)).toLocaleDateString(
        "en-us"
      );
      const availabilityLength = document.availability
        ? document.availability?.length
        : 0;
      return (
        document.companyCode === companyCode &&
        document.location === locationFilter &&
        docDate === date &&
        document._kf_payrollNo === dataFormatting_payrollFields(reference) &&
        availabilityLength === 0
      );
    });

    // Determine the encoded title based on type (matches Realm's switch logic)
    let newJobEncoded = "";
    switch (type) {
      case "Available":
        newJobEncoded = "";
        break;
      case "OFF":
        newJobEncoded = "OFF";
        break;
      case "BUFFER":
        newJobEncoded = "BUFFER";
        break;
      case "Custom":
        newJobEncoded = "🔥" + value + "🔥";
        break;
      default:
        console.log(
          "ModelTsContext tsUpdateAttributeList, switch(type) UNHANDLED value:",
          type
        );
        break;
    }

    if (existingAttribute) {
      const currentMongoId = existingAttribute._id?.toString();

      if (type === "Available") {
        // For "Available", clear the payrollNo to effectively remove the attribute
        // (matches Realm's behavior of orphaning the document)
        const updateFields = {
          _kf_payrollNo: "",
          encodedSmallTitle: "",
          encodedLargeTitle: "",
          availability: [],
        };

        console.log(
          "%cModelTsContext tsUpdateAttributeList (REMOVE/Available), _id:",
          "color: #00BFFF; font-weight: bold;",
          currentMongoId,
          ", updateFields:",
          updateFields
        );

        // (#132 — optimistic UI) Apply the field change locally before the
        // mutation round-trips. Without this, the modal (and the underlying
        // schedule grid) doesn't see the change until polling resumes after
        // modal close — and during that window, repeated user clicks all see
        // the same stale state and produce duplicate mutations.
        // tsCreateCrewDefaultsAttribute (line ~4509) already does this; this
        // call site was missed in that pattern's original rollout.
        setAwsgwAttributesResult((prev) =>
          applyOptimisticAttributeFieldsByMongoId(
            prev,
            currentMongoId,
            updateFields
          )
        );

        // Skip the server mutation if currentMongoId is missing (means the
        // existingAttribute was itself an optimistic record from a CREATE
        // earlier in this modal session that hasn't round-tripped yet). The
        // pending CREATE's onSuccess will backfill the _id; until then, the
        // optimistic state above keeps the UI responsive. Real-world
        // implication: rapid status-toggling on a brand-new day may lose
        // intermediate toggles, but no duplicate records are created.
        if (currentMongoId) {
          setAttributeById.mutate({ id: currentMongoId, updateFields });
        }
      } else {
        // Update existing attribute with new encoded title
        const updateFields = {
          encodedSmallTitle: newJobEncoded,
          encodedLargeTitle: newJobEncoded,
          availability: [],
        };

        console.log(
          "%cModelTsContext tsUpdateAttributeList (UPDATE), _id:",
          "color: #00BFFF; font-weight: bold;",
          currentMongoId,
          ", updateFields:",
          updateFields
        );

        // (#132 — optimistic UI) Same reasoning as the REMOVE branch above.
        setAwsgwAttributesResult((prev) =>
          applyOptimisticAttributeFieldsByMongoId(
            prev,
            currentMongoId,
            updateFields
          )
        );

        if (currentMongoId) {
          setAttributeById.mutate({ id: currentMongoId, updateFields });
        }
      }
    } else {
      // Create new attribute for this date/installer (only if not "Available")
      if (type !== "Available") {
        var newAttribute = { ...DOCUMENT_ATTRIBUTES_TEMPLATE };
        newAttribute.companyCode = companyCode;
        newAttribute.location = locationFilter;
        newAttribute.id = createUuid();
        newAttribute._kf_payrollNo = dataFormatting_payrollFields(reference);
        newAttribute.date = date;
        newAttribute.encodedSmallTitle = newJobEncoded;
        newAttribute.encodedLargeTitle = newJobEncoded;
        newAttribute.availability = [];

        console.log(
          "%cModelTsContext tsUpdateAttributeList (CREATE), newAttribute:",
          "color: #00BFFF; font-weight: bold;",
          newAttribute
        );

        // (#132 — optimistic UI) Append the new attribute to
        // awsgwAttributesResult BEFORE firing the mutation. Without this, a
        // second rapid click on the same day re-enters the existingAttribute
        // === undefined branch and creates a duplicate. tsCreateCrewDefaults-
        // Attribute (line ~4510) already does this; this call site was missed
        // in that pattern's original rollout.
        //
        // The optimistic record has no `_id` yet. The per-call onSuccess
        // below backfills the real `_id` when the Lambda responds, so any
        // subsequent UPDATE/REMOVE in the same modal session can find the
        // record by its real `_id`.
        const optimisticHandle = newAttribute.id;
        setAwsgwAttributesResult((prev) =>
          applyOptimisticAttributeCreate(prev, newAttribute)
        );

        createNewAttribute.mutate(
          { document: newAttribute },
          {
            onSuccess: (data) => {
              const insertedId = data?.message?.result?.insertedId;
              if (insertedId) {
                setAwsgwAttributesResult((prev) =>
                  backfillOptimisticAttributeMongoId(
                    prev,
                    optimisticHandle,
                    insertedId
                  )
                );
              }
            },
          }
        );
      }
    }
  };

  // - - - - - - - - - -

  /**
   * #174 — Lock or unlock a single installer-day cell. Mirrors
   * `tsUpdateAttributeList`'s find-or-create pattern, writing the `locked`
   * boolean + `lockedBy` ("Last, F") onto the cell's dated Attributes doc:
   *   • lock + existing doc  → setAttributeById update
   *   • lock + no doc        → createNewAttribute (bare dated attr, locked:true)
   *   • unlock + existing doc → setAttributeById update (locked:false, lockedBy "")
   *   • unlock + no doc       → no-op (already unlocked)
   * The write flows through the same Attributes mutation path as every other
   * edit, so the EventLog audit trail captures who/when for Time Machine.
   *
   * @param {string} date - cell date (locale string "M/D/YYYY")
   * @param {string} reference - installer payrollNo
   * @param {boolean} locked - true to lock, false to unlock
   * The actor ("Last, F") is derived from the logged-in MSAL profile and
   * stamped on lock / cleared on unlock; the EventLog records who/when either way.
   */
  const tsToggleCellLock = (date, reference, locked) => {
    // #174 — actor for the lock. The MSAL access-token flow doesn't reliably
    // surface a name; the RELIABLE identifier is the email, which the rest of
    // the app reads from `tsUserDocument.username` (used in every history
    // string / EventLog write). #185 (Brandon, 2026-06-04): the attribution
    // must be the **email username** like Time Machine shows — CONSISTENTLY
    // (Leland's decision). We deliberately pass ONLY the email (no name /
    // given / family), so formatLockedByFromProfile yields the email local-part
    // (e.g. "brandonlowell"). Sourcing the name from the MSAL account was both
    // inconsistent ("Last, F" only when account.name happened to be present)
    // and flaky (absent → empty "another user"); the email is always available.
    const lockProfile = {
      email:
        tsUserDocument?.username ||
        mongoUser?._profile?.data?.email ||
        "",
    };
    const lockedBy = locked ? formatLockedByFromProfile(lockProfile) : "";
    const existingAttribute = awsgwAttributesResult.find((document) => {
      const docDate = new Date(Date.parse(document.date)).toLocaleDateString(
        "en-us"
      );
      const availabilityLength = document.availability
        ? document.availability?.length
        : 0;
      return (
        document.companyCode === companyCode &&
        document.location === locationFilter &&
        docDate === date &&
        document._kf_payrollNo === dataFormatting_payrollFields(reference) &&
        availabilityLength === 0
      );
    });

    // #186 — never LOCK an OFF day (data-layer guard; OptimizerGrid also
    // disables the menu option). Unlock is allowed through (clears a stray lock).
    if (locked && existingAttribute?.encodedSmallTitle === "OFF") {
      console.log(
        "%cModelTsContext tsToggleCellLock blocked — OFF day cannot be locked (#186)",
        "color: #f97316; font-weight: bold;",
        { date, reference }
      );
      return;
    }

    if (existingAttribute) {
      const currentMongoId = existingAttribute._id?.toString();
      const updateFields = { locked, lockedBy };

      console.log(
        "%cModelTsContext tsToggleCellLock (UPDATE), _id:",
        "color: #00BFFF; font-weight: bold;",
        currentMongoId,
        ", updateFields:",
        updateFields
      );

      // (#132 — optimistic UI) Apply locally before the mutation round-trips so
      // the grid recolors / re-shows the 🔍 immediately.
      setAwsgwAttributesResult((prev) =>
        applyOptimisticAttributeFieldsByMongoId(prev, currentMongoId, updateFields)
      );

      if (currentMongoId) {
        setAttributeById.mutate({ id: currentMongoId, updateFields });
      }
      return;
    }

    // No dated attribute for this cell yet.
    if (!locked) return; // unlocking a cell with no attribute → already unlocked

    var newAttribute = { ...DOCUMENT_ATTRIBUTES_TEMPLATE };
    newAttribute.companyCode = companyCode;
    newAttribute.location = locationFilter;
    newAttribute.id = createUuid();
    newAttribute._kf_payrollNo = dataFormatting_payrollFields(reference);
    newAttribute.date = date;
    newAttribute.encodedSmallTitle = "";
    newAttribute.encodedLargeTitle = "";
    newAttribute.availability = [];
    newAttribute.locked = true;
    newAttribute.lockedBy = lockedBy;

    console.log(
      "%cModelTsContext tsToggleCellLock (CREATE), newAttribute:",
      "color: #00BFFF; font-weight: bold;",
      newAttribute
    );

    const optimisticHandle = newAttribute.id;
    setAwsgwAttributesResult((prev) =>
      applyOptimisticAttributeCreate(prev, newAttribute)
    );

    createNewAttribute.mutate(
      { document: newAttribute },
      {
        onSuccess: (data) => {
          const insertedId = data?.message?.result?.insertedId;
          if (insertedId) {
            setAwsgwAttributesResult((prev) =>
              backfillOptimisticAttributeMongoId(
                prev,
                optimisticHandle,
                insertedId
              )
            );
          }
        },
      }
    );
  };

  // - - - - - - - - - -

  /**
   * Updates unassigned attribute list (TanStack version).
   * Stub function - matches the empty implementation in modelRW.context.js.
   * @param {string} date - The date.
   * @param {string} value - The value.
   */
  const tsUpdateUnassignedAttributeList = (date, value) => {
    // This is currently an empty stub in modelRW.context.js
    // Keeping it as a stub for API compatibility
    console.log(
      "%cModelTsContext tsUpdateUnassignedAttributeList (STUB), date:",
      "color: #00BFFF; font-weight: bold;",
      date,
      ", value:",
      value
    );
  };

  //
  //
  // - - - - - - - - - - - - - - - - -  LAUNCH FUNCTIONS  - - - - - - - - - - - - - - - - - -
  //
  //

  // NOTE: these are only needed to only trigger at certain times
  //       without them, and with using:
  //               refetchInterval: shouldFetchUniqueLocations ? 5000 : false,
  //       they just auto-initialize and run every interval

  const awsgwGetUniqueLocations = () => {
    // originally launched in schedule.context -> onScheduleNeedsModel()
    setShouldFetchUniqueLocations(true);
  };

  // - - - - - - - - - -

  const awsgwGetOfficeMetaDataByLocation = () => {
    // originally launched in schedule.context -> onScheduleNeedsModel()
    setShouldFetchOfficeMetaData(true);
  };

  // - - - - - - - - - -

  const awsgwGetUserById = () => {
    // originally launched in modelRW.context -> useEffect[mongoUser]
    // now launched in modelTS.context -> useEffect[mongoUser]
    setShouldFetchUser(true);
  };

  // - - - - - - - - - -

  const awsgwGetUsers = () => {
    // originally launched in schedule.context -> onScheduleNeedsModel()
    setShouldFetchUsers(true);
  };

  // - - - - - - - - - -

  const awsgwGetProjectById = () => {
    // never implemented
    setShouldFetchProject(true);
  };

  // - - - - - - - - - -

  const awsgwGetProjectsByCompany = () => {
    // originally launched in schedule.context -> onScheduleNeedsModel()
    setShouldFetchProjects(true);
  };

  // - - - - - - - - - -

  const awsgwGetCandidatesByLocation = () => {
    // originally launched in schedule.context -> onScheduleNeedsModel()
    console.log(
      "%cModelTsContext awsgwGetCandidatesByLocation() - setting shouldFetchCandidates=true",
      "color: #00BFFF; font-weight: bold; background-color: #1a1a1a; padding: 2px 4px;"
    );
    setShouldFetchCandidates(true);
  };

  // - - - - - - - - - -

  const awsgwGetCandidatesByPayrollNoList = () => {
    // originally launched in modelRw.context -> getCandidatesByCompany()
    setShouldFetchCandidatesAdditional(true);
  };

  // - - - - - - - - - -

  const awsgwGetProjectsByPayrollNoList = () => {
    // originally launched in modelRw.context -> getCandidatesByCompany()
    setShouldFetchProjectsAdditional(true);
  };

  // - - - - - - - - - -

  const awsgwGetAttributesByLocation = () => {
    // originally launched in schedule.context -> onScheduleNeedsModel()
    setShouldFetchAttributes(true);
  };

  // - - - - - - - - - -

  const awsgwGetLogs = () => {
    // originally launched in schedule.context -> onScheduleNeedsModel()
    setShouldFetchLogs(true);
  };

  // - - - - - - - - - -

  const awsgwGetLogsChartData = () => {
    setShouldFetchLogsChartData(true);
  };

  // - - - - - - - - - -

  const awsgwGetLogsUserActivityTime = (params = {}) => {
    setActivityTimeParams(prev => ({ ...prev, ...params }));
    setShouldFetchLogsActivityTime(true);
  };

  //
  //
  // - - - - - - - - - - USER MANAGEMENT & AUTHENTICATION
  //
  //

  /**
   * Sets up user settings based on the tsUserDocument.
   * Copied from modelRW.context.js setupUserSettings()
   */
  const setupUserSettings = () => {
    // darkMode
    const userUImode =
      tsUserDocument.defaultUImode !== ""
        ? tsUserDocument.defaultUImode
        : defaultUImode;

    // jobTitleConfig
    const userJobTitleConfig =
      tsUserDocument.jobTitleConfig !== ""
        ? tsUserDocument.jobTitleConfig
        : defaultJobTitleConfig;

    // companyCode (KI or SCG)
    const userCompanyCode =
      tsUserDocument.defaultCompany !== ""
        ? tsUserDocument.defaultCompany
        : defaultCompanyCode;

    // location (CHS, RVS, BHM, etc)
    var userLocation = "";
    if (parameterLocation) {
      userLocation = parameterLocation;
      setParameterLocation("");
    } else {
      userLocation =
        tsUserDocument.defaultLocation !== ""
          ? tsUserDocument.defaultLocation
          : defaultLocationFilter;
    }

    // startDate (week beginning with)
    // 2026-04-28 (#105): no longer clearing parameterWeek here. Removing
    // the clear lets OptimizerScreen also consume the value when it mounts
    // (which happens AFTER setupUserSettings in the typical flow). Without
    // this, multi-grid Optimizer's grid #1 would never see the URL ?week
    // parameter and would render today's week (or last localStorage value).
    // setupUserSettings only runs once per session via hasRunSetupUserSettings
    // ref, so leaving parameterWeek populated is harmless.
    var userStartDate = "";
    if (parameterWeek) {
      userStartDate = parameterWeek;
    } else {
      userStartDate =
        tsUserDocument.defaultDate !== ""
          ? tsUserDocument.defaultDate
          : defaultStartDate;
    }

    const userTab =
      tsUserDocument.defaultTab !== "" ? tsUserDocument.defaultTab : defaultTab;
    console.log(
      "%c🔍 setupUserSettings - userTab decision:",
      "color: #FF6B6B; font-weight: bold; background-color: #1a1a1a; padding: 2px 4px;",
      "tsUserDocument.defaultTab:", tsUserDocument.defaultTab,
      "| defaultTab fallback:", defaultTab,
      "| final userTab:", userTab
    );

    // filters (classification, surface, crewType, etc)
    const userFilters =
      tsUserDocument.defaultFilters !== ""
        ? tsUserDocument.defaultFilters
        : defaultFilters;

    // screenAccess (classification, surface, crewType, etc)
    const userScreenAccess =
      tsUserDocument.screenAccessList !== ""
        ? tsUserDocument.screenAccessList
        : defaultScreenAccessList;

    // lock/unlock button access
    const userRoleList = tsUserDocument.roleList;
    var userLockAccess_unassigned = false;
    var userLockAccess_assigned = false;

    // InstallMgrs
    if (userRoleList.find((obj) => obj === "im_all")) {
      userLockAccess_unassigned = true;
      userLockAccess_assigned = true;

      // Coordinators
    } else if (userRoleList.find((obj) => obj === "coord_all")) {
      userLockAccess_unassigned = true;
      userLockAccess_assigned = false;
    }

    console.log(
      "%cModelTsContext (" + mongoDatabase + ") setupUserSettings, userUImode: ",
      "color: #00BFFF; font-weight: bold;",
      userUImode,
      ", setCompanyCode: ",
      userCompanyCode,
      ", setLocationFilter: ",
      userLocation,
      ", userStartDate: ",
      userStartDate,
      ", userTab: ",
      userTab,
      ", userLockAccess_unassigned: ",
      userLockAccess_unassigned,
      ", userLockAccess_assigned: ",
      userLockAccess_assigned,
      ", userScreenAccess: ",
      userScreenAccess
    );
    setDarkMode(userUImode === "dark" ? true : false);
    setJobTitleConfig(userJobTitleConfig);
    setLocationFilter(userLocation);
    setCompanyCode(userCompanyCode);
    setMondayThisWeek(userStartDate);
    setWhichList(userTab);
    setScreenAccess(userScreenAccess);
    setLockAccess_unassigned(userLockAccess_unassigned);
    setLockAccess_assigned(userLockAccess_assigned);
    setCurrentFilters(userFilters);

    // Mark that user settings have been applied from DB AFTER a small delay
    // This prevents the prefs useEffect from saving when state updates trigger during setupUserSettings
    // We need to wait for all the set* calls above to trigger their effects first
    setTimeout(() => {
      hasAppliedUserSettings.current = true;
    }, 100);
  };

  //
  //
  // - - - - - - - - - - - - - - - - -  USER LOGIN/OUT; MSAL  - - - - - - - - - - - - - - - - - -
  //
  //

  /**
   * Logs in a user using MSAL JWT token.
   * UPDATED: Jan 13, 2026 - Realm SDK removed (Atlas App Services EOL)
   * Now decodes JWT directly to extract user info instead of calling Realm.
   * @param {string} jwt - The JWT token from MSAL.
   */
  const loginMsal = async (jwt, account) => {
    setUserLoading(true);

    try {
      // Decode the JWT to extract user information
      // JWT structure: header.payload.signature (base64url encoded)
      const payloadBase64Url = jwt.split(".")[1];
      // Handle base64url encoding (replace - with + and _ with /)
      const payloadBase64 = payloadBase64Url.replace(/-/g, "+").replace(/_/g, "/");
      // Restore padding (atob requires length divisible by 4)
      const padLen = payloadBase64.length % 4;
      const payloadBase64Padded = padLen ? payloadBase64 + "=".repeat(4 - padLen) : payloadBase64;
      const payloadJson = atob(payloadBase64Padded);
      const payload = JSON.parse(payloadJson);

      // Create a user object compatible with what Realm used to provide
      // The 'oid' (object ID) from Azure AD is used as the user ID
      // This matches what Realm would have used as the user.id
      // #174 — the access token (what we decode here) does NOT carry the
      // profile name claims (name / given_name / family_name); those live on
      // the MSAL account's ID-token claims. Prefer the account for the display
      // name + discrete given/family names so the lock-attribution ("Last, F")
      // is correct (was showing "Unknown" because payload.name is absent on
      // access tokens). Email still comes from the access token's
      // preferred_username (falling back to the account username).
      const idClaims = account?.idTokenClaims || {};
      const user = {
        id: payload.oid || payload.sub, // Azure AD object ID or subject
        _profile: {
          data: {
            name: account?.name || payload.name || "Unknown",
            givenName: idClaims.given_name || "",
            familyName: idClaims.family_name || "",
            email:
              payload.preferred_username ||
              payload.email ||
              payload.upn ||
              account?.username ||
              "Unknown",
          }
        }
      };

      console.log(
        "%cModelTsContext (" + mongoDatabase + ") loginMsal (JWT decoded) -> mongoUser: ",
        "color: #00BFFF; font-weight: bold;",
        user
      );
      console.log(
        "%cModelTsContext (" + mongoDatabase + ") loginMsal name: ",
        "color: #00BFFF; font-weight: bold;",
        user._profile.data.name,
        ", email: ",
        user._profile.data.email
      );

      setMongoUser(user);
    } catch (error) {
      console.error(
        "%cModelTsContext (" + mongoDatabase + ") loginMsal ERROR decoding JWT:",
        "color: #ff0000; font-weight: bold;",
        error
      );
    }

    setUserLoading(false);
  };

  // - - - - - - - - - -

  /**
   * Logs out the current user.
   * UPDATED: Jan 13, 2026 - Realm SDK removed, simplified logout
   * UPDATED: 2026-04-15 (#86) - Clear tsUserDocument, reset shouldFetchUser,
   *   and flush TanStack cache so a same-user re-login in the SAME browser
   *   tab re-runs the full user-defaults chain. Previously `tsUserDocument`
   *   persisted across logout, the ID-equality gate at line ~4973 blocked
   *   `setTsUserDocument(userResult)`, and `setupUserSettings()` never
   *   re-fired — so the user stayed on whatever screen/location was active
   *   before logout. Closing/reopening the browser worked because it wiped
   *   all React state + cache. See #86 for full root cause analysis.
   */
  const logoutUser = async () => {
    if (mongoUser) {
      const previousUserId = mongoUser?.id;
      // REMOVED: mongoUser.logOut() - Realm SDK no longer used
      // All logout clears are orchestrated by performLogoutClears so tests
      // can import the exact production logic and catch any future
      // regression of the #86 fix (e.g. someone removes a clear).
      performLogoutClears({
        setMongoUser,
        setAwsgwUserResult,
        setAwsgwProjectsResult,
        setAwsgwCandidatesResult,
        setAwsgwAttributesResult,
        setAwsgwUniqueLocationsResult,
        setAwsgwOfficeMetaDataResult,
        setTsUserDocument,
        setShouldFetchUser,
        queryClient,
        hasRunSetupUserSettingsRef: hasRunSetupUserSettings,
        hasAppliedUserSettingsRef: hasAppliedUserSettings,
        hasTriedCreateUserRef: hasTriedCreateUser,
      });
      console.log(
        "ModelTsContext (" + mongoDatabase + ") Logout complete",
        { previousUser: previousUserId }
      );
    }
  };

  //
  //
  // - - - - - - - - - - - - - - - - -  EFFECT HOOKS  - - - - - - - - - - - - - - - - - -
  //
  //

  // - - - - - - - - - - mongoUser -> getUserByEmail

  // Trigger getUserByEmail when mongoUser changes (after login)
  // UPDATED: Jan 13, 2026 - Now uses email from JWT instead of Realm user ID
  useEffect(() => {
    if (mongoUser && userEmail) {
      console.log(
        "%cModelTsContext (" +
          mongoDatabase +
          ") useEffect[mongoUser]...(awsgwGetUserByEmail)",
        "color: #00BFFF; font-weight: bold;",
        { mongoUserId: mongoUser?.id, userEmail, shouldFetchUser }
      );
      awsgwGetUserById(); // Function name unchanged but now triggers getUserByEmail query
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mongoUser, userEmail]);

  // - - - - - - - - - - locationChanges -> logging

  // Reset log tracking refs when location changes
  useEffect(() => {
    // Only treat as a location "change" if we're going from one non-empty location to another
    // Initial load from '' to 'MGM' is NOT a change, it's initialization
    const isActualLocationChange = previousLocation.current !== '' && 
                                    locationFilter !== '' && 
                                    previousLocation.current !== locationFilter;
    
    if (!isActualLocationChange) {
      previousLocation.current = locationFilter; // Update for next time
      // Reset tracking flags even on initialization (handles page refresh)
      hasLoggedUsers.current = false;
      hasLoggedLogs.current = false;
      hasLoggedCandidatesAdditional.current = false;
      hasCompletedUniqueLocations.current = false;
      hasCompletedOfficeMetaData.current = false;
      hasCompletedProjectsBeforeMerge.current = false;
      hasCompletedCandidatesList.current = false;
      hasCompletedAttributes.current = false;
      setAttributesInitialLoadComplete(false); // Reset for new location
      // NOTE: hasCompletedUser is NOT reset here - user query is user-specific, not location-specific
      // The user data doesn't change when location changes, so the query doesn't re-run
      hasCompletedUsers.current = false;
      hasCompletedLogs.current = false;
      return; // Don't treat initialization as a location change
    }

    // This is an actual location change (user clicked a different location)
    hasLoggedUsers.current = false;
    hasLoggedLogs.current = false;
    hasLoggedCandidatesAdditional.current = false;
    hasCompletedUniqueLocations.current = false;
    hasCompletedOfficeMetaData.current = false;
    hasCompletedProjectsBeforeMerge.current = false;
    hasCompletedCandidatesList.current = false;
    hasCompletedAttributes.current = false;
    setAttributesInitialLoadComplete(false); // Reset for new location
    // NOTE: hasCompletedUser is NOT reset - user query is user-specific, not location-specific
    // The user data doesn't change when location changes, and the query doesn't re-run
    // Resetting this causes log timeouts because completedQueries can never include "user"
    hasCompletedUsers.current = false;
    hasCompletedLogs.current = false;
    expectedProjectQueryList.current = ''; // Clear expected projectQueryList to prevent stale queries
    lastSetAdditionalTimestamp.current = null; // Clear timestamp tracking to allow new query results
    // Don't clear the refs here - let the data validation useEffects set them when fresh data arrives
    // realmDataLocation.current = null;
    // tanstackBaseLocation.current = null;
    // tanstackAdditionalLocation.current = null;
    hasHadLocationChange.current = true; // Mark that we've had a location change
    // Clear Realm's requestedProjectsList to prevent stale data comparisons
    // when switching locations (prevents projectsAfterMerge mismatches)
    setRealmRequestedProjectsList([]);
    // Also clear projectsListBeforeMerge to prevent stale Realm base projects
    // from being merged with new location's shared projects
    setProjectsListBeforeMerge([]);
    // Clear projectQueryList to prevent querying with old location's shared installer list
    setProjectQueryList([]);
    // Clear TanStack state to prevent stale data from being processed
    setAwsgwProjectsResult([]);
    setAwsgwProjectsAdditionalResult([]);
    // CRITICAL: Clear projectListAfterMerge to prevent "THE BIG MOVE" from using stale data
    // Without this, the old location's merged project list gets passed to the UI on location change
    setProjectListAfterMerge([]);
    setProjectListAfterMergeLocation(""); // Clear location tracking
    // Also clear attributes result to prevent "THE BIG MOVE - Attributes" from using stale data
    setAwsgwAttributesResult([]);
    setAwsgwAttributesResultLocation(""); // Clear attributes location tracking
    // Clear candidates result since candidates are location-specific
    setAwsgwCandidatesResult([]);
    setAwsgwCandidatesResultLocation(""); // Clear candidates location tracking
    // Reset completed queries to prevent timeout from old location's incomplete queries
    // DON'T reset expectedQueries - ScheduleContext just set them and will manage them
    setCompletedQueries(new Set());
    
    // Cancel in-flight queries from old location and remove cached data completely
    // NOTE: shouldFetch flag resets are handled by schedule.context.js in onScheduleNeedsModel()
    // We intentionally do NOT reset them here to avoid race conditions between the two contexts
    // Use predicate to remove ALL instances of each query type, not just specific parameters
    queryClient.cancelQueries({ predicate: (query) => query.queryKey[0] === "getCandidatesByCompany" });
    queryClient.cancelQueries({ predicate: (query) => query.queryKey[0] === "getProjectsByCompany" });
    queryClient.cancelQueries({ predicate: (query) => query.queryKey[0] === "getProjectsByPayrollNoList" });
    queryClient.cancelQueries({ predicate: (query) => query.queryKey[0] === "getAttributesByLocation" });
    queryClient.cancelQueries({ predicate: (query) => query.queryKey[0] === "getAttributesByLocationPolling" });
    queryClient.removeQueries({ predicate: (query) => query.queryKey[0] === "getCandidatesByCompany" });
    queryClient.removeQueries({ predicate: (query) => query.queryKey[0] === "getProjectsByCompany" });
    queryClient.removeQueries({ predicate: (query) => query.queryKey[0] === "getProjectsByPayrollNoList" });
    queryClient.removeQueries({ predicate: (query) => query.queryKey[0] === "getAttributesByLocation" });
    queryClient.removeQueries({ predicate: (query) => query.queryKey[0] === "getAttributesByLocationPolling" });

    // Clear polling buffers on location change to avoid mixing data from different locations
    setPollingResultsBuffer([]);
    setLastPollingLogTime(null);
    setAttributesPollingResultsBuffer([]);
    setLastAttributesPollingLogTime(null);
    
    // Don't clear TanStack state - let TanStack Query manage its own cache invalidation via queryKey
    console.log(
      "%cModelTsContext - Location changed, cleared Realm cached data, cancelled and removed ALL TanStack query cache (using predicates)",
      "color: #FF6347; font-weight: bold; background-color: #1a1a1a; padding: 2px 4px;",
      { from: previousLocation.current, to: locationFilter }
    );
    previousLocation.current = locationFilter; // Update for next time
  }, [locationFilter, queryClient]);

  // Track when projectsListBeforeMerge updates from Realm
  useEffect(() => {
    // Silent tracking - no logging needed
  }, [projectsListBeforeMerge]);

  // - - - - - - - - - - Idle Detection for Polling - - - - - - - - - -

  // Log when isUserIdle state changes (for debugging visibility)
  useEffect(() => {
    console.log(
      `%cModelTsContext - isUserIdle state changed to: ${isUserIdle ? "IDLE (polling paused)" : "ACTIVE (polling enabled)"}`,
      `color: ${isUserIdle ? "#FF6347" : "#32CD32"}; font-weight: bold; background-color: #1a1a1a; padding: 2px 4px;`
    );
  }, [isUserIdle]);

  // Track user activity (mouse, keyboard, touch) and pause polling after idle timeout
  // Also pause polling when window/tab loses focus (visibilitychange, blur)
  useEffect(() => {
    const IDLE_TIMEOUT_MS = MONGODB_POLLING_IDLE_TIMEOUT * 60 * 1000; // Convert minutes to milliseconds

    // Update last activity time on any user interaction
    // Only resume polling if the window actually has focus (prevents mousemove over unfocused browser from resuming)
    const handleUserActivity = () => {
      // Ignore activity if window doesn't have focus
      if (!document.hasFocus()) return;

      lastUserActivityTime.current = Date.now();

      if (isUserIdle) {
        setIsUserIdle(false);
        console.log(
          "%cModelTsContext - User activity detected, resuming polling",
          "color: #9370DB; font-weight: bold; background-color: #1a1a1a; padding: 2px 4px;"
        );
      }
    };

    // Handle visibility change (tab switch, minimize, etc.)
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Tab/window is now hidden - pause polling immediately
        if (!isUserIdle) {
          setIsUserIdle(true);
          console.log(
            "%cModelTsContext - Window/tab hidden, pausing polling",
            "color: #9370DB; font-weight: bold; background-color: #1a1a1a; padding: 2px 4px;"
          );
        }
      } else {
        // Tab/window is now visible - resume polling
        lastUserActivityTime.current = Date.now();
        if (isUserIdle) {
          setIsUserIdle(false);
          console.log(
            "%cModelTsContext - Window/tab visible, resuming polling",
            "color: #9370DB; font-weight: bold; background-color: #1a1a1a; padding: 2px 4px;"
          );
        }
      }
    };

    // Handle window blur (another app gains focus)
    const handleWindowBlur = () => {
      if (!isUserIdle) {
        setIsUserIdle(true);
        console.log(
          "%cModelTsContext - Window lost focus, pausing polling",
          "color: #9370DB; font-weight: bold; background-color: #1a1a1a; padding: 2px 4px;"
        );
      }
    };

    // Handle window focus (app gains focus)
    const handleWindowFocus = () => {
      lastUserActivityTime.current = Date.now();
      if (isUserIdle) {
        setIsUserIdle(false);
        console.log(
          "%cModelTsContext - Window gained focus, resuming polling",
          "color: #9370DB; font-weight: bold; background-color: #1a1a1a; padding: 2px 4px;"
        );
      }
    };

    // Check idle status every 30 seconds
    const checkIdleStatus = () => {
      // Skip idle check if already idle due to visibility/focus
      if (isUserIdle) return;

      const now = Date.now();
      const timeSinceActivity = now - lastUserActivityTime.current;

      if (timeSinceActivity >= IDLE_TIMEOUT_MS && !isUserIdle) {
        setIsUserIdle(true);
        console.log(
          "%cModelTsContext - User idle for " + MONGODB_POLLING_IDLE_TIMEOUT + " minutes, pausing polling",
          "color: #9370DB; font-weight: bold; background-color: #1a1a1a; padding: 2px 4px;"
        );
      }
    };

    // Listen for user activity events
    const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart'];
    events.forEach(event => {
      window.addEventListener(event, handleUserActivity, { passive: true });
    });

    // Listen for visibility and focus changes
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('blur', handleWindowBlur);
    window.addEventListener('focus', handleWindowFocus);

    // Check idle status periodically
    const idleCheckInterval = setInterval(checkIdleStatus, 30000); // Check every 30 seconds

    // Cleanup
    return () => {
      events.forEach(event => {
        window.removeEventListener(event, handleUserActivity);
      });
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('blur', handleWindowBlur);
      window.removeEventListener('focus', handleWindowFocus);
      clearInterval(idleCheckInterval);
    };
  }, [isUserIdle]);

  //
  // - - - - - - - - - - uniqueLocations
  //

  useEffect(() => {
    if (uniqueLocationsLoading) {
      console.log(
        "%cModelTsContext useEffect[uniqueLocationsLoading] triggered...",
        "color: #eaff00ff; font-weight: bold; background-color: #1a1a1a; padding: 2px 4px;"
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uniqueLocationsLoading]);

  // - - - - - - - - - -

  useEffect(() => {
    if (uniqueLocationsError) {
      console.log(
        "%cModelTsContext useEffect[uniqueLocationsError] ERROR: ",
        "color: #ff8400ff; font-weight: bold; background-color: #1a1a1a; padding: 2px 4px;",
        uniqueLocationsError
      );

      // Track failed query for UI feedback
      setFailedQueries(prev => new Set([...prev, "uniqueLocations"]));

      // Auto-retry after 5 seconds if not already retrying
      if (!retryingQueries.has("uniqueLocations")) {
        setRetryingQueries(prev => new Set([...prev, "uniqueLocations"]));
        console.log(
          "%cModelTsContext - uniqueLocations query failed, auto-retrying in 5 seconds...",
          "color: #FFA500; font-weight: bold; background-color: #1a1a1a; padding: 2px 4px;"
        );
        setTimeout(() => {
          refetchUniqueLocations();
          setRetryingQueries(prev => {
            const updated = new Set(prev);
            updated.delete("uniqueLocations");
            return updated;
          });
        }, 5000);
      }
    } else if (!uniqueLocationsError && uniqueLocationsResult) {
      // Query succeeded - clear from failed queries
      setFailedQueries(prev => {
        const updated = new Set(prev);
        updated.delete("uniqueLocations");
        return updated;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uniqueLocationsError, uniqueLocationsResult]);

  // - - - - - - - - - -

  useEffect(() => {
    if (
      uniqueLocationsResult &&
      Object.keys(uniqueLocationsResult).length > 0 &&
      !uniqueLocationsFetching
    ) {
      setAwsgwUniqueLocationsResult(uniqueLocationsResult);

      // Only set locationArray from TanStack if conversion is NOT "realmOnly"
      // If "realmOnly", Realm's getUniqueLocations() will set locationArray instead
      const usesTanStackDataSource = tsUserDocument?.conversion !== "realmOnly";
      if (usesTanStackDataSource) {
        setLocationArray(uniqueLocationsResult);
      }

      setShouldFetchUniqueLocations(false); // Reset trigger

      // Clear from failedQueries since query succeeded (handles retry success case)
      setFailedQueries(prev => {
        if (prev.has("uniqueLocations")) {
          const updated = new Set(prev);
          updated.delete("uniqueLocations");
          console.log(
            "%cModelTsContext - Cleared uniqueLocations from failedQueries (query succeeded)",
            "color: #22c55e; font-weight: bold;"
          );
          return updated;
        }
        return prev;
      });

      // Mark query as completed for initial-load tracking
      if (!hasCompletedUniqueLocations.current) {
        hasCompletedUniqueLocations.current = true;
        setCompletedQueries((prev) => {
          const updated = new Set(prev);
          updated.add("uniqueLocations");
          return updated;
        });
      }

      // Log execution time (no comparison - TanStack-primary)
      const executionTime = queryExecutionTimes.getUniqueLocations || 0;
      if (executionTime > 0 && !hasLoggedUniqueLocations.current) {
        hasLoggedUniqueLocations.current = true;
        const payloadSizeBytes = new Blob([JSON.stringify(uniqueLocationsResult)]).size;
        const logEntry = {
          query: "uniqueLocations",
          result: `${uniqueLocationsResult.length} locations`,
          realmExecutionTimeMs: 0,
          tanstackExecutionTimeMs: executionTime,
          payloadSizeBytes: payloadSizeBytes,
          recordCount: uniqueLocationsResult.length,
          maxLimit: 0,
          capacityPercent: 0,
          match: null,
          note: usesTanStackDataSource ? "TanStack data source" : "Realm data source (TanStack logged only)",
        };
        setConsolidatedLogQueries((prev) => [...prev, logEntry]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    uniqueLocationsResult,
    queryExecutionTimes.getUniqueLocations,
    uniqueLocationsFetching,
    tsUserDocument?.conversion,
  ]);

  //
  // - - - - - - - - - - officeMetaData
  //

  useEffect(() => {
    if (officeMetaDataLoading) {
      console.log(
        "%cModelTsContext useEffect[officeMetaDataLoading] triggered...",
        "color: #eaff00ff; font-weight: bold; background-color: #1a1a1a; padding: 2px 4px;"
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [officeMetaDataLoading]);

  // - - - - - - - - - -

  useEffect(() => {
    if (officeMetaDataError) {
      console.log(
        "%cModelTsContext useEffect[officeMetaDataError] ERROR: ",
        "color: #ff8400ff; font-weight: bold; background-color: #1a1a1a; padding: 2px 4px;",
        officeMetaDataError
      );

      // Track failed query for UI feedback
      setFailedQueries(prev => new Set([...prev, "officeMetaData"]));

      // Auto-retry after 5 seconds if not already retrying
      if (!retryingQueries.has("officeMetaData")) {
        setRetryingQueries(prev => new Set([...prev, "officeMetaData"]));
        console.log(
          "%cModelTsContext - officeMetaData query failed, auto-retrying in 5 seconds...",
          "color: #FFA500; font-weight: bold; background-color: #1a1a1a; padding: 2px 4px;"
        );
        setTimeout(() => {
          refetchOfficeMetaData();
          setRetryingQueries(prev => {
            const updated = new Set(prev);
            updated.delete("officeMetaData");
            return updated;
          });
        }, 5000);
      }
    } else if (!officeMetaDataError && officeMetaDataResult) {
      // Query succeeded - clear from failed queries
      setFailedQueries(prev => {
        const updated = new Set(prev);
        updated.delete("officeMetaData");
        return updated;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [officeMetaDataError, officeMetaDataResult]);

  // - - - - - - - - - -

  useEffect(() => {
    // Debug: Log when this effect triggers to trace officeMetaData completion issues
    if (officeMetaDataResult !== undefined) {
      console.log(
        "%cModelTsContext useEffect[officeMetaDataResult] triggered:",
        "color: #FFA500; font-weight: bold;",
        {
          hasResult: !!officeMetaDataResult,
          resultKeys: officeMetaDataResult ? Object.keys(officeMetaDataResult).length : 0,
          isFetching: officeMetaDataFetching,
          hasCompletedRef: hasCompletedOfficeMetaData.current,
        }
      );
    }

    if (
      officeMetaDataResult &&
      Object.keys(officeMetaDataResult).length > 0 &&
      !officeMetaDataFetching
    ) {
      console.log(
        "%cModelTsContext useEffect[officeMetaDataResult] INSIDE IF BLOCK - marking complete",
        "color: #22c55e; font-weight: bold;",
        { hasCompletedRef: hasCompletedOfficeMetaData.current }
      );

      setAwsgwOfficeMetaDataResult(officeMetaDataResult);

      // Only set officeDocument from TanStack if conversion is NOT "realmOnly"
      // If "realmOnly", Realm's getOfficeMetaDataByLocation() will set officeDocument instead
      const usesTanStackDataSource = tsUserDocument?.conversion !== "realmOnly";
      if (usesTanStackDataSource) {
        setOfficeDocument(officeMetaDataResult);
      }

      setShouldFetchOfficeMetaData(false); // Reset trigger

      // Clear from failedQueries since query succeeded (handles retry success case)
      setFailedQueries(prev => {
        if (prev.has("officeMetaData")) {
          const updated = new Set(prev);
          updated.delete("officeMetaData");
          console.log(
            "%cModelTsContext - Cleared officeMetaData from failedQueries (query succeeded)",
            "color: #22c55e; font-weight: bold;"
          );
          return updated;
        }
        return prev;
      });

      // Mark query as completed for initial-load tracking
      if (!hasCompletedOfficeMetaData.current) {
        console.log(
          "%cModelTsContext - ADDING officeMetaData to completedQueries",
          "color: #22c55e; font-weight: bold;"
        );
        hasCompletedOfficeMetaData.current = true;
        setCompletedQueries((prev) => {
          const updated = new Set(prev);
          updated.add("officeMetaData");
          console.log("%cModelTsContext - completedQueries after add:", "color: #22c55e;", [...updated]);
          return updated;
        });
      }

      // Log execution time
      const executionTime = queryExecutionTimes.getOfficeMetaDataByLocation || 0;
      if (executionTime > 0 && !hasLoggedOfficeMetaData.current) {
        hasLoggedOfficeMetaData.current = true;
        const payloadSizeBytes = new Blob([JSON.stringify(officeMetaDataResult)]).size;
        const logEntry = {
          query: "officeMetaData",
          result: "1 office document",
          realmExecutionTimeMs: 0,
          tanstackExecutionTimeMs: executionTime,
          payloadSizeBytes: payloadSizeBytes,
          recordCount: 1,
          maxLimit: 0,
          capacityPercent: 0,
          match: null,
          note: usesTanStackDataSource ? "TanStack data source" : "Realm data source (TanStack logged only)",
        };
        setConsolidatedLogQueries((prev) => [...prev, logEntry]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    officeMetaDataResult,
    queryExecutionTimes.getOfficeMetaDataByLocation,
    officeMetaDataFetching,
    tsUserDocument?.conversion,
  ]);

  //
  // - - - - - - - - - - userById
  //

  useEffect(() => {
    if (userByIdLoading) {
      console.log(
        "%cModelTsContext useEffect[userByIdLoading] triggered...",
        "color: #eaff00ff; font-weight: bold; background-color: #1a1a1a; padding: 2px 4px;"
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userByIdLoading]);

  // - - - - - - - - - -

  useEffect(() => {
    if (userError) {
      console.log(
        "%cModelTsContext useEffect[userError] ERROR: ",
        "color: #ff8400ff; font-weight: bold; background-color: #1a1a1a; padding: 2px 4px;",
        userError
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userError]);

  // - - - - - - - - - -

  useEffect(() => {
    const hasKeys = userResult ? Object.keys(userResult).length : 0;
    console.log(
      "%cModelTsContext useEffect[userResult] triggered:",
      "color: #00BFFF; font-weight: bold;",
      { userResult, userFetching, hasKeys }
    );
    if (userResult && Object.keys(userResult).length > 0 && !userFetching) {
      // userResult is already extracted by select: (raw) => raw?.message?.result
      setAwsgwUserResult(userResult);

      // Only set tsUserDocument if it's actually different (prevent triggering setupUserSettings multiple times)
      // Compare user IDs to avoid setting when it's the same user data
      if (tsUserDocument?.id !== userResult?.id) {
        console.log(
          "%cModelTsContext setting tsUserDocument:",
          "color: #00BFFF; font-weight: bold;",
          { newUserId: userResult?.id, currentUserId: tsUserDocument?.id }
        );
        setTsUserDocument(userResult);
      } else {
        console.log(
          "%cModelTsContext SKIPPED setting tsUserDocument (same user ID):",
          "color: #FFA500; font-weight: bold;",
          { userId: userResult?.id }
        );
      }
      // Don't auto-reset shouldFetchUser - let caller control when to stop polling

      // Mark user query as completed when TanStack data arrives
      // This is INDEPENDENT of whether Realm comparison can run
      // (Same pattern as other queries - completion tracking separate from log entry creation)
      if (!hasCompletedUser.current) {
        hasCompletedUser.current = true;
        // If expectedQueries already includes "user", add to completedQueries now
        if (expectedQueries.has("user")) {
          // console.log(
          //   "%c[DEBUG] User TanStack data arrived - marking complete",
          //   "color: lime; font-weight: bold"
          // );
          setCompletedQueries((prev) => {
            if (prev.has("user")) return prev;
            const updated = new Set(prev);
            updated.add("user");
            return updated;
          });
        }
      }

      // Realm comparison removed - Realm EOL Jan 13, 2026
      // All users now use TanStack data exclusively
      console.log(
        "%cModelTsContext - user loaded (TanStack)",
        "color: #00FF00; font-weight: bold;"
      );
      const logEntry = {
        query: "getUserByEmail",
        result: userResult ? "1 user" : "no user",
        realmExecutionTimeMs: null,
        tanstackExecutionTimeMs: queryExecutionTimes.getUserByEmail || null,
        match: null,
        note: "TanStack data source (Realm EOL)",
      };
      setConsolidatedLogQueries((prev) => [...prev, logEntry]);
    } else if (
      // User NOT found in database - auto-create new user.
      // Uses the pure `shouldCreateNewUser` function so tests import the
      // EXACT guard logic production uses (same pattern as datesEqual,
      // buildScheduleUpdateFields, etc.). See src/utility/shouldCreateNewUser.js
      // for the full #90 explanation and the test file for the regression
      // guards.
      shouldCreateNewUser({
        userFetching,
        userResult,
        mongoUser,
        createNewUserIsPending: createNewUser.isPending,
        hasTriedCreateUser: hasTriedCreateUser.current,
      })
    ) {
      console.log(
        "%c🆕 ModelTsContext - User not found in database, auto-creating new user...",
        "color: #FFA500; font-weight: bold; background-color: #1a1a1a; padding: 2px 4px;",
        { mongoUserId: mongoUser.id, email: mongoUser._profile.data.email }
      );

      // Mark the attempt BEFORE firing the mutation (not in onSuccess) so a
      // subsequent useEffect fire during the race window sees the ref=true
      // and skips the else-if branch entirely. This is the load-bearing
      // line of the #90 fix.
      hasTriedCreateUser.current = true;

      // Create new user record using default template from AppContext
      // Deep copy to avoid mutating the template
      const newUserRecord = JSON.parse(JSON.stringify(defaultNewUserRecord));
      newUserRecord.id = mongoUser.id;
      newUserRecord.username = mongoUser._profile.data.email;

      console.log(
        "%cModelTsContext - Creating new user with record:",
        "color: #00BFFF; font-weight: bold;",
        newUserRecord
      );

      createNewUser.mutate({ document: newUserRecord });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    userResult,
    tsUserDocument,
    queryExecutionTimes.getUserByEmail,
    // realmQueryExecutionTimes removed - Realm EOL
    userFetching,
    mongoUser,
    defaultNewUserRecord,
  ]);

  // - - - - - - - - - -

  // Trigger setupUserSettings when tsUserDocument changes
  // Replaces modelRwContext useEffect[userDocument]
  // NOW ACTIVE: TanStack is primary auth source
  useEffect(() => {
    if (tsUserDocument && tsUserDocument.id) {
      // Only run setupUserSettings ONCE per page load session
      // This prevents re-applying settings when tsUserDocument object reference changes
      // (TanStack Query returns new object on each fetch even if data is identical)
      if (!hasRunSetupUserSettings.current) {
        console.log(
          "%cModelTsContext (" +
            mongoDatabase +
            ") useEffect[tsUserDocument]...(setupUserSettings)",
          "color: #00BFFF; font-weight: bold;",
          {
            tsUserDocument_defaultLocation: tsUserDocument.defaultLocation,
            tsUserDocument_defaultTab: tsUserDocument.defaultTab,
            userId: tsUserDocument.id
          }
        );
        setupUserSettings(); // NOW ACTIVE: TanStack is primary auth source
        hasRunSetupUserSettings.current = true; // Mark that we've run setupUserSettings this session
      } else {
        console.log(
          "%cModelTsContext (" +
            mongoDatabase +
            ") useEffect[tsUserDocument]...(SKIPPED - settings already applied this session)",
          "color: #FFA500; font-weight: bold;",
          { userId: tsUserDocument.id, defaultTab: tsUserDocument.defaultTab }
        );
      }
    } else if (tsUserDocument && !tsUserDocument.id) {
      console.log(
        "%cModelTsContext (" +
          mongoDatabase +
          ") useEffect[tsUserDocument], no tsUserDocument.id, id: ",
        "color: #00BFFF; font-weight: bold;",
        tsUserDocument?.id
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tsUserDocument]);

  // - - - - - - - - - -

  // Trigger setUserPrefsById when user preferences change
  // Replaces modelRwContext useEffect[currentFilters, countsToggle, jobTitleConfig, weekDatesArray, whichList]
  // NOW ACTIVE: TanStack is primary auth source
  // NOTE: Added darkMode, companyCode, locationFilter which were missing from original modelRW implementation
  useEffect(() => {
    // IMPORTANT: Don't save prefs until user settings have been loaded from DB
    // This prevents the initial default values (like whichList="schedule") from overwriting
    // the user's actual saved preferences (like defaultTab="optimizer") on app startup
    if (mongoUser && hasAppliedUserSettings.current && userEmail) {
      // prefs has been changed, so process the change
      console.log(
        "%cModelTsContext (" +
          mongoDatabase +
          ") useEffect[prefs]...(setUserById)",
        "color: #00BFFF; font-weight: bold;"
      );
      // UPDATED: Jan 13, 2026 - Use username (email) for filter since Realm user IDs no longer exist
      setUserById.mutate({
        filter: { username: userEmail },
        update: {
          $set: {
            defaultUImode: darkMode ? "dark" : "light",
            defaultCompany: companyCode,
            defaultLocation: locationFilter,
            defaultDate: weekDatesArray[0],
            defaultTab: whichList,
            defaultFilters: currentFilters,
            defaultCountsToggle: countsToggle,
            jobTitleConfig: jobTitleConfig,
          },
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentFilters, countsToggle, jobTitleConfig, weekDatesArray, whichList, darkMode, companyCode, locationFilter]);

  //
  // - - - - - - - - - - users
  //

  useEffect(() => {
    if (usersLoading) {
      console.log(
        "%cModelTsContext useEffect[usersLoading] triggered...",
        "color: #eaff00ff; font-weight: bold; background-color: #1a1a1a; padding: 2px 4px;"
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usersLoading]);

  // - - - - - - - - - -

  useEffect(() => {
    if (usersError) {
      console.log(
        "%cModelTsContext useEffect[usersError] ERROR: ",
        "color: #ff8400ff; font-weight: bold; background-color: #1a1a1a; padding: 2px 4px;",
        usersError
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usersError]);

  // - - - - - - - - - -

  useEffect(() => {
    // Debug: trace this useEffect
    // console.log(
    //   "%c[DEBUG] users useEffect triggered",
    //   "color: magenta; font-weight: bold",
    //   {
    //     hasUsersResult: !!usersResult,
    //     usersResultKeys: usersResult ? Object.keys(usersResult).length : 0,
    //     usersFetching,
    //     hasCompletedUsers: hasCompletedUsers.current,
    //     expectedQueriesHasUsers: expectedQueries.has("users"),
    //     expectedQueriesArray: Array.from(expectedQueries),
    //   }
    // );

    if (usersResult && Object.keys(usersResult).length > 0 && !usersFetching) {
      const { users, _executionTime } = usersResult;
      setAwsgwUsersResult(users);

      // Only set requestedUsersList from TanStack if conversion is NOT "realmOnly"
      // If "realmOnly", Realm's getUsers() will set requestedUsersList instead
      const usesTanStackDataSource = tsUserDocument?.conversion !== "realmOnly";
      if (usesTanStackDataSource) {
        const usersWithMongoId = users.map((doc) => ({
          ...doc,
          mongoId: doc._id?.toString() ?? doc._id,
        }));
        setRequestedUsersList(usersWithMongoId);
      }
      // Don't auto-reset shouldFetchUsers - let caller control when to stop polling

      // Mark query as completed for initial-load tracking (regardless of log entry)
      // Only mark complete if users is in expectedQueries (prevents premature completion before login)
      if (!hasCompletedUsers.current && expectedQueries.has("users")) {
        // console.log(
        //   "%c[DEBUG] users: MARKING COMPLETE",
        //   "color: lime; font-weight: bold"
        // );
        hasCompletedUsers.current = true;
        setCompletedQueries((prev) => {
          const updated = new Set(prev);
          updated.add("users");
          return updated;
        });
      }
      // else {
      //   console.log(
      //     "%c[DEBUG] users: NOT marking complete",
      //     "color: orange; font-weight: bold",
      //     {
      //       reason: hasCompletedUsers.current
      //         ? "already completed"
      //         : "users not in expectedQueries",
      //     }
      //   );
      // }

      // Add to consolidated log (tracking execution time without comparison)
      const executionTime = _executionTime || 0;

      // console.log(
      //   "%c[DEBUG] users: checking log entry creation",
      //   "color: yellow; font-weight: bold",
      //   {
      //     executionTime,
      //     hasLoggedUsers: hasLoggedUsers.current,
      //     expectedQueriesHasUsers: expectedQueries.has("users"),
      //     willCreateLogEntry: executionTime > 0 && !hasLoggedUsers.current && expectedQueries.has("users"),
      //   }
      // );

      // Only create log entry once when execution time is available
      if (executionTime > 0 && !hasLoggedUsers.current && expectedQueries.has("users")) {
        // console.log(
        //   "%c[DEBUG] users: CREATING LOG ENTRY",
        //   "color: lime; font-weight: bold"
        // );
        hasLoggedUsers.current = true;

        const payloadSizeBytes = new Blob([JSON.stringify(users)]).size;
        const capacityPercent = Math.round((users.length / MONGODB_MAXLIMIT_USERS) * 100);
        const logEntry = {
          query: "users",
          result: `${users.length} users`,
          realmExecutionTimeMs: 0,
          tanstackExecutionTimeMs: executionTime,
          payloadSizeBytes: payloadSizeBytes,
          recordCount: users.length,
          maxLimit: MONGODB_MAXLIMIT_USERS,
          capacityPercent: capacityPercent,
          match: null, // Not compared
          note: usesTanStackDataSource ? "TanStack data source" : "Realm data source (TanStack logged only)",
        };
        setConsolidatedLogQueries((prev) => [...prev, logEntry]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usersResult, usersFetching, expectedQueries, tsUserDocument?.conversion]);

  //
  // - - - - - - - - - - project
  //

  useEffect(() => {
    if (projectLoading) {
      console.log(
        "%cModelTsContext useEffect[projectLoading] triggered...",
        "color: #eaff00ff; font-weight: bold; background-color: #1a1a1a; padding: 2px 4px;"
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectLoading]);

  // - - - - - - - - - -

  useEffect(() => {
    if (projectError) {
      console.log(
        "%cModelTsContext useEffect[projectError] ERROR: ",
        "color: #ff8400ff; font-weight: bold; background-color: #1a1a1a; padding: 2px 4px;",
        projectError
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectError]);

  // - - - - - - - - - -

  useEffect(() => {
    if (projectResult && Object.keys(projectResult).length > 0) {
      setAwsgwProjectResult(projectResult);
      // Don't auto-reset shouldFetchProject - let caller control when to stop polling
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectResult]);

  //
  // - - - - - - - - - - projects
  //

  useEffect(() => {
    if (projectsLoading) {
      console.log(
        "%cModelTsContext useEffect[projectsLoading] triggered...",
        "color: #eaff00ff; font-weight: bold; background-color: #1a1a1a; padding: 2px 4px;"
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectsLoading]);

  // - - - - - - - - - -

  useEffect(() => {
    if (projectsError) {
      console.log(
        "%cModelTsContext useEffect[projectsError] ERROR: ",
        "color: #ff8400ff; font-weight: bold; background-color: #1a1a1a; padding: 2px 4px;",
        projectsError
      );

      // Track failed query for UI feedback
      setFailedQueries(prev => new Set([...prev, "projects"]));

      // Auto-retry after 5 seconds if not already retrying
      if (!retryingQueries.has("projects")) {
        setRetryingQueries(prev => new Set([...prev, "projects"]));
        console.log(
          "%cModelTsContext - projects query failed, auto-retrying in 5 seconds...",
          "color: #FFA500; font-weight: bold; background-color: #1a1a1a; padding: 2px 4px;"
        );
        setTimeout(() => {
          refetchProjects();
          setRetryingQueries(prev => {
            const updated = new Set(prev);
            updated.delete("projects");
            return updated;
          });
        }, 5000);
      }
    } else if (!projectsError && projectsResult) {
      // Query succeeded - clear from failed queries
      setFailedQueries(prev => {
        const updated = new Set(prev);
        updated.delete("projects");
        return updated;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectsError, projectsResult]);

  // - - - - - - - - - -

  useEffect(() => {
    if (
      projectsResult &&
      Object.keys(projectsResult).length > 0 &&
      !projectsFetching
    ) {
      // NOTE: Polling is now handled by getProjectsByCompanyWithShared query.
      // This effect only runs for initial fetch (for comparison with Realm's initial getProjectsByCompany).

      // Check if data matches current location
      const sampleLocations = projectsResult.slice(0, 3).map(p => p?.location);
      const dataMatchesLocation = sampleLocations.length > 0 && sampleLocations.every(loc => loc === locationFilter);

      // On initial load (before any location changes), accept data regardless of location match
      // This handles TanStack Query returning stale cached data on mount
      // After first location change, only accept data that matches current location
      if (dataMatchesLocation || !hasHadLocationChange.current) {
        tanstackBaseLocation.current = locationFilter;
        setAwsgwProjectsResult(projectsResult);

        // Clear from failedQueries since query succeeded (handles retry success case)
        setFailedQueries(prev => {
          if (prev.has("projects")) {
            const updated = new Set(prev);
            updated.delete("projects");
            console.log(
              "%cModelTsContext - Cleared projects from failedQueries (query succeeded)",
              "color: #22c55e; font-weight: bold;"
            );
            return updated;
          }
          return prev;
        });

        // Mark query as completed for initial-load tracking (regardless of comparison)
        if (!hasCompletedProjectsBeforeMerge.current) {
          hasCompletedProjectsBeforeMerge.current = true;
          setCompletedQueries((prev) => {
            const updated = new Set(prev);
            updated.add("projectsBeforeMerge");
            return updated;
          });
        }
      } else {
        console.log(
          "%cModelTsContext - projectsResult REJECTED - location mismatch, waiting for fresh data",
          "color: #FF0000; font-weight: bold; background-color: #1a1a1a; padding: 2px 4px;",
          { expected: locationFilter, got: sampleLocations }
        );
        return; // Don't proceed with comparison if data is rejected
      }
      // Don't auto-reset shouldFetchProjects - let caller control when to stop polling

      // Realm comparison removed - Realm EOL Jan 13, 2026
      // All users now use TanStack data exclusively
      console.log(
        "%cModelTsContext - projects loaded (TanStack)",
        "color: #00FF00; font-weight: bold;"
      );
      const logEntry = {
        query: "getProjectsByCompany",
        result: `${projectsResult?.length || 0} projects`,
        realmExecutionTimeMs: null,
        tanstackExecutionTimeMs: queryExecutionTimes.getProjectsByCompany || null,
        match: null,
        note: "TanStack data source (Realm EOL)",
      };
      setConsolidatedLogQueries((prev) => [...prev, logEntry]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    projectsResult,
    projectsListBeforeMerge,
    queryExecutionTimes.getProjectsByCompany,
    // realmQueryExecutionTimes.getProjectsByCompany removed - Realm EOL
    projectsFetching,
  ]);

  // - - - - - - - - - - polling

  // Handle getProjectsByCompanyWithShared polling results - compare with Realm's projectsList
  useEffect(() => {
    // After select transform, projectsWithSharedResult is directly { projects, meta, _executionTime }
    // (NOT wrapped in message.result - the select transform already extracted that)
    const pollingResult = projectsWithSharedResult;

    // Debug: Log when this effect runs to help diagnose comparison issues
    if (projectsWithSharedResult) {
      console.log(
        "%cModelTsContext - projectsWithShared effect check:",
        "color: #9370DB; font-weight: bold;",
        {
          hasProjects: !!pollingResult?.projects,
          projectCount: pollingResult?.projects?.length,
          isFetching: projectsWithSharedFetching,
          shouldPollProjects,
          willRunComparison: !!(pollingResult?.projects && !projectsWithSharedFetching && shouldPollProjects),
        }
      );
    }

    if (
      pollingResult &&
      pollingResult.projects &&
      !projectsWithSharedFetching &&
      shouldPollProjects
    ) {
      const tanstackProjects = pollingResult.projects || [];
      const tanstackMeta = pollingResult.meta || {};
      const executionTime = pollingResult._executionTime;

      // Realm's projectsList is the merged list (base + shared installer projects via change streams)
      const realmProjects = projectsList || [];

      // Calculate payload size
      const payloadSizeBytes = tanstackProjects.length > 0
        ? new Blob([JSON.stringify(tanstackProjects)]).size
        : 0;

      // Perform comparison between Realm and TanStack
      let comparison = {
        realmCount: realmProjects.length,
        tanstackCount: tanstackProjects.length,
        identical: false,
        diffCount: 0,
        note: "",
      };

      if (realmProjects.length === 0) {
        comparison.note = "Realm projectsList empty - waiting for data";
      } else if (tanstackProjects.length === 0) {
        comparison.note = "TanStack result empty";
      } else {
        // Compare by _id to find differences
        const realmIds = new Set(realmProjects.map(p => p._id?.toString?.() || p._id));
        const tanstackIds = new Set(tanstackProjects.map(p => p._id?.toString?.() || p._id));

        // Find projects in Realm but not in TanStack
        const inRealmOnly = [...realmIds].filter(id => !tanstackIds.has(id));
        // Find projects in TanStack but not in Realm
        const inTanstackOnly = [...tanstackIds].filter(id => !realmIds.has(id));

        comparison.diffCount = inRealmOnly.length + inTanstackOnly.length;
        comparison.identical = comparison.diffCount === 0 && realmProjects.length === tanstackProjects.length;

        if (comparison.identical) {
          comparison.note = "Data matches";
        } else {
          const notes = [];
          if (inRealmOnly.length > 0) notes.push(`${inRealmOnly.length} in Realm only`);
          if (inTanstackOnly.length > 0) notes.push(`${inTanstackOnly.length} in TanStack only`);
          if (realmProjects.length !== tanstackProjects.length) {
            notes.push(`count mismatch (${realmProjects.length} vs ${tanstackProjects.length})`);
          }
          comparison.note = notes.join("; ");

          // Debug: Log which specific projects differ (limit to first 10 each)
          // Use actual field names from MongoDB: installer (payroll), date_scheduled, location, invoiceNo
          if (inTanstackOnly.length > 0) {
            const tanstackOnlyProjects = tanstackProjects
              .filter(p => inTanstackOnly.includes(p._id?.toString?.() || p._id))
              .slice(0, 10)
              .map(p => ({
                _id: p._id,
                invoiceNo: p.invoiceNo,
                installer: p.installer,
                date_scheduled: p.date_scheduled,
                location: p.location,
              }));
            console.log(
              "%c[COMPARISON DEBUG] Projects in TanStack only:",
              "color: #FFA500; font-weight: bold;",
              tanstackOnlyProjects
            );
          }
          if (inRealmOnly.length > 0) {
            const realmOnlyProjects = realmProjects
              .filter(p => inRealmOnly.includes(p._id?.toString?.() || p._id))
              .slice(0, 10)
              .map(p => ({
                _id: p._id,
                invoiceNo: p.invoiceNo,
                installer: p.installer,
                date_scheduled: p.date_scheduled,
                location: p.location,
              }));
            console.log(
              "%c[COMPARISON DEBUG] Projects in Realm only:",
              "color: #FF6347; font-weight: bold;",
              realmOnlyProjects
            );
          }
        }
      }

      console.log(
        "%cModelTsContext - POLLING WITH SHARED result received",
        "color: #9370DB; font-weight: bold; background-color: #1a1a1a; padding: 2px 4px;",
        {
          tanstackCount: tanstackProjects.length,
          realmCount: realmProjects.length,
          meta: tanstackMeta,
          comparison,
          executionTime,
          changeStreamOverride: changeStreamPollingOverride.active ? `${changeStreamPollingOverride.remainingPolls} polls remaining` : "inactive",
        }
      );

      // Only update projectListAfterMerge if polling returned valid data
      // Don't overwrite good initial-load data with empty polling results
      // (This can happen if getProjectsByCompanyWithShared Lambda handler is not deployed)
      if (tanstackProjects.length > 0) {
        console.log(
          "%cModelTsContext - POLLING calling setProjectListAfterMerge",
          "color: #22c55e; font-weight: bold; background-color: #1a1a1a; padding: 2px 4px;",
          { projectCount: tanstackProjects.length, location: locationFilter }
        );
        setProjectListAfterMerge(tanstackProjects);
        setProjectListAfterMergeLocation(locationFilter); // Track which location this data belongs to
      } else {
        console.log(
          "%cModelTsContext - POLLING returned 0 projects, keeping existing projectListAfterMerge",
          "color: #FFA500; font-weight: bold;"
        );
      }

      // Decrement change stream polling override counter if active
      if (changeStreamPollingOverride.active && changeStreamPollingOverride.remainingPolls > 0) {
        const newRemaining = changeStreamPollingOverride.remainingPolls - 1;
        if (newRemaining <= 0) {
          // All verification polls complete - deactivate override
          console.log(
            "%cModelTsContext - Change stream verification polling complete, returning to idle",
            "color: #9370DB; font-weight: bold; background-color: #1a1a1a; padding: 2px 4px;"
          );
          setChangeStreamPollingOverride({ active: false, remainingPolls: 0, triggeredAt: null });
        } else {
          setChangeStreamPollingOverride(prev => ({ ...prev, remainingPolls: newRemaining }));
        }
      }

      // Check for pending change stream verifications
      // These are documents that Realm received via change stream that we now need to verify TanStack caught up
      // Initialize retry tracker if not exists (tracks consecutive poll failures per invoice)
      if (!window._changeStreamRetryTracker) {
        window._changeStreamRetryTracker = {};
      }

      if (window._pendingChangeStreamVerifications && window._pendingChangeStreamVerifications.length > 0) {
        const pendingList = [...window._pendingChangeStreamVerifications];
        window._pendingChangeStreamVerifications = []; // Clear the queue

        pendingList.forEach(pending => {
          const tsDocument = tanstackProjects.find(p => p.invoiceNo === pending.invoiceNo);
          if (tsDocument) {
            const differences = [];
            const allComparisons = []; // For debugging
            pending.fieldsToCompare.forEach(field => {
              const realmVal = pending.realmDocument[field];
              const tsVal = tsDocument[field];
              const realmStr = JSON.stringify(realmVal);
              const tsStr = JSON.stringify(tsVal);
              const isDifferent = realmStr !== tsStr;

              allComparisons.push({
                field,
                realmType: typeof realmVal,
                tsType: typeof tsVal,
                realmStr: realmStr,
                tsStr: tsStr,
                match: isDifferent ? '✗' : '✓'
              });

              if (isDifferent) {
                differences.push({ field, realm: realmVal, tanstack: tsVal, realmStr, tsStr });
              }
            });
            if (differences.length > 0) {
              // Track consecutive failures for this invoice
              const invoiceKey = String(pending.invoiceNo);
              const tracker = window._changeStreamRetryTracker[invoiceKey] || { count: 0, firstSeen: pending.timestamp };
              tracker.count += 1;
              tracker.lastSeen = new Date().toISOString();
              window._changeStreamRetryTracker[invoiceKey] = tracker;

              // Re-queue for next poll verification (up to 5 retries)
              if (tracker.count < 5) {
                if (!window._pendingChangeStreamVerifications) {
                  window._pendingChangeStreamVerifications = [];
                }
                window._pendingChangeStreamVerifications.push(pending);
              }

              const retryInfo = tracker.count >= 5 ? " (MAX RETRIES - giving up)" : ` (retry ${tracker.count}/5)`;
              const color = tracker.count >= 3 ? "#dc2626" : "#ef4444"; // Darker red after 3 retries

              console.log(
                `%c[POST-POLL VERIFY] Invoice ${pending.invoiceNo} - STILL DIFFERS after poll (${differences.length} fields)${retryInfo}`,
                `color: ${color}; font-weight: bold;`
              );
              console.log(`%c[POST-POLL VERIFY] Detailed comparison (showing stringified values):`, `color: ${color};`);
              console.table(allComparisons);

              if (tracker.count >= 5) {
                console.warn(
                  `%c[POST-POLL VERIFY] Invoice ${pending.invoiceNo} - PERSISTENT MISMATCH after 5 polls! First seen: ${tracker.firstSeen}`,
                  "color: #dc2626; font-weight: bold; background-color: #fef2f2; padding: 2px 4px;"
                );
              }
            } else {
              // Success! Clear from retry tracker
              const invoiceKey = String(pending.invoiceNo);
              const tracker = window._changeStreamRetryTracker[invoiceKey];
              const retryInfo = tracker ? ` (caught up after ${tracker.count} poll(s))` : "";
              delete window._changeStreamRetryTracker[invoiceKey];

              console.log(
                `%c[POST-POLL VERIFY] Invoice ${pending.invoiceNo} - TanStack CAUGHT UP! Documents now match${retryInfo}`,
                "color: #22c55e; font-weight: bold;"
              );
            }
          } else {
            console.log(
              `%c[POST-POLL VERIFY] Invoice ${pending.invoiceNo} - NOT FOUND in TanStack after poll`,
              "color: #ef4444; font-weight: bold;"
            );
          }
        });
      }

      // Add to polling buffer WITH comparison data
      setPollingResultsBuffer(prev => [...prev, {
        timestamp: new Date().toISOString(),
        query: 'getProjectsByCompanyWithShared',
        executionTime: executionTime,
        recordCount: tanstackProjects.length,
        payloadSizeBytes: payloadSizeBytes,
        location: locationFilter,
        meta: tanstackMeta,
        comparison: comparison,
      }]);

      // Update "Updated: X ago" timestamp for non-realmOnly users
      // This replaces the Realm change stream timestamp update
      if (tsUserDocument?.conversion !== "realmOnly") {
        setLatestChangeTS(new Date());
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectsWithSharedResult, projectsWithSharedFetching, projectsList]);

  // Batched Polling Log Entry - send accumulated polling results every N minutes
  useEffect(() => {
    if (pollingResultsBuffer.length === 0) return;

    const now = Date.now();
    const batchIntervalMs = MONGODB_POLLING_LOG_BATCH_INTERVAL * 60 * 1000; // Convert minutes to milliseconds

    // Initialize lastPollingLogTime on first polling result
    if (lastPollingLogTime === null) {
      setLastPollingLogTime(now);
      return;
    }

    // Check if it's time to send batched log
    const timeSinceLastLog = now - lastPollingLogTime;
    if (timeSinceLastLog >= batchIntervalMs) {
      // Calculate aggregated stats from buffer
      const executionTimes = pollingResultsBuffer.map(r => r.executionTime).filter(t => t !== undefined);
      const minTime = executionTimes.length > 0 ? Math.min(...executionTimes) : 0;
      const maxTime = executionTimes.length > 0 ? Math.max(...executionTimes) : 0;
      const avgTime = executionTimes.length > 0
        ? Math.round(executionTimes.reduce((sum, t) => sum + t, 0) / executionTimes.length)
        : 0;

      const timestamp = getEasternTimestamp();

      // Transform polling results buffer into queries array matching initial-load format
      const queries = pollingResultsBuffer.map(poll => ({
        query: poll.query || "getProjectsByCompany",
        result: `${poll.recordCount} projects`,
        realmExecutionTimeMs: null, // Polling doesn't use Realm execution time
        tanstackExecutionTimeMs: poll.executionTime,
        payloadSizeBytes: poll.payloadSizeBytes || 0,
        recordCount: poll.recordCount,
        maxLimit: MONGODB_MAXLIMIT_PROJECTSBYCOMPANY,
        capacityPercent: Math.round((poll.recordCount / MONGODB_MAXLIMIT_PROJECTSBYCOMPANY) * 100),
        // Include comparison data if available (from getProjectsByCompanyWithShared)
        identical: poll.comparison?.identical,
        note: poll.comparison?.note || "Polling query",
        timestamp: poll.timestamp,
        // Include comparison details if available
        ...(poll.comparison && {
          realmCount: poll.comparison.realmCount,
          tanstackCount: poll.comparison.tanstackCount,
          diffCount: poll.comparison.diffCount,
        }),
      }));

      // Calculate actual elapsed time for accurate activity tracking
      const elapsedSeconds = Math.round((now - lastPollingLogTime) / 1000);

      // Aggregate comparison stats from all polls in this batch
      const pollsWithComparison = pollingResultsBuffer.filter(p => p.comparison);
      const identicalCount = pollsWithComparison.filter(p => p.comparison.identical).length;
      const mismatchCount = pollsWithComparison.filter(p => !p.comparison.identical).length;
      const lastComparison = pollsWithComparison.length > 0
        ? pollsWithComparison[pollsWithComparison.length - 1].comparison
        : null;

      const logData = {
        type: "polling-batch",
        user: tsUserDocument?.username || "unknown",
        when: timestamp.when,
        whenDate: timestamp.whenDate,
        location: locationFilter || "unknown",
        queries: queries,
        pollCount: pollingResultsBuffer.length,
        elapsedSeconds, // Actual time this batch covers (for accurate activity time calculation)
        timeRange: `${pollingResultsBuffer[0].timestamp} to ${pollingResultsBuffer[pollingResultsBuffer.length - 1].timestamp}`,
        minTime,
        maxTime,
        avgTime,
        // Aggregated comparison summary for the batch
        comparison: lastComparison ? {
          identicalCount,
          mismatchCount,
          lastRealmCount: lastComparison.realmCount,
          lastTanstackCount: lastComparison.tanstackCount,
          lastIdentical: lastComparison.identical,
          lastNote: lastComparison.note,
        } : null,
      };

      console.log(
        "%cModelTsContext - Sending batched polling log",
        "color: #9370DB; font-weight: bold; background-color: #1a1a1a; padding: 2px 4px;",
        logData
      );

      // Send batched log entry to server
      const payload = {
        method: "createLogEntry",
        database: mongoDatabase,
        collection: MONGODB_COLLECTION_LOGS,
        document: logData,
      };

      fetch(MONGODB_AWSGW_MUTATIONS, {
        method: "POST",
        mode: "cors",
        credentials: "omit",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "x-api-key": MONGODB_AWSGW_APIKEY,
        },
        body: JSON.stringify(payload),
      })
        .then(response => response.json())
        .then(result => {
          console.log(
            "%cModelTsContext - Batched polling log sent successfully",
            "color: #9370DB; font-weight: bold; background-color: #1a1a1a; padding: 2px 4px;",
            result
          );
        })
        .catch(error => {
          console.error("Error sending batched polling log:", error);
        });

      // Send wide event to Axiom for polling-batch
      const wideEvent = createWideEvent("polling-batch");
      wideEvent.set("_appVersion", APP_VERSION);
      wideEvent.set("userDocument", tsUserDocument);
      wideEvent.set("location", locationFilter);
      wideEvent.set("companyCode", companyCode);
      wideEvent.set("conversion", tsUserDocument?.conversion || "both");
      // Include the same data as MongoDB log entry
      wideEvent.set("queries", queries);
      wideEvent.set("pollCount", pollingResultsBuffer.length);
      wideEvent.set("elapsedSeconds", elapsedSeconds);
      wideEvent.set("timeRange", `${pollingResultsBuffer[0].timestamp} to ${pollingResultsBuffer[pollingResultsBuffer.length - 1].timestamp}`);
      wideEvent.set("comparison", logData.comparison);
      // Add summary counts and timings
      wideEvent.addCount("pollCount", pollingResultsBuffer.length);
      wideEvent.addCount("identicalCount", identicalCount);
      wideEvent.addCount("mismatchCount", mismatchCount);
      wideEvent.addTiming("minTimeMs", minTime);
      wideEvent.addTiming("maxTimeMs", maxTime);
      wideEvent.addTiming("avgTimeMs", avgTime);
      // Browser/device info
      if (typeof window !== "undefined") {
        wideEvent.set("userAgent", navigator.userAgent);
        wideEvent.set("screenWidth", window.screen?.width);
        wideEvent.set("screenHeight", window.screen?.height);
        wideEvent.set("viewportWidth", window.innerWidth);
        wideEvent.set("viewportHeight", window.innerHeight);
      }
      wideEvent.send();

      // Clear buffer and reset timer
      setPollingResultsBuffer([]);
      setLastPollingLogTime(now);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pollingResultsBuffer, lastPollingLogTime]);

  // - - - - - - - - - -

  // Save buffered polling data before browser closes/refreshes
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (pollingResultsBuffer.length > 0) {
        // Calculate aggregated stats from buffer
        const executionTimes = pollingResultsBuffer.map(r => r.executionTime).filter(t => t !== undefined);
        const minTime = executionTimes.length > 0 ? Math.min(...executionTimes) : 0;
        const maxTime = executionTimes.length > 0 ? Math.max(...executionTimes) : 0;
        const avgTime = executionTimes.length > 0
          ? Math.round(executionTimes.reduce((sum, t) => sum + t, 0) / executionTimes.length)
          : 0;

        const timestamp = getEasternTimestamp();

        // Transform polling results buffer into queries array matching initial-load format
        const queries = pollingResultsBuffer.map(poll => ({
          query: poll.query || "getProjectsByCompany",
          result: `${poll.recordCount} projects`,
          realmExecutionTimeMs: null, // Polling doesn't use Realm execution time
          tanstackExecutionTimeMs: poll.executionTime,
          payloadSizeBytes: poll.payloadSizeBytes || 0,
          recordCount: poll.recordCount,
          maxLimit: MONGODB_MAXLIMIT_PROJECTSBYCOMPANY,
          capacityPercent: Math.round((poll.recordCount / MONGODB_MAXLIMIT_PROJECTSBYCOMPANY) * 100),
          // Include comparison data if available (from getProjectsByCompanyWithShared)
          identical: poll.comparison?.identical,
          note: poll.comparison?.note || "Polling query",
          timestamp: poll.timestamp,
          // Include comparison details if available
          ...(poll.comparison && {
            realmCount: poll.comparison.realmCount,
            tanstackCount: poll.comparison.tanstackCount,
            diffCount: poll.comparison.diffCount,
          }),
        }));

        // Calculate actual elapsed time for accurate activity tracking
        const elapsedSeconds = lastPollingLogTime
          ? Math.round((Date.now() - lastPollingLogTime) / 1000)
          : pollingResultsBuffer.length * 5; // Fallback if no lastPollingLogTime

        // Aggregate comparison stats from all polls in this batch
        const pollsWithComparison = pollingResultsBuffer.filter(p => p.comparison);
        const identicalCount = pollsWithComparison.filter(p => p.comparison.identical).length;
        const mismatchCount = pollsWithComparison.filter(p => !p.comparison.identical).length;
        const lastComparison = pollsWithComparison.length > 0
          ? pollsWithComparison[pollsWithComparison.length - 1].comparison
          : null;

        const logData = {
          type: "polling-partial",
          user: tsUserDocument?.username || "unknown",
          when: timestamp.when,
          whenDate: timestamp.whenDate,
          location: locationFilter || "unknown",
          queries: queries,
          pollCount: pollingResultsBuffer.length,
          elapsedSeconds, // Actual time this batch covers (for accurate activity time calculation)
          timeRange: `${pollingResultsBuffer[0].timestamp} to ${pollingResultsBuffer[pollingResultsBuffer.length - 1].timestamp}`,
          minTime,
          maxTime,
          avgTime,
          // Aggregated comparison summary for the batch
          comparison: lastComparison ? {
            identicalCount,
            mismatchCount,
            lastRealmCount: lastComparison.realmCount,
            lastTanstackCount: lastComparison.tanstackCount,
            lastIdentical: lastComparison.identical,
            lastNote: lastComparison.note,
          } : null,
        };

        console.log(
          "%cModelTsContext - Browser closing, sending partial polling log",
          "color: #9370DB; font-weight: bold; background-color: #1a1a1a; padding: 2px 4px;",
          logData
        );

        // Send batched log entry to server using fetch with keepalive
        // keepalive: true allows the request to complete even after page unload
        // This is the modern alternative to sendBeacon that supports custom headers
        const payload = {
          method: "createLogEntry",
          database: mongoDatabase,
          collection: MONGODB_COLLECTION_LOGS,
          document: logData,
        };

        try {
          fetch(MONGODB_AWSGW_MUTATIONS, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'x-api-key': MONGODB_AWSGW_APIKEY,
            },
            body: JSON.stringify(payload),
            keepalive: true, // Allows request to outlive the page
          });
        } catch (err) {
          // Silently fail - page is closing anyway
          console.log("Failed to send partial polling log:", err);
        }

        // Send wide event to Axiom for polling-partial (browser close)
        const wideEvent = createWideEvent("polling-partial");
        wideEvent.set("_appVersion", APP_VERSION);
        wideEvent.set("userDocument", tsUserDocument);
        wideEvent.set("location", locationFilter);
        wideEvent.set("companyCode", companyCode);
        wideEvent.set("conversion", tsUserDocument?.conversion || "both");
        // Include the same data as MongoDB log entry
        wideEvent.set("queries", queries);
        wideEvent.set("pollCount", pollingResultsBuffer.length);
        wideEvent.set("elapsedSeconds", elapsedSeconds);
        wideEvent.set("timeRange", `${pollingResultsBuffer[0].timestamp} to ${pollingResultsBuffer[pollingResultsBuffer.length - 1].timestamp}`);
        wideEvent.set("comparison", logData.comparison);
        // Add summary counts and timings
        wideEvent.addCount("pollCount", pollingResultsBuffer.length);
        wideEvent.addCount("identicalCount", identicalCount);
        wideEvent.addCount("mismatchCount", mismatchCount);
        wideEvent.addTiming("minTimeMs", minTime);
        wideEvent.addTiming("maxTimeMs", maxTime);
        wideEvent.addTiming("avgTimeMs", avgTime);
        // Browser/device info
        if (typeof window !== "undefined") {
          wideEvent.set("userAgent", navigator.userAgent);
          wideEvent.set("screenWidth", window.screen?.width);
          wideEvent.set("screenHeight", window.screen?.height);
          wideEvent.set("viewportWidth", window.innerWidth);
          wideEvent.set("viewportHeight", window.innerHeight);
        }
        wideEvent.send();
      }

      // Also send attributes polling buffer if it has data
      if (attributesPollingResultsBuffer.length > 0) {
        const executionTimes = attributesPollingResultsBuffer.map(r => r.executionTime).filter(t => t !== undefined);
        const minTime = executionTimes.length > 0 ? Math.min(...executionTimes) : 0;
        const maxTime = executionTimes.length > 0 ? Math.max(...executionTimes) : 0;
        const avgTime = executionTimes.length > 0
          ? Math.round(executionTimes.reduce((sum, t) => sum + t, 0) / executionTimes.length)
          : 0;

        const timestamp = getEasternTimestamp();

        const queries = attributesPollingResultsBuffer.map(poll => ({
          query: "getAttributesByLocation",
          result: `${poll.recordCount} attributes`,
          realmExecutionTimeMs: null,
          tanstackExecutionTimeMs: poll.executionTime,
          payloadSizeBytes: poll.payloadSizeBytes || 0,
          recordCount: poll.recordCount,
          maxLimit: MONGODB_MAXLIMIT_ATTRIBUTESBYLOCATION,
          capacityPercent: Math.round((poll.recordCount / MONGODB_MAXLIMIT_ATTRIBUTESBYLOCATION) * 100),
          identical: poll.comparison?.identical,
          note: poll.comparison?.note || "Polling query",
          timestamp: poll.timestamp,
          ...(poll.comparison && {
            realmCount: poll.comparison.realmCount,
            tanstackCount: poll.comparison.tanstackCount,
            diffCount: poll.comparison.diffCount,
          }),
        }));

        const elapsedSeconds = lastAttributesPollingLogTime
          ? Math.round((Date.now() - lastAttributesPollingLogTime) / 1000)
          : attributesPollingResultsBuffer.length * 10; // Fallback (10 seconds per poll)

        const pollsWithComparison = attributesPollingResultsBuffer.filter(p => p.comparison);
        const identicalCount = pollsWithComparison.filter(p => p.comparison.identical).length;
        const mismatchCount = pollsWithComparison.filter(p => !p.comparison.identical).length;
        const lastComparison = pollsWithComparison.length > 0
          ? pollsWithComparison[pollsWithComparison.length - 1].comparison
          : null;

        const attrLogData = {
          type: "attributes-polling-partial",
          user: tsUserDocument?.username || "unknown",
          when: timestamp.when,
          whenDate: timestamp.whenDate,
          location: locationFilter || "unknown",
          queries: queries,
          pollCount: attributesPollingResultsBuffer.length,
          elapsedSeconds,
          timeRange: `${attributesPollingResultsBuffer[0].timestamp} to ${attributesPollingResultsBuffer[attributesPollingResultsBuffer.length - 1].timestamp}`,
          minTime,
          maxTime,
          avgTime,
          comparison: lastComparison ? {
            identicalCount,
            mismatchCount,
            lastRealmCount: lastComparison.realmCount,
            lastTanstackCount: lastComparison.tanstackCount,
            lastIdentical: lastComparison.identical,
            lastNote: lastComparison.note,
          } : null,
        };

        console.log(
          "%cModelTsContext - Browser closing, sending partial ATTRIBUTES polling log",
          "color: #00CED1; font-weight: bold; background-color: #1a1a1a; padding: 2px 4px;",
          attrLogData
        );

        const attrPayload = {
          method: "createLogEntry",
          database: mongoDatabase,
          collection: MONGODB_COLLECTION_LOGS,
          document: attrLogData,
        };

        try {
          fetch(MONGODB_AWSGW_MUTATIONS, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'x-api-key': MONGODB_AWSGW_APIKEY,
            },
            body: JSON.stringify(attrPayload),
            keepalive: true,
          });
        } catch (err) {
          console.log("Failed to send partial attributes polling log:", err);
        }

        // Send wide event to Axiom for attributes-polling-partial (browser close)
        const attrWideEvent = createWideEvent("attributes-polling-partial");
        attrWideEvent.set("_appVersion", APP_VERSION);
        attrWideEvent.set("userDocument", tsUserDocument);
        attrWideEvent.set("location", locationFilter);
        attrWideEvent.set("companyCode", companyCode);
        attrWideEvent.set("conversion", tsUserDocument?.conversion || "both");
        // Include the same data as MongoDB log entry
        attrWideEvent.set("queries", queries);
        attrWideEvent.set("pollCount", attributesPollingResultsBuffer.length);
        attrWideEvent.set("elapsedSeconds", elapsedSeconds);
        attrWideEvent.set("timeRange", `${attributesPollingResultsBuffer[0].timestamp} to ${attributesPollingResultsBuffer[attributesPollingResultsBuffer.length - 1].timestamp}`);
        attrWideEvent.set("comparison", attrLogData.comparison);
        // Add summary counts and timings
        attrWideEvent.addCount("pollCount", attributesPollingResultsBuffer.length);
        attrWideEvent.addCount("identicalCount", identicalCount);
        attrWideEvent.addCount("mismatchCount", mismatchCount);
        attrWideEvent.addTiming("minTimeMs", minTime);
        attrWideEvent.addTiming("maxTimeMs", maxTime);
        attrWideEvent.addTiming("avgTimeMs", avgTime);
        // Browser/device info
        if (typeof window !== "undefined") {
          attrWideEvent.set("userAgent", navigator.userAgent);
          attrWideEvent.set("screenWidth", window.screen?.width);
          attrWideEvent.set("screenHeight", window.screen?.height);
          attrWideEvent.set("viewportWidth", window.innerWidth);
          attrWideEvent.set("viewportHeight", window.innerHeight);
        }
        attrWideEvent.send();
      }
    };

    // Listen for both beforeunload and pagehide for better browser coverage
    // pagehide is more reliable in Safari and mobile browsers
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('pagehide', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('pagehide', handleBeforeUnload);
    };
    // (#79) Intentionally NOT including companyCode/locationFilter/mongoDatabase/
    // tsUserDocument in deps. Location changes trigger a full data refresh via
    // a separate code path (onScheduleNeedsModel); adding these would cause
    // this effect to double-fire on every location change and double-write the
    // window unload listener. Confirmed intentional in #79's notes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pollingResultsBuffer, tsUserDocument?.username, lastPollingLogTime, attributesPollingResultsBuffer, lastAttributesPollingLogTime]);

  // - - - - - - - - - - - - - - - - -  ATTRIBUTES POLLING RESULT  - - - - - - - - - - - - - - - - - -
  //
  // Process attributes polling results - compare with Realm and store in buffer
  //

  useEffect(() => {
    if (
      attributesPollingResult &&
      Array.isArray(attributesPollingResult) &&
      !attributesPollingFetching &&
      shouldPollAttributes
    ) {
      const tanstackAttributes = attributesPollingResult || [];
      const executionTime = attributesPollingResult._executionTime;

      // Realm's attributesList is the current source of truth
      const realmAttributes = attributesList || [];

      // Calculate payload size
      const payloadSizeBytes = tanstackAttributes.length > 0
        ? new Blob([JSON.stringify(tanstackAttributes)]).size
        : 0;

      // Perform comparison between Realm and TanStack
      let comparison = {
        realmCount: realmAttributes.length,
        tanstackCount: tanstackAttributes.length,
        identical: false,
        diffCount: 0,
        note: "",
      };

      if (realmAttributes.length === 0) {
        comparison.note = "Realm attributesList empty - waiting for data";
      } else if (tanstackAttributes.length === 0) {
        comparison.note = "TanStack result empty";
      } else {
        // Compare by _id to find differences
        const realmIds = new Set(realmAttributes.map(a => a._id?.toString?.() || a._id));
        const tanstackIds = new Set(tanstackAttributes.map(a => a._id?.toString?.() || a._id));

        // Find attributes in Realm but not in TanStack
        const inRealmOnly = [...realmIds].filter(id => !tanstackIds.has(id));
        // Find attributes in TanStack but not in Realm
        const inTanstackOnly = [...tanstackIds].filter(id => !realmIds.has(id));

        comparison.diffCount = inRealmOnly.length + inTanstackOnly.length;
        comparison.identical = comparison.diffCount === 0 && realmAttributes.length === tanstackAttributes.length;

        if (comparison.identical) {
          comparison.note = "Data matches";
        } else {
          const notes = [];
          if (inRealmOnly.length > 0) notes.push(`${inRealmOnly.length} in Realm only`);
          if (inTanstackOnly.length > 0) notes.push(`${inTanstackOnly.length} in TanStack only`);
          if (realmAttributes.length !== tanstackAttributes.length) {
            notes.push(`count mismatch (${realmAttributes.length} vs ${tanstackAttributes.length})`);
          }
          comparison.note = notes.join("; ");
        }
      }

      console.log(
        "%cModelTsContext - ATTRIBUTES POLLING result received",
        "color: #00CED1; font-weight: bold; background-color: #1a1a1a; padding: 2px 4px;",
        {
          tanstackCount: tanstackAttributes.length,
          realmCount: realmAttributes.length,
          comparison,
          executionTime,
        }
      );

      // Add to attributes polling buffer WITH comparison data
      setAttributesPollingResultsBuffer(prev => [...prev, {
        timestamp: new Date().toISOString(),
        query: 'getAttributesByLocation',
        executionTime: executionTime,
        recordCount: tanstackAttributes.length,
        payloadSizeBytes: payloadSizeBytes,
        location: locationFilter,
        comparison: comparison,
      }]);

      // Update "Updated: X ago" timestamp for non-realmOnly users
      // This replaces the Realm change stream timestamp update
      if (tsUserDocument?.conversion !== "realmOnly") {
        setLatestChangeTS(new Date());
      }

      // Update awsgwAttributesResult from polling data to keep it fresh
      // This ensures findAttribute() and POST-POLL VERIFY use consistent data
      // NOTE: After initial load, THE BIG MOVE in schedule.context.js will skip
      // overwriting attributesList, so polling won't revert user's optimistic updates
      setAwsgwAttributesResult(tanstackAttributes);
      setAwsgwAttributesResultLocation(locationFilter); // Track which location this attributes data belongs to

      // Log override status if active
      if (attributesChangeStreamPollingOverride.active) {
        console.log(
          `%cModelTsContext - Attributes polling (change stream override: ${attributesChangeStreamPollingOverride.remainingPolls} polls remaining)`,
          "color: #9370DB; font-weight: bold; background-color: #1a1a1a; padding: 2px 4px;",
          {
            attributesChangeStreamOverride: attributesChangeStreamPollingOverride.active ? `${attributesChangeStreamPollingOverride.remainingPolls} polls remaining` : "inactive",
          }
        );

        // Decrement remaining polls in override mode
        if (attributesChangeStreamPollingOverride.active && attributesChangeStreamPollingOverride.remainingPolls > 0) {
          const newRemaining = attributesChangeStreamPollingOverride.remainingPolls - 1;
          if (newRemaining === 0) {
            console.log(
              "%cModelTsContext - Attributes change stream polling override complete, pausing polling",
              "color: #9370DB; font-weight: bold; background-color: #1a1a1a; padding: 2px 4px;"
            );
          }
          setAttributesChangeStreamPollingOverride(prev => ({
            ...prev,
            remainingPolls: newRemaining,
            active: newRemaining > 0,
          }));
        }
      }

      // POST-POLL VERIFY: Check pending attribute change stream verifications
      if (window._pendingAttributeChangeStreamVerifications && window._pendingAttributeChangeStreamVerifications.length > 0) {
        const pendingList = [...window._pendingAttributeChangeStreamVerifications];
        window._pendingAttributeChangeStreamVerifications = []; // Clear the queue

        console.log(
          `%c[POST-POLL VERIFY ATTRIBUTES] Checking ${pendingList.length} pending attribute change stream verification(s)...`,
          "color: #9370DB; font-weight: bold; background-color: #1a1a1a; padding: 2px 4px;"
        );

        // Initialize retry tracker if not exists
        if (!window._attributeChangeStreamRetryTracker) {
          window._attributeChangeStreamRetryTracker = {};
        }

        pendingList.forEach(pending => {
          const { uuid, payrollNo, operationType, realmDocument, timestamp, fieldsToCompare } = pending;
          const tsDocument = tanstackAttributes.find(a => a.id === uuid);

          // Initialize or get retry count for this uuid
          const trackerKey = uuid;
          if (!window._attributeChangeStreamRetryTracker[trackerKey]) {
            window._attributeChangeStreamRetryTracker[trackerKey] = { count: 0, firstSeen: timestamp };
          }
          const retryInfo = window._attributeChangeStreamRetryTracker[trackerKey];

          if (operationType === "insert") {
            // For INSERT, check if the document now exists in TanStack
            if (tsDocument) {
              console.log(
                `%c[POST-POLL VERIFY ATTRIBUTES] Attribute ${uuid} (payrollNo: ${payrollNo}) - NOW IN TANSTACK (INSERT verified)`,
                "color: #22c55e; font-weight: bold;"
              );
              // Clean up retry tracker
              delete window._attributeChangeStreamRetryTracker[trackerKey];
            } else {
              retryInfo.count++;
              if (retryInfo.count < 5) {
                console.log(
                  `%c[POST-POLL VERIFY ATTRIBUTES] Attribute ${uuid} (payrollNo: ${payrollNo}) - STILL NOT IN TANSTACK (retry ${retryInfo.count}/5)`,
                  "color: #FFA500; font-weight: bold;"
                );
                // Re-queue for next poll
                if (!window._pendingAttributeChangeStreamVerifications) {
                  window._pendingAttributeChangeStreamVerifications = [];
                }
                window._pendingAttributeChangeStreamVerifications.push(pending);
              } else {
                console.log(
                  `%c[POST-POLL VERIFY ATTRIBUTES] Attribute ${uuid} (payrollNo: ${payrollNo}) - PERSISTENT MISMATCH after 5 polls! INSERT still not in TanStack`,
                  "color: #ef4444; font-weight: bold;"
                );
                delete window._attributeChangeStreamRetryTracker[trackerKey];
              }
            }
          } else if (operationType === "replace" && tsDocument && fieldsToCompare) {
            // For REPLACE, compare specific fields
            const stillDiffers = [];
            const comparisons = [];

            fieldsToCompare.forEach(field => {
              const realmVal = realmDocument[field];
              const tsVal = tsDocument[field];
              const realmStr = JSON.stringify(realmVal);
              const tsStr = JSON.stringify(tsVal);
              const isMatch = realmStr === tsStr;

              comparisons.push({
                field,
                realm: realmVal ?? '(undefined)',
                tanstack: tsVal ?? '(undefined)',
                realmType: typeof realmVal,
                tsType: typeof tsVal,
                realmStr,
                tsStr,
                match: isMatch ? '✓' : '✗'
              });

              if (!isMatch) {
                stillDiffers.push(field);
              }
            });

            if (stillDiffers.length === 0) {
              console.log(
                `%c[POST-POLL VERIFY ATTRIBUTES] Attribute ${uuid} (payrollNo: ${payrollNo}) - TANSTACK NOW MATCHES REALM`,
                "color: #22c55e; font-weight: bold;"
              );
              console.table(comparisons);
              // Clean up retry tracker
              delete window._attributeChangeStreamRetryTracker[trackerKey];
            } else {
              retryInfo.count++;
              if (retryInfo.count < 5) {
                console.log(
                  `%c[POST-POLL VERIFY ATTRIBUTES] Attribute ${uuid} (payrollNo: ${payrollNo}) - STILL DIFFERS on ${stillDiffers.length} field(s) (retry ${retryInfo.count}/5): ${stillDiffers.join(', ')}`,
                  "color: #FFA500; font-weight: bold;"
                );
                console.table(comparisons);
                // Re-queue for next poll
                if (!window._pendingAttributeChangeStreamVerifications) {
                  window._pendingAttributeChangeStreamVerifications = [];
                }
                window._pendingAttributeChangeStreamVerifications.push(pending);
              } else {
                console.log(
                  `%c[POST-POLL VERIFY ATTRIBUTES] Attribute ${uuid} (payrollNo: ${payrollNo}) - PERSISTENT MISMATCH after 5 polls!`,
                  "color: #ef4444; font-weight: bold;"
                );
                console.table(comparisons);
                delete window._attributeChangeStreamRetryTracker[trackerKey];
              }
            }
          } else if (!tsDocument) {
            // Document not found in TanStack (unexpected for REPLACE)
            retryInfo.count++;
            if (retryInfo.count < 5) {
              console.log(
                `%c[POST-POLL VERIFY ATTRIBUTES] Attribute ${uuid} (payrollNo: ${payrollNo}) - NOT FOUND in TanStack (retry ${retryInfo.count}/5)`,
                "color: #FFA500; font-weight: bold;"
              );
              // Re-queue
              if (!window._pendingAttributeChangeStreamVerifications) {
                window._pendingAttributeChangeStreamVerifications = [];
              }
              window._pendingAttributeChangeStreamVerifications.push(pending);
            } else {
              console.log(
                `%c[POST-POLL VERIFY ATTRIBUTES] Attribute ${uuid} (payrollNo: ${payrollNo}) - PERSISTENT: Still not found after 5 polls`,
                "color: #ef4444; font-weight: bold;"
              );
              delete window._attributeChangeStreamRetryTracker[trackerKey];
            }
          }
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attributesPollingResult, attributesPollingFetching, attributesList, attributesChangeStreamPollingOverride]);

  // Batched Attributes Polling Log Entry - send accumulated polling results every N minutes
  useEffect(() => {
    if (attributesPollingResultsBuffer.length === 0) return;

    const now = Date.now();
    const batchIntervalMs = MONGODB_POLLING_LOG_BATCH_INTERVAL * 60 * 1000; // Convert minutes to milliseconds

    // Initialize lastAttributesPollingLogTime on first polling result
    if (lastAttributesPollingLogTime === null) {
      setLastAttributesPollingLogTime(now);
      return;
    }

    // Check if it's time to send batched log
    const timeSinceLastLog = now - lastAttributesPollingLogTime;
    if (timeSinceLastLog >= batchIntervalMs) {
      // Calculate aggregated stats from buffer
      const executionTimes = attributesPollingResultsBuffer.map(r => r.executionTime).filter(t => t !== undefined);
      const minTime = executionTimes.length > 0 ? Math.min(...executionTimes) : 0;
      const maxTime = executionTimes.length > 0 ? Math.max(...executionTimes) : 0;
      const avgTime = executionTimes.length > 0
        ? Math.round(executionTimes.reduce((sum, t) => sum + t, 0) / executionTimes.length)
        : 0;

      const timestamp = getEasternTimestamp();

      // Transform polling results buffer into queries array matching initial-load format
      const queries = attributesPollingResultsBuffer.map(poll => ({
        query: "getAttributesByLocation",
        result: `${poll.recordCount} attributes`,
        realmExecutionTimeMs: null, // Polling doesn't use Realm execution time
        tanstackExecutionTimeMs: poll.executionTime,
        payloadSizeBytes: poll.payloadSizeBytes || 0,
        recordCount: poll.recordCount,
        maxLimit: MONGODB_MAXLIMIT_ATTRIBUTESBYLOCATION,
        capacityPercent: Math.round((poll.recordCount / MONGODB_MAXLIMIT_ATTRIBUTESBYLOCATION) * 100),
        identical: poll.comparison?.identical,
        note: poll.comparison?.note || "Polling query",
        timestamp: poll.timestamp,
        ...(poll.comparison && {
          realmCount: poll.comparison.realmCount,
          tanstackCount: poll.comparison.tanstackCount,
          diffCount: poll.comparison.diffCount,
        }),
      }));

      // Calculate actual elapsed time for accurate activity tracking
      const elapsedSeconds = Math.round((now - lastAttributesPollingLogTime) / 1000);

      // Aggregate comparison stats from all polls in this batch
      const pollsWithComparison = attributesPollingResultsBuffer.filter(p => p.comparison);
      const identicalCount = pollsWithComparison.filter(p => p.comparison.identical).length;
      const mismatchCount = pollsWithComparison.filter(p => !p.comparison.identical).length;
      const lastComparison = pollsWithComparison.length > 0
        ? pollsWithComparison[pollsWithComparison.length - 1].comparison
        : null;

      const logData = {
        type: "attributes-polling-batch",
        user: tsUserDocument?.username || "unknown",
        when: timestamp.when,
        whenDate: timestamp.whenDate,
        location: locationFilter || "unknown",
        queries: queries,
        pollCount: attributesPollingResultsBuffer.length,
        elapsedSeconds,
        timeRange: `${attributesPollingResultsBuffer[0].timestamp} to ${attributesPollingResultsBuffer[attributesPollingResultsBuffer.length - 1].timestamp}`,
        minTime,
        maxTime,
        avgTime,
        comparison: lastComparison ? {
          identicalCount,
          mismatchCount,
          lastRealmCount: lastComparison.realmCount,
          lastTanstackCount: lastComparison.tanstackCount,
          lastIdentical: lastComparison.identical,
          lastNote: lastComparison.note,
        } : null,
      };

      console.log(
        "%cModelTsContext - Sending batched ATTRIBUTES polling log",
        "color: #00CED1; font-weight: bold; background-color: #1a1a1a; padding: 2px 4px;",
        logData
      );

      // Send batched log entry to server
      const payload = {
        method: "createLogEntry",
        database: mongoDatabase,
        collection: MONGODB_COLLECTION_LOGS,
        document: logData,
      };

      fetch(MONGODB_AWSGW_MUTATIONS, {
        method: "POST",
        mode: "cors",
        credentials: "omit",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "x-api-key": MONGODB_AWSGW_APIKEY,
        },
        body: JSON.stringify(payload),
      })
        .then(response => response.json())
        .then(result => {
          console.log(
            "%cModelTsContext - Batched ATTRIBUTES polling log sent successfully",
            "color: #00CED1; font-weight: bold; background-color: #1a1a1a; padding: 2px 4px;",
            result
          );
        })
        .catch(error => {
          console.error("Error sending batched ATTRIBUTES polling log:", error);
        });

      // Send wide event to Axiom for attributes-polling-batch
      const wideEvent = createWideEvent("attributes-polling-batch");
      wideEvent.set("_appVersion", APP_VERSION);
      wideEvent.set("userDocument", tsUserDocument);
      wideEvent.set("location", locationFilter);
      wideEvent.set("companyCode", companyCode);
      wideEvent.set("conversion", tsUserDocument?.conversion || "both");
      // Include the same data as MongoDB log entry
      wideEvent.set("queries", queries);
      wideEvent.set("pollCount", attributesPollingResultsBuffer.length);
      wideEvent.set("elapsedSeconds", elapsedSeconds);
      wideEvent.set("timeRange", `${attributesPollingResultsBuffer[0].timestamp} to ${attributesPollingResultsBuffer[attributesPollingResultsBuffer.length - 1].timestamp}`);
      wideEvent.set("comparison", logData.comparison);
      // Add summary counts and timings
      wideEvent.addCount("pollCount", attributesPollingResultsBuffer.length);
      wideEvent.addCount("identicalCount", identicalCount);
      wideEvent.addCount("mismatchCount", mismatchCount);
      wideEvent.addTiming("minTimeMs", minTime);
      wideEvent.addTiming("maxTimeMs", maxTime);
      wideEvent.addTiming("avgTimeMs", avgTime);
      // Browser/device info
      if (typeof window !== "undefined") {
        wideEvent.set("userAgent", navigator.userAgent);
        wideEvent.set("screenWidth", window.screen?.width);
        wideEvent.set("screenHeight", window.screen?.height);
        wideEvent.set("viewportWidth", window.innerWidth);
        wideEvent.set("viewportHeight", window.innerHeight);
      }
      wideEvent.send();

      // Clear buffer and reset timer
      setAttributesPollingResultsBuffer([]);
      setLastAttributesPollingLogTime(now);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attributesPollingResultsBuffer, lastAttributesPollingLogTime]);

  //
  // - - - - - - - - - - candidates
  //

  useEffect(() => {
    if (candidatesLoading) {
      console.log(
        "%cModelTsContext useEffect[candidatesLoading] triggered...",
        "color: #eaff00ff; font-weight: bold; background-color: #1a1a1a; padding: 2px 4px;"
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candidatesLoading]);

  // - - - - - - - - - -

  useEffect(() => {
    if (candidatesError) {
      console.log(
        "%cModelTsContext useEffect[candidatesError] ERROR: ",
        "color: #ff8400ff; font-weight: bold; background-color: #1a1a1a; padding: 2px 4px;",
        candidatesError
      );

      // Track failed query for UI feedback
      setFailedQueries(prev => new Set([...prev, "candidates"]));

      // Auto-retry after 5 seconds if not already retrying
      if (!retryingQueries.has("candidates")) {
        setRetryingQueries(prev => new Set([...prev, "candidates"]));
        console.log(
          "%cModelTsContext - candidates query failed, auto-retrying in 5 seconds...",
          "color: #FFA500; font-weight: bold; background-color: #1a1a1a; padding: 2px 4px;"
        );
        setTimeout(() => {
          refetchCandidates();
          setRetryingQueries(prev => {
            const updated = new Set(prev);
            updated.delete("candidates");
            return updated;
          });
        }, 5000);
      }
    } else if (!candidatesError && candidatesResult) {
      // Query succeeded - clear from failed queries
      setFailedQueries(prev => {
        const updated = new Set(prev);
        updated.delete("candidates");
        return updated;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candidatesError, candidatesResult]);

  // - - - - - - - - - -

  useEffect(() => {
    if (
      candidatesResult &&
      Object.keys(candidatesResult).length > 0 &&
      !candidatesFetching
    ) {
      // Extract the sorted list and queryPayrollListArray from the result
      const { sortedList, queryPayrollListArray } = candidatesResult;

      // Validate that data matches current location (prevent stale data on location switch)
      // Candidates can match via cLocationCode OR Additional_Locations (shared installers)
      // The Lambda query returns: cLocationCode === locationFilter OR Additional_Locations contains locationFilter
      const candidateMatchesLocation = (candidate) => {
        if (candidate?.cLocationCode === locationFilter) return true;
        // Shared installers have Additional_Locations field that might contain this location
        const additionalLocs = candidate?.Additional_Locations || "";
        return additionalLocs.toLowerCase().includes(locationFilter.toLowerCase());
      };
      const sampleCandidates = sortedList.slice(0, 10); // Check more candidates to catch shared installers
      const dataMatchesLocation = sampleCandidates.length > 0 && sampleCandidates.every(candidateMatchesLocation);

      // On initial load (before any location changes), accept data regardless of location match
      // After first location change, only accept data that matches current location
      if (!dataMatchesLocation && hasHadLocationChange.current) {
        const sampleInfo = sampleCandidates.slice(0, 3).map(c => ({
          cLocationCode: c?.cLocationCode,
          Additional_Locations: c?.Additional_Locations
        }));
        console.log(
          "%cModelTsContext - candidatesResult REJECTED - location mismatch, waiting for fresh data",
          "color: #FF0000; font-weight: bold; background-color: #1a1a1a; padding: 2px 4px;",
          { expected: locationFilter, sampleCandidates: sampleInfo }
        );
        return; // Don't proceed if data is rejected
      }

      setAwsgwCandidatesResult(sortedList);
      setAwsgwCandidatesResultLocation(locationFilter); // Track which location this candidates data belongs to
      // Don't auto-reset shouldFetchCandidates - let caller control when to stop polling

      // Clear from failedQueries since query succeeded (handles retry success case)
      setFailedQueries(prev => {
        if (prev.has("candidates")) {
          const updated = new Set(prev);
          updated.delete("candidates");
          console.log(
            "%cModelTsContext - Cleared candidates from failedQueries (query succeeded)",
            "color: #22c55e; font-weight: bold;"
          );
          return updated;
        }
        return prev;
      });

      // Mark query as completed for initial-load tracking (regardless of comparison)
      if (!hasCompletedCandidatesList.current) {
        hasCompletedCandidatesList.current = true;
        setCompletedQueries((prev) => {
          const updated = new Set(prev);
          updated.add("candidatesList");
          return updated;
        });
      }

      // Set the queryPayrollListArray for shared installers
      if (queryPayrollListArray && queryPayrollListArray.length > 0) {
        const queryListString = queryPayrollListArray.join(',');

        // Only update if the list has actually changed (prevent duplicate queries from same data)
        if (queryListString !== expectedProjectQueryList.current) {
          console.log(
            `%cModelTsContext AWSGW_getCandidatesByCompany projectQueryList: `,
            "color: #00BFFF; font-weight: bold;",
            queryPayrollListArray
          );
          projectQueryGeneration.current += 1; // Increment generation
          expectedGeneration.current = projectQueryGeneration.current; // Save what we're expecting
          setQueryTimestamp(Date.now()); // Update timestamp to create unique queryKey
          setProjectQueryList(queryPayrollListArray);
          expectedProjectQueryList.current = queryListString; // Track what we're expecting
        } else {
          console.log(
            `%cModelTsContext AWSGW_getCandidatesByCompany - SKIPPED setting projectQueryList (unchanged)`,
            "color: #FFA500; font-weight: bold; background-color: #1a1a1a; padding: 2px 4px;",
            queryPayrollListArray
          );
        }

        // Add projectsAfterMerge to expected queries since we'll be merging projects
        setExpectedQueries((prev) => {
          const updated = new Set(prev);
          updated.add("projectsAfterMerge");
          // console.log(
          //   "%cModelTsContext - Adding projectsAfterMerge to expected queries:",
          //   "color: #00FFFF; font-weight: bold; background-color: #1a1a1a; padding: 2px 4px;",
          //   [...updated]
          // );
          return updated;
        });
      } else if (queryPayrollListArray && queryPayrollListArray.length === 0) {
        // No shared installers - if projectsAfterMerge is in expected queries, mark it complete
        setCompletedQueries((prev) => {
          if (expectedQueries.has("projectsAfterMerge") && !prev.has("projectsAfterMerge")) {
            const updated = new Set(prev);
            updated.add("projectsAfterMerge");
            console.log(
              "%cModelTsContext - Query marked complete (no shared installers):",
              "color: #FFD700; font-weight: bold;",
              "projectsAfterMerge",
              "| Total completed:",
              updated.size
            );
            return updated;
          }
          return prev;
        });
      }

      // Realm comparison removed - Realm EOL Jan 13, 2026
      // All users now use TanStack data exclusively
      console.log(
        "%cModelTsContext - candidates loaded (TanStack)",
        "color: #00FF00; font-weight: bold;"
      );
      const logEntry = {
        query: "getCandidatesByCompany",
        result: `${sortedList?.length || 0} candidates`,
        realmExecutionTimeMs: null,
        tanstackExecutionTimeMs: queryExecutionTimes.getCandidatesByCompany || null,
        match: null,
        note: "TanStack data source (Realm EOL)",
      };
      setConsolidatedLogQueries((prev) => [...prev, logEntry]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    candidatesResult,
    candidatesList,
    queryExecutionTimes.getCandidatesByCompany,
    // realmQueryExecutionTimes.getCandidatesByCompany removed - Realm EOL
    candidatesFetching,
  ]);

  //
  // - - - - - - - - - - candidatesAdditional
  //

  useEffect(() => {
    if (candidatesAdditionalLoading) {
      console.log(
        "%cModelTsContext useEffect[candidatesAdditionalLoading] triggered...",
        "color: #eaff00ff; font-weight: bold; background-color: #1a1a1a; padding: 2px 4px;"
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candidatesAdditionalLoading]);

  // - - - - - - - - - -

  useEffect(() => {
    if (candidatesAdditionalError) {
      console.log(
        "%cModelTsContext useEffect[candidatesError] ERROR: ",
        "color: #ff8400ff; font-weight: bold; background-color: #1a1a1a; padding: 2px 4px;",
        candidatesAdditionalError
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candidatesAdditionalError]);

  // - - - - - - - - - -

  useEffect(() => {
    if (
      candidatesAdditionalResult &&
      Array.isArray(candidatesAdditionalResult) &&
      candidatesAdditionalResult.length > 0 &&
      !candidatesAdditionalFetching
    ) {
      setAwsgwCandidatesAdditionalResult(candidatesAdditionalResult);
      // Don't auto-reset shouldFetchCandidatesAdditional - let caller control when to stop polling

      // Merge additional candidates with existing candidatesResult
      // NOTE: concatenating multiple arrays: https://stackoverflow.com/questions/5080028/what-is-the-most-efficient-way-to-concatenate-n-arrays
      let newCandidatesList = [...awsgwCandidatesResult, ...candidatesAdditionalResult];
      console.log(
        `%cModelTsContext (${mongoDatabase}) useEffect[candidatesAdditionalResult] merged candidates (${awsgwCandidatesResult.length} + ${candidatesAdditionalResult.length} = ${newCandidatesList.length}): `,
        "color: #00BFFF; font-weight: bold;",
        newCandidatesList
      );
      // Sort the resulting list
      newCandidatesList = myJsonArraySort(
        newCandidatesList,
        "payrollNo",
        "Number",
        "ascending"
      );
      newCandidatesList = myJsonArraySort(
        newCandidatesList,
        "Scheduling_Surface",
        "String/Text",
        "descending"
      );
      setAwsgwCandidatesResult(newCandidatesList);
      setAwsgwCandidatesResultLocation(locationFilter); // Track which location this candidates data belongs to

      // Create log entry with payload, capacity, and durations (no data comparison)
      const executionTime = queryExecutionTimes.getCandidatesByPayrollNoList || 0;
      // realmExecutionTime removed - Realm EOL Jan 13, 2026

      if (executionTime > 0 && !hasLoggedCandidatesAdditional.current) {
        hasLoggedCandidatesAdditional.current = true;

        const payloadSizeBytes = new Blob([JSON.stringify(candidatesAdditionalResult)]).size;
        const capacityPercent = Math.round((candidatesAdditionalResult.length / MONGODB_MAXLIMIT_CANDIDATESBYPAYROLLNOLIST) * 100);
        const logEntry = {
          query: "candidatesAdditional",
          result: `${candidatesAdditionalResult.length} candidates`,
          realmExecutionTimeMs: null, // Realm EOL
          tanstackExecutionTimeMs: executionTime,
          payloadSizeBytes: payloadSizeBytes,
          recordCount: candidatesAdditionalResult.length,
          maxLimit: MONGODB_MAXLIMIT_CANDIDATESBYPAYROLLNOLIST,
          capacityPercent: capacityPercent,
          match: null, // Not compared
          note: "Additional candidates for shared installers (Realm EOL)",
        };
        setConsolidatedLogQueries((prev) => [...prev, logEntry]);
      }
    } else if (
      candidatesAdditionalResult &&
      Array.isArray(candidatesAdditionalResult) &&
      candidatesAdditionalResult.length === 0 &&
      !candidatesAdditionalFetching &&
      candidatesAdditionalQueryList.length > 0
    ) {
      // Query was made but returned 0 results
      const executionTime = queryExecutionTimes.getCandidatesByPayrollNoList || 0;
      // realmExecutionTime removed - Realm EOL Jan 13, 2026

      if (executionTime > 0 && !hasLoggedCandidatesAdditional.current) {
        hasLoggedCandidatesAdditional.current = true;

        const logEntry = {
          query: "candidatesAdditional",
          result: "No additional candidates found",
          realmExecutionTimeMs: null, // Realm EOL
          tanstackExecutionTimeMs: executionTime,
          payloadSizeBytes: 0,
          recordCount: 0,
          maxLimit: MONGODB_MAXLIMIT_CANDIDATESBYPAYROLLNOLIST,
          capacityPercent: 0,
          match: null,
          note: "Shared installers detected but no additional candidates found (Realm EOL)",
        };
        setConsolidatedLogQueries((prev) => [...prev, logEntry]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    candidatesAdditionalResult,
    candidatesAdditionalQueryList,
    queryExecutionTimes.getCandidatesByPayrollNoList,
    // realmQueryExecutionTimes.getCandidatesByPayrollNoList removed - Realm EOL
    candidatesAdditionalFetching,
  ]);

  //
  // - - - - - - - - - - projectsAdditional
  //

  useEffect(() => {
    if (projectsAdditionalLoading) {
      console.log(
        "%cModelTsContext useEffect[projectsAdditionalLoading] triggered...",
        "color: #eaff00ff; font-weight: bold; background-color: #1a1a1a; padding: 2px 4px;"
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectsAdditionalLoading]);

  // - - - - - - - - - -

  useEffect(() => {
    if (projectsAdditionalError) {
      console.log(
        "%cModelTsContext useEffect[projectsAdditionalError] ERROR: ",
        "color: #ff8400ff; font-weight: bold; background-color: #1a1a1a; padding: 2px 4px;",
        projectsAdditionalError
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectsAdditionalError]);

  // - - - - - - - - - -

  useEffect(() => {
    if (
      projectsAdditionalResult &&
      Array.isArray(projectsAdditionalResult) &&
      projectsAdditionalResult.length > 0 &&
      !projectsAdditionalFetching &&
      projectQueryList.length > 0  // Only process if we have a valid projectQueryList (not cleared during location change)
    ) {
      // Get metadata from the query result itself (not from current refs which may have changed)
      const metadata = projectsAdditionalResult._queryMetadata;
      const queryGeneration = metadata?.generation;
      // (#79) queryLocation + dataMatchesLocation removed — never used in this
      // block. Cross-location projects are expected here, so location-matching
      // logic doesn't apply (see comment ~10 lines below).

      // Check if this query result is from the current generation
      // Generation increments each time projectQueryList is set, so stale queries will have old generation
      const currentGeneration = projectQueryGeneration.current;

      if (queryGeneration !== undefined && currentGeneration !== queryGeneration) {
        return; // Don't process stale query results from previous generations
      }

      // Get metadata timestamp to check if this is the same query result we already processed
      const resultTimestamp = metadata?.timestamp;
      
      // This query returns cross-location projects for shared installers - location variation is expected and correct
      // Only set if this is a NEW query result (prevent infinite loop from select() re-running)
      if (lastSetAdditionalTimestamp.current !== resultTimestamp) {
        lastSetAdditionalTimestamp.current = resultTimestamp;
        tanstackAdditionalLocation.current = locationFilter;
        setAwsgwProjectsAdditionalResult(projectsAdditionalResult);
      }
    } else if (
      projectsAdditionalResult &&
      Array.isArray(projectsAdditionalResult) &&
      projectsAdditionalResult.length === 0 &&
      !projectsAdditionalFetching &&
      projectQueryList.length > 0 &&
      expectedQueries.has("projectsAfterMerge") &&
      !completedQueries.has("projectsAfterMerge")  // Only run if not already completed
    ) {
      // Shared installers were detected but query returned 0 results
      // Mark projectsAfterMerge as complete with a note
      console.log(
        "%cModelTsContext useEffect[projectsAdditionalResult] - No additional projects found for shared installers, marking complete",
        "color: #FFA500; font-weight: bold; background-color: #1a1a1a; padding: 2px 4px;"
      );

      // Set empty array to trigger the merge effect (which will just use base projects)
      setAwsgwProjectsAdditionalResult([]);

      const logEntry = {
        query: "projectsAfterMerge",
        result: "No additional projects found",
        realmExecutionTimeMs: null, // Realm EOL
        tanstackExecutionTimeMs:
          queryExecutionTimes.getProjectsByPayrollNoList || 0,
        match: true,
        note: "Shared installers detected but no projects at other locations (Realm EOL)",
      };

      setConsolidatedLogQueries((prev) => [...prev, logEntry]);
      setCompletedQueries((prev) => new Set([...prev, "projectsAfterMerge"]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectsAdditionalResult, projectsAdditionalFetching, projectQueryList, locationFilter]);

  // - - - - - - - - - -
  // Capture Realm's requestedProjectsList before it gets cleared
  // Only capture if we don't already have data to prevent re-capturing stale data after location change

  useEffect(() => {
    if (
      requestedProjectsList &&
      requestedProjectsList.length > 0 &&
      realmRequestedProjectsList.length === 0
    ) {
      setRealmRequestedProjectsList([...requestedProjectsList]);
      realmDataLocation.current = locationFilter; // Track which location this Realm data is from
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestedProjectsList]); // Removed locationFilter to prevent re-capture of stale data on location change

  //
  // - - - - - - - - - - attributes
  //

  useEffect(() => {
    if (attributesLoading) {
      console.log(
        "%cModelTsContext useEffect[attributesLoading] triggered...",
        "color: #eaff00ff; font-weight: bold; background-color: #1a1a1a; padding: 2px 4px;"
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attributesLoading]);

  // - - - - - - - - - -

  useEffect(() => {
    if (attributesError) {
      console.log(
        "%cModelTsContext useEffect[attributesError] ERROR: ",
        "color: #ff8400ff; font-weight: bold; background-color: #1a1a1a; padding: 2px 4px;",
        attributesError
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attributesError]);

  // - - - - - - - - - -

  useEffect(() => {
    if (attributesResult && Object.keys(attributesResult).length > 0) {
      // Validate that data matches current location (prevent stale data on location switch)
      // Attributes have location field - check first few to verify location match
      const sampleLocations = attributesResult.slice(0, 3).map(a => a?.location);
      const dataMatchesLocation = sampleLocations.length > 0 && sampleLocations.every(loc => loc === locationFilter);

      // On initial load (before any location changes), accept data regardless of location match
      // After first location change, only accept data that matches current location
      if (!dataMatchesLocation && hasHadLocationChange.current) {
        console.log(
          "%cModelTsContext - attributesResult REJECTED - location mismatch, waiting for fresh data",
          "color: #FF0000; font-weight: bold; background-color: #1a1a1a; padding: 2px 4px;",
          { expected: locationFilter, got: sampleLocations }
        );
        return; // Don't proceed if data is rejected
      }

      // Only set awsgwAttributesResult during initial load (before polling takes over)
      // After initial load, the polling effect (attributesPollingResult) handles data updates
      // This prevents stale initial-load data from overwriting fresh polling data
      // when this effect re-runs due to attributesList dependency changes
      if (!hasCompletedAttributes.current) {
        setAwsgwAttributesResult(attributesResult);
        setAwsgwAttributesResultLocation(locationFilter); // Track which location this attributes data belongs to
      }
      // Don't auto-reset shouldFetchAttributes - let caller control when to stop polling

      // Mark query as completed for initial-load tracking (regardless of comparison)
      if (!hasCompletedAttributes.current) {
        hasCompletedAttributes.current = true;
        setAttributesInitialLoadComplete(true); // Signal to THE BIG MOVE to stop overwriting attributesList
        setCompletedQueries((prev) => {
          const updated = new Set(prev);
          updated.add("attributes");
          return updated;
        });
      }

      // Realm comparison removed - Realm EOL Jan 13, 2026
      // All users now use TanStack data exclusively
      console.log(
        "%cModelTsContext - attributes loaded (TanStack)",
        "color: #00FF00; font-weight: bold;"
      );
      const logEntry = {
        query: "getAttributesByLocation",
        result: `${attributesResult?.length || 0} attributes`,
        realmExecutionTimeMs: null,
        tanstackExecutionTimeMs: queryExecutionTimes.getAttributesByLocation || null,
        match: null,
        note: "TanStack data source (Realm EOL)",
      };
      setConsolidatedLogQueries((prev) => [...prev, logEntry]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    attributesResult,
    attributesList,
    queryExecutionTimes.getAttributesByLocation,
    // realmQueryExecutionTimes.getAttributesByLocation removed - Realm EOL
  ]);

  //
  // - - - - - - - - - - logs
  //

  useEffect(() => {
    if (logsLoading) {
      console.log(
        "%cModelTsContext useEffect[logsLoading] triggered...",
        "color: #eaff00ff; font-weight: bold; background-color: #1a1a1a; padding: 2px 4px;"
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [logsLoading]);

  // - - - - - - - - - -

  useEffect(() => {
    if (logsError) {
      console.log(
        "%cModelTsContext useEffect[logsError] ERROR: ",
        "color: #ff8400ff; font-weight: bold; background-color: #1a1a1a; padding: 2px 4px;",
        logsError
      );
      
      // Mark logs query as completed even on error to prevent timeout
      if (!hasLoggedLogs.current) {
        hasLoggedLogs.current = true;
        
        const logEntry = {
          query: "logs",
          result: `Error: ${logsError.message || 'Unknown error'}`,
          realmExecutionTimeMs: 0,
          tanstackExecutionTimeMs: 0,
          match: null,
          note: "Query failed with error",
        };
        setConsolidatedLogQueries((prev) => [...prev, logEntry]);
        
        setCompletedQueries((prev) => {
          const updated = new Set(prev);
          updated.add("logs");
          // console.log(
          //   "%cModelTsContext - Query completed (with error):",
          //   "color: #FFD700; font-weight: bold;",
          //   "logs",
          //   "| Total completed:",
          //   updated.size
          // );
          return updated;
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [logsError]);

  // - - - - - - - - - -

  useEffect(() => {
    // Debug: trace this useEffect
    // console.log(
    //   "%c[DEBUG] logs useEffect triggered",
    //   "color: magenta; font-weight: bold",
    //   {
    //     hasLogsResult: !!logsResult,
    //     logsResultKeys: logsResult ? Object.keys(logsResult).length : 0,
    //     hasCompletedLogs: hasCompletedLogs.current,
    //     expectedQueriesHasLogs: expectedQueries.has("logs"),
    //     expectedQueriesArray: Array.from(expectedQueries),
    //   }
    // );

    if (logsResult && Object.keys(logsResult).length > 0) {
      const { logs, _executionTime } = logsResult;
      setAwsgwLogsResult(logs);
      // Don't auto-reset shouldFetchLogs - let caller control when to stop polling

      // Mark query as completed for initial-load tracking (regardless of log entry)
      // Only mark complete if logs is in expectedQueries (prevents premature completion before login)
      if (!hasCompletedLogs.current && expectedQueries.has("logs")) {
        // console.log(
        //   "%c[DEBUG] logs: MARKING COMPLETE",
        //   "color: lime; font-weight: bold"
        // );
        hasCompletedLogs.current = true;
        setCompletedQueries((prev) => {
          const updated = new Set(prev);
          updated.add("logs");
          return updated;
        });
      }
      // else {
      //   console.log(
      //     "%c[DEBUG] logs: NOT marking complete",
      //     "color: orange; font-weight: bold",
      //     {
      //       reason: hasCompletedLogs.current
      //         ? "already completed"
      //         : "logs not in expectedQueries",
      //     }
      //   );
      // }

      // Add to consolidated log (tracking execution time without comparison)
      const executionTime = _executionTime || 0;

      // console.log(
      //   "%c[DEBUG] logs: checking log entry creation",
      //   "color: yellow; font-weight: bold",
      //   {
      //     executionTime,
      //     hasLoggedLogs: hasLoggedLogs.current,
      //     expectedQueriesHasLogs: expectedQueries.has("logs"),
      //     willCreateLogEntry: executionTime > 0 && !hasLoggedLogs.current && expectedQueries.has("logs"),
      //   }
      // );

      // Only create log entry once when execution time is available
      if (executionTime > 0 && !hasLoggedLogs.current && expectedQueries.has("logs")) {
        // console.log(
        //   "%c[DEBUG] logs: CREATING LOG ENTRY",
        //   "color: lime; font-weight: bold"
        // );
        hasLoggedLogs.current = true;

        const payloadSizeBytes = new Blob([JSON.stringify(logs)]).size;
        const capacityPercent = Math.round((logs.length / MONGODB_MAXLIMIT_LOGS) * 100);
        const logEntry = {
          query: "logs",
          result: `${logs.length} log entries`,
          realmExecutionTimeMs: 0,
          tanstackExecutionTimeMs: executionTime,
          payloadSizeBytes: payloadSizeBytes,
          recordCount: logs.length,
          maxLimit: MONGODB_MAXLIMIT_LOGS,
          capacityPercent: capacityPercent,
          match: null, // Not compared
          note: "Admin-only list - no comparison performed",
        };
        setConsolidatedLogQueries((prev) => [...prev, logEntry]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [logsResult, expectedQueries]);

  // - - - - - - - - - -
  // Catch-up effect: When expectedQueries changes, check if data is already available
  // This handles the race condition where data arrives before expectedQueries is set
  // - - - - - - - - - -
  useEffect(() => {
    if (expectedQueries.size === 0) return; // No expected queries yet

    // console.log(
    //   "%c[DEBUG] expectedQueries CHANGED - checking for catch-up",
    //   "color: cyan; font-weight: bold",
    //   Array.from(expectedQueries)
    // );

    // Check user (getUserByEmail): comparison runs before expectedQueries is set
    // If comparison already ran, mark it complete now that expectedQueries includes "user"
    if (
      expectedQueries.has("user") &&
      hasCompletedUser.current
    ) {
      // console.log(
      //   "%c[DEBUG] CATCH-UP: Marking user complete (comparison already ran)",
      //   "color: lime; font-weight: bold"
      // );
      setCompletedQueries((prev) => {
        if (prev.has("user")) return prev; // Already added
        const updated = new Set(prev);
        updated.add("user");
        return updated;
      });
    }

    // Check logs: if data is already available and not yet marked complete
    if (
      expectedQueries.has("logs") &&
      !hasCompletedLogs.current &&
      logsResult &&
      Object.keys(logsResult).length > 0
    ) {
      // console.log(
      //   "%c[DEBUG] CATCH-UP: Marking logs complete (data already available)",
      //   "color: lime; font-weight: bold"
      // );
      hasCompletedLogs.current = true;
      setCompletedQueries((prev) => {
        const updated = new Set(prev);
        updated.add("logs");
        return updated;
      });
    }

    // Check users: if data is already available and not yet marked complete
    if (
      expectedQueries.has("users") &&
      !hasCompletedUsers.current &&
      usersResult &&
      Object.keys(usersResult).length > 0
    ) {
      // console.log(
      //   "%c[DEBUG] CATCH-UP: Marking users complete (data already available)",
      //   "color: lime; font-weight: bold"
      // );
      hasCompletedUsers.current = true;
      setCompletedQueries((prev) => {
        const updated = new Set(prev);
        updated.add("users");
        return updated;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expectedQueries]);

  //
  // - - - - - - - - - - trigger for stage: candidateResult -> projectsAdditional
  //

  useEffect(() => {
    // candidatesResult has been mapped and marked shared installers
    //    and added their payrollNo's to this list.
    //    So now query the list for their assigned jobs/projects
    //    and add them to the projectsList
    if (projectQueryList.length > 0) {
      // projectQueryList has been changed, so process the change
      awsgwGetProjectsByPayrollNoList();
      console.log(
        "%cModelTsContext (" +
          mongoDatabase +
          ") useEffect[projectQueryList(" +
          projectQueryList.length +
          ")]...(awsgwGetProjectsByPayrollNoList)",
        "color: #00BFFF; font-weight: bold;"
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectQueryList]);

  //
  // - - - - - - - - - - projectListAfterMerge (projectsListBeforeMerge + projectsAdditional)
  //

  useEffect(() => {
    // console.log(
    //   "%cModelTsContext - projectsAfterMerge useEffect TRIGGERED",
    //   "color: #FF00FF; font-weight: bold; background-color: #1a1a1a; padding: 2px 4px;",
    //   {
    //     awsgwProjectsResult: awsgwProjectsResult.length,
    //     awsgwProjectsAdditionalResult: awsgwProjectsAdditionalResult.length,
    //     realmRequestedProjectsList: realmRequestedProjectsList?.length || 0,
    //     realmExecutionTime: realmQueryExecutionTimes.getProjectsByPayrollNoList,
    //     tanstackExecutionTime: queryExecutionTimes.getProjectsByPayrollNoList,
    //     willMerge:
    //       awsgwProjectsResult.length > 0 &&
    //       awsgwProjectsAdditionalResult.length > 0,
    //     canCompare:
    //       realmRequestedProjectsList &&
    //       realmRequestedProjectsList.length > 0 &&
    //       realmQueryExecutionTimes.getProjectsByPayrollNoList,
    //   }
    // );
    // Set projectListAfterMerge when we have base projects
    // If there are additional (shared installer) projects, merge them; otherwise just use base projects
    if (awsgwProjectsResult.length > 0) {
      // Validate that the base data has projects from the current location
      // Additional projects are cross-location (for shared installers), so they won't match current location
      const baseSampleLocations = awsgwProjectsResult.slice(0, 3).map(p => p?.location);
      const baseDataMatchesLocation = baseSampleLocations.length > 0 && baseSampleLocations.every(loc => loc === locationFilter);

      // Skip validation on initial load - only validate after first location change
      // Only check base projects - additional projects are cross-location by design
      if (hasHadLocationChange.current && !baseDataMatchesLocation) {
        // Don't clear data - TanStack Query will update with fresh data when queries complete
        // Just skip this merge and wait for the next useEffect trigger with valid data
        return; // Don't merge or compare if locations don't match
      }

      // Merge base projects with additional (shared installer) projects if any exist
      var newProjectsList = [
        ...awsgwProjectsResult,
        ...awsgwProjectsAdditionalResult,
      ];
      console.log(
        `%cModelTsContext (${mongoDatabase}) useEffect[awsgwProjectsAdditionalResult(${awsgwProjectsAdditionalResult.length})] new combined ProjectsList(${newProjectsList.length}): `,
        "color: #00BFFF; font-weight: bold;",
        newProjectsList
      );
      setProjectListAfterMerge(newProjectsList);
      setProjectListAfterMergeLocation(locationFilter); // Track which location this data belongs to

      // Only compare if realmRequestedProjectsList (Realm shared installer projects) exists and Realm query has executed
      // AND validate that Realm data is from the current location (prevent stale data comparison)
      // FULL ISOLATION: Skip Realm comparison unless explicitly realmOnly (Realm is EOL)
      if (tsUserDocument?.conversion !== "realmOnly") {
        console.log(
          "%cModelTsContext - projectsAfterMerge comparison: n/a (tanstackOnly mode)",
          "color: #00FF00; font-weight: bold;"
        );
        // Add log entry for tanstackOnly users (no comparison, just log the result)
        const logEntry = {
          query: "getProjectsByPayrollNoList",
          result: `${newProjectsList?.length || 0} projects (merged)`,
          realmExecutionTimeMs: null,
          tanstackExecutionTimeMs: queryExecutionTimes.getProjectsByPayrollNoList || null,
          match: null,
          note: "TanStack data source (tanstackOnly) - shared installer projects merged",
        };
        setConsolidatedLogQueries((prev) => [...prev, logEntry]);
        // Realm comparison removed - Realm EOL Jan 13, 2026
        // Mark projectsAfterMerge as complete (TanStack is sole data source)
        if (expectedQueries.has("projectsAfterMerge")) {
          setCompletedQueries((prev) => {
            if (!prev.has("projectsAfterMerge")) {
              const updated = new Set(prev);
              updated.add("projectsAfterMerge");
              console.log(
                "%cModelTsContext - ADDING projectsAfterMerge to completedQueries (TanStack)",
                "color: #00FF00; font-weight: bold;"
              );
              return updated;
            }
            return prev;
          });
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    awsgwProjectsAdditionalResult,
    awsgwProjectsResult,
    projectsListBeforeMerge,
    // projectListAfterMerge removed - it's set inside this effect, would cause infinite loop
    realmRequestedProjectsList,
    queryExecutionTimes.getProjectsByPayrollNoList,
    // realmQueryExecutionTimes.getProjectsByPayrollNoList removed - Realm EOL
    locationFilter,
  ]);

  //
  // - - - - - - - - - - trigger for stage: candidateResult -> candidatesAdditional
  //

  useEffect(() => {
    if (candidatesAdditionalQueryList.length > 0) {
      // candidatesAdditionalQueryList has been changed, so process the change
      awsgwGetCandidatesByPayrollNoList();
      console.log(
        "%cModelTsContext (" +
          mongoDatabase +
          ") useEffect[candidatesAdditionalQueryList(" +
          candidatesAdditionalQueryList.length +
          ")]...(awsgwGetCandidatesByPayrollNoList)",
        "color: #00BFFF; font-weight: bold;"
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candidatesAdditionalQueryList]);

  //
  // - - - - - - - - - - trigger for stage: sharedInstallerAttributeDefaults
  //

  useEffect(() => {
    if (awsgwAttributesResult.length > 0) {
      // attributes are loaded, so handle defaultAttributes
      processSharedInstallerAttributeDefaults();
      console.log(
        `%cModelTsContext (${mongoDatabase}) useEffect[awsgwAttributesResult...] handle defaultAttributes`,
        "color: #00BFFF; font-weight: bold;"
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [awsgwAttributesResult]);

  //
  // - - - - - - - - - - SAVE CONSOLIDATED LOG ENTRY
  //

  useEffect(() => {
    // Only check if we have expected queries
    if (expectedQueries.size === 0) {
      return;
    }

    // Check if all expected queries have been completed
    const allQueriesComplete =
      expectedQueries.size > 0 &&
      completedQueries.size === expectedQueries.size &&
      [...expectedQueries].every((q) => completedQueries.has(q));

    // console.log(
    //   "%c[DEBUG] Consolidated Log Check:",
    //   "color: #FF00FF; font-weight: bold;",
    //   {
    //     expectedCount: expectedQueries.size,
    //     completedCount: completedQueries.size,
    //     logQueriesCount: consolidatedLogQueries.length,
    //     allQueriesComplete,
    //     expected: [...expectedQueries],
    //     completed: [...completedQueries],
    //     missing: [...expectedQueries].filter((q) => !completedQueries.has(q)),
    //   }
    // );

    if (allQueriesComplete && consolidatedLogQueries.length > 0) {
      // Clear timeout if all queries completed successfully
      if (consolidatedLogTimeout) {
        clearTimeout(consolidatedLogTimeout);
        setConsolidatedLogTimeout(null);
      }

      const timestamp = getEasternTimestamp();

      const consolidatedLogEntry = {
        user: tsUserDocument?.username || "unknown",
        type: "initial-load", // vs "polling" later
        when: timestamp.when,
        whenDate: timestamp.whenDate,
        location: locationFilter || "unknown",
        queries: consolidatedLogQueries,
        expectedQueries: [...expectedQueries],
        completedQueries: [...completedQueries],
      };

      // console.log(
      //   `%c📋 Consolidated Log Entry (ALL COMPLETE): ${consolidatedLogQueries.length} queries | Expected: ${expectedQueries.size} | Completed: ${completedQueries.size}`,
      //   "color: #00FF00; font-weight: bold; background-color: #1a1a1a; padding: 4px 8px;"
      // );

      createLogEntry.mutate(consolidatedLogEntry);

      // Send wide event to Axiom (mirrors MongoDB log entry + additional context)
      const wideEvent = createWideEvent("initial-load");
      wideEvent.set("_appVersion", APP_VERSION);
      wideEvent.set("userDocument", tsUserDocument);
      wideEvent.set("location", locationFilter);
      wideEvent.set("companyCode", companyCode);
      wideEvent.set("weekStartDate", weekDatesArray?.[0]);
      wideEvent.set("conversion", tsUserDocument?.conversion || "both");
      // Include the same data as MongoDB log entry
      wideEvent.set("queries", consolidatedLogQueries);
      wideEvent.set("expectedQueries", [...expectedQueries]);
      wideEvent.set("completedQueries", [...completedQueries]);
      // Add summary counts
      wideEvent.addCount("queryCount", consolidatedLogQueries.length);
      wideEvent.addCount("expectedQueryCount", expectedQueries.size);
      wideEvent.addCount("completedQueryCount", completedQueries.size);
      // Calculate total execution time across all queries
      const totalExecutionTimeMs = consolidatedLogQueries.reduce(
        (sum, q) => sum + (q.tanstackExecutionTimeMs || 0),
        0
      );
      wideEvent.addTiming("totalQueryExecutionTimeMs", totalExecutionTimeMs);
      // Browser/device info
      if (typeof window !== "undefined") {
        wideEvent.set("userAgent", navigator.userAgent);
        wideEvent.set("screenWidth", window.screen?.width);
        wideEvent.set("screenHeight", window.screen?.height);
        wideEvent.set("viewportWidth", window.innerWidth);
        wideEvent.set("viewportHeight", window.innerHeight);
      }
      wideEvent.send();

      // Reset for future polling entries
      setConsolidatedLogQueries([]);
      setExpectedQueries(new Set());
      setCompletedQueries(new Set());

      // Enable polling now that initial load is complete
      console.log(
        "%cModelTsContext - Initial load complete, enabling polling (projects + attributes)",
        "color: #9370DB; font-weight: bold; background-color: #1a1a1a; padding: 2px 4px;"
      );
      setShouldPollProjects(true);
      setShouldPollAttributes(true);

      // Update "Updated: X ago" timestamp for non-realmOnly users on initial load complete
      if (tsUserDocument?.conversion !== "realmOnly") {
        setLatestChangeTS(new Date());
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expectedQueries, completedQueries, consolidatedLogQueries]);

  // - - - - - - - - - -

  // Timeout mechanism: Save partial log if queries don't complete within 15 seconds
  // Resets timeout each time a query completes to give stragglers more time
  useEffect(() => {
    if (
      expectedQueries.size > 0 &&
      completedQueries.size < expectedQueries.size
    ) {
      // Clear any existing timeout
      if (consolidatedLogTimeout) {
        clearTimeout(consolidatedLogTimeout);
      }

      // Set new timeout - restarts on each query completion
      const timeoutId = setTimeout(() => {
        const missingQueries = [...expectedQueries].filter(
          (q) => !completedQueries.has(q)
        );

        if (missingQueries.length > 0 && consolidatedLogQueries.length > 0) {
          console.warn(
            `%c⚠️ TIMEOUT: Saving partial log after 15 seconds of inactivity. Missing queries: ${missingQueries.join(
              ", "
            )}`,
            "color: #FFA500; font-weight: bold; background-color: #1a1a1a; padding: 4px 8px;",
            {
              expectedCount: expectedQueries.size,
              completedCount: completedQueries.size,
              completedList: [...completedQueries],
              expectedList: [...expectedQueries],
              logQueriesCount: consolidatedLogQueries.length,
              missing: missingQueries,
            }
          );

          // Add missing queries to failedQueries so the GlobalLoadingIndicator shows them
          setFailedQueries(prev => {
            const updated = new Set(prev);
            missingQueries.forEach(q => updated.add(q));
            return updated;
          });

          // Auto-retry missing queries after 3 seconds
          setRetryingQueries(prev => {
            const updated = new Set(prev);
            missingQueries.forEach(q => updated.add(q));
            return updated;
          });

          console.log(
            `%cModelTsContext - Auto-retrying ${missingQueries.length} missing queries in 3 seconds: ${missingQueries.join(", ")}`,
            "color: #FFA500; font-weight: bold; background-color: #1a1a1a; padding: 2px 4px;"
          );

          // First reset flags to false (required for setShouldFetch(true) to trigger re-fetch)
          missingQueries.forEach(q => {
            if (q === "officeMetaData") {
              setShouldFetchOfficeMetaData(false);
            } else if (q === "uniqueLocations") {
              setShouldFetchUniqueLocations(false);
            } else if (q === "user") {
              setShouldFetchUser(false);
            } else if (q === "users") {
              setShouldFetchUsers(false);
            } else if (q === "projectsBeforeMerge") {
              setShouldFetchProjects(false);
            } else if (q === "candidatesList") {
              setShouldFetchCandidates(false);
            } else if (q === "attributes") {
              setShouldFetchAttributes(false);
            } else if (q === "logs") {
              setShouldFetchLogs(false);
            }
          });

          // Then set to true after a short delay to trigger re-fetch
          setTimeout(() => {
            missingQueries.forEach(q => {
              if (q === "officeMetaData") {
                setShouldFetchOfficeMetaData(true);
              } else if (q === "uniqueLocations") {
                setShouldFetchUniqueLocations(true);
              } else if (q === "user") {
                setShouldFetchUser(true);
              } else if (q === "users") {
                setShouldFetchUsers(true);
              } else if (q === "projectsBeforeMerge") {
                setShouldFetchProjects(true);
              } else if (q === "candidatesList") {
                setShouldFetchCandidates(true);
              } else if (q === "attributes") {
                setShouldFetchAttributes(true);
              } else if (q === "logs") {
                setShouldFetchLogs(true);
              }
            });
            // Clear retrying status after triggering refetch
            setRetryingQueries(prev => {
              const updated = new Set(prev);
              missingQueries.forEach(q => updated.delete(q));
              return updated;
            });
          }, 3000);

          const timestamp = getEasternTimestamp();

          const consolidatedLogEntry = {
            user: tsUserDocument?.username || "unknown",
            type: "initial-load-partial", // indicates incomplete
            when: timestamp.when,
            whenDate: timestamp.whenDate,
            location: locationFilter || "unknown",
            queries: consolidatedLogQueries,
            expectedQueries: [...expectedQueries],
            completedQueries: [...completedQueries],
            missingQueries: missingQueries,
            timeoutReason:
              "Some queries did not complete within 15 seconds of last completion",
          };

          createLogEntry.mutate(consolidatedLogEntry);

          // Send wide event to Axiom for partial/timeout load
          const wideEvent = createWideEvent("initial-load-partial");
          wideEvent.set("_appVersion", APP_VERSION);
          wideEvent.set("userDocument", tsUserDocument);
          wideEvent.set("location", locationFilter);
          wideEvent.set("companyCode", companyCode);
          wideEvent.set("weekStartDate", weekDatesArray?.[0]);
          wideEvent.set("conversion", tsUserDocument?.conversion || "both");
          // Include the same data as MongoDB log entry
          wideEvent.set("queries", consolidatedLogQueries);
          wideEvent.set("expectedQueries", [...expectedQueries]);
          wideEvent.set("completedQueries", [...completedQueries]);
          wideEvent.set("missingQueries", missingQueries);
          wideEvent.set("timeoutReason", "Some queries did not complete within 15 seconds of last completion");
          // Add summary counts
          wideEvent.addCount("queryCount", consolidatedLogQueries.length);
          wideEvent.addCount("expectedQueryCount", expectedQueries.size);
          wideEvent.addCount("completedQueryCount", completedQueries.size);
          wideEvent.addCount("missingQueryCount", missingQueries.length);
          // Calculate total execution time across completed queries
          const totalExecutionTimeMs = consolidatedLogQueries.reduce(
            (sum, q) => sum + (q.tanstackExecutionTimeMs || 0),
            0
          );
          wideEvent.addTiming("totalQueryExecutionTimeMs", totalExecutionTimeMs);
          // Browser/device info
          if (typeof window !== "undefined") {
            wideEvent.set("userAgent", navigator.userAgent);
            wideEvent.set("screenWidth", window.screen?.width);
            wideEvent.set("screenHeight", window.screen?.height);
            wideEvent.set("viewportWidth", window.innerWidth);
            wideEvent.set("viewportHeight", window.innerHeight);
          }
          wideEvent.send();

          // Reset
          setConsolidatedLogQueries([]);
          setExpectedQueries(new Set());
          setCompletedQueries(new Set());
          setConsolidatedLogTimeout(null);

          // Enable polling even after partial/timeout - initial load attempted
          console.log(
            "%cModelTsContext - Partial load complete (timeout), enabling polling (projects + attributes)",
            "color: #9370DB; font-weight: bold; background-color: #1a1a1a; padding: 2px 4px;"
          );
          setShouldPollProjects(true);
          setShouldPollAttributes(true);

          // Update "Updated: X ago" timestamp for non-realmOnly users on partial load complete
          if (tsUserDocument?.conversion !== "realmOnly") {
            setLatestChangeTS(new Date());
          }
        }
      }, 15000); // 15 second timeout from last activity

      setConsolidatedLogTimeout(timeoutId);

      // Cleanup on unmount
      return () => {
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expectedQueries, completedQueries]); // Restarts timeout each time completedQueries changes

  // - - - - - - - - - - - - - - - - -  DEBUG UTILITIES  - - - - - - - - - - - - - - - - - -
  //
  // Expose debug functions to window for console access during live troubleshooting
  // Usage from browser console:
  //   window.tsDebug.findInvoice(2206113)     - Find project by invoice number
  //   window.tsDebug.findInstaller("2735.02") - Find all projects for an installer
  //   window.tsDebug.getSharedInstallers()    - List all shared installers found
  //   window.tsDebug.getMeta()                - Get metadata from last polling result
  //   window.tsDebug.compareProjects()        - Compare TanStack vs Realm projects
  //
  useEffect(() => {
    window.tsDebug = {
      // Find a project by invoice number
      findInvoice: (invoiceNo) => {
        const projects = projectListAfterMerge || [];
        const found = projects.filter(p =>
          p.invoiceNo === invoiceNo ||
          String(p.invoiceNo) === String(invoiceNo)
        );
        if (found.length === 0) {
          console.log(`%c[tsDebug] Invoice ${invoiceNo} NOT FOUND in TanStack data`, "color: #ef4444; font-weight: bold;");
          console.log(`Total projects in TanStack: ${projects.length}`);
        } else {
          console.log(`%c[tsDebug] Invoice ${invoiceNo} FOUND:`, "color: #22c55e; font-weight: bold;", found);
        }
        return found;
      },

      // Find all projects for an installer (by payrollNo)
      findInstaller: (payrollNo) => {
        const projects = projectListAfterMerge || [];
        const normalizedPayroll = String(payrollNo).padStart(7, "0");
        const found = projects.filter(p =>
          p.installer === payrollNo ||
          p.installer === normalizedPayroll ||
          String(p.installer) === String(payrollNo)
        );
        console.log(`%c[tsDebug] Installer ${payrollNo} has ${found.length} projects:`, "color: #3b82f6; font-weight: bold;", found);
        return found;
      },

      // Get shared installers from last polling result
      getSharedInstallers: () => {
        const meta = projectsWithSharedResult?.meta || {};
        console.log(`%c[tsDebug] Shared Installers (${meta.sharedInstallerCount || 0}):`, "color: #9370DB; font-weight: bold;");
        console.table(meta.sharedInstallers || []);
        return meta.sharedInstallers || [];
      },

      // Get full metadata from last polling result
      getMeta: () => {
        const meta = projectsWithSharedResult?.meta || {};
        console.log("%c[tsDebug] Last polling metadata:", "color: #9370DB; font-weight: bold;", meta);
        return meta;
      },

      // Compare TanStack projects vs Realm projects
      compareProjects: () => {
        const tsProjects = projectListAfterMerge || [];
        const realmProjects = projectsList || [];

        const tsIds = new Set(tsProjects.map(p => p._id?.toString?.() || p._id));
        const realmIds = new Set(realmProjects.map(p => p._id?.toString?.() || p._id));

        const inTsOnly = tsProjects.filter(p => !realmIds.has(p._id?.toString?.() || p._id));
        const inRealmOnly = realmProjects.filter(p => !tsIds.has(p._id?.toString?.() || p._id));

        console.log("%c[tsDebug] Comparison Results:", "color: #FFA500; font-weight: bold;");
        console.log(`  TanStack total: ${tsProjects.length}`);
        console.log(`  Realm total: ${realmProjects.length}`);
        console.log(`  In TanStack only: ${inTsOnly.length}`, inTsOnly.map(p => ({ invoiceNo: p.invoiceNo, installer: p.installer, location: p.location })));
        console.log(`  In Realm only: ${inRealmOnly.length}`, inRealmOnly.map(p => ({ invoiceNo: p.invoiceNo, installer: p.installer, location: p.location })));

        return { inTsOnly, inRealmOnly, tsCount: tsProjects.length, realmCount: realmProjects.length };
      },

      // Get all projects (raw data)
      getAllProjects: () => {
        const projects = projectListAfterMerge || [];
        console.log(`%c[tsDebug] All ${projects.length} projects:`, "color: #3b82f6; font-weight: bold;", projects);
        return projects;
      },

      // Search projects by any field
      searchProjects: (field, value) => {
        const projects = projectListAfterMerge || [];
        const found = projects.filter(p => {
          const fieldValue = p[field];
          return fieldValue === value || String(fieldValue) === String(value);
        });
        console.log(`%c[tsDebug] Projects where ${field}="${value}":`, "color: #3b82f6; font-weight: bold;", found);
        return found;
      },

      // Get all attributes (both TanStack and Realm data)
      getAllAttributes: () => {
        const tsAttributes = awsgwAttributesResult || [];
        const realmAttributes = attributesList || [];
        console.log(`%c[tsDebug] Attributes - TanStack: ${tsAttributes.length}, Realm: ${realmAttributes.length}`, "color: #3b82f6; font-weight: bold;");
        return { tanstack: tsAttributes, realm: realmAttributes };
      },

      // Find attribute by uuid (id field) - searches both sources
      findAttribute: (uuid) => {
        const tsAttributes = awsgwAttributesResult || [];
        const realmAttributes = attributesList || [];
        const tsFound = tsAttributes.find(a => a.id === uuid);
        const realmFound = realmAttributes.find(a => a.id === uuid);

        console.log(`%c[tsDebug] Attribute ${uuid} lookup:`, "color: #FFA500; font-weight: bold;");

        if (tsFound) {
          console.log(`  %cTanStack: FOUND`, "color: #22c55e; font-weight: bold;", tsFound);
        } else {
          console.log(`  %cTanStack: NOT FOUND`, "color: #ef4444; font-weight: bold;");
        }

        if (realmFound) {
          console.log(`  %cRealm: FOUND`, "color: #22c55e; font-weight: bold;", realmFound);
        } else {
          console.log(`  %cRealm: NOT FOUND`, "color: #ef4444; font-weight: bold;");
        }

        // If both found, compare key fields (same fields as POST-POLL VERIFY)
        if (tsFound && realmFound) {
          const fieldsToCompare = ['locked', 'distant', 'expedite', '_kf_payrollNo', 'date', 'invoiceNo', 'encodedLargeTitle', 'encodedSmallTitle'];
          const differences = [];
          const comparisons = [];
          fieldsToCompare.forEach(field => {
            const tsVal = tsFound[field];
            const realmVal = realmFound[field];
            const tsStr = JSON.stringify(tsVal);
            const realmStr = JSON.stringify(realmVal);
            const isMatch = tsStr === realmStr;
            comparisons.push({
              field,
              tanstack: tsVal ?? '(undefined)',
              realm: realmVal ?? '(undefined)',
              match: isMatch ? '✓' : '✗'
            });
            if (!isMatch) {
              differences.push({ field, tanstack: tsVal, realm: realmVal });
            }
          });
          console.table(comparisons);
          if (differences.length > 0) {
            console.log(`  %cDIFFERENCES FOUND (${differences.length}):`, "color: #ef4444; font-weight: bold;", differences);
          } else {
            console.log(`  %cNo differences in key fields`, "color: #22c55e; font-weight: bold;");
          }
        }

        return { tanstack: tsFound, realm: realmFound };
      },

      // Compare TanStack attributes vs Realm attributes
      compareAttributes: () => {
        const tsAttributes = awsgwAttributesResult || [];
        const realmAttributes = attributesList || [];

        const tsIds = new Set(tsAttributes.map(a => a.id));
        const realmIds = new Set(realmAttributes.map(a => a.id));

        const inTsOnly = tsAttributes.filter(a => !realmIds.has(a.id));
        const inRealmOnly = realmAttributes.filter(a => !tsIds.has(a.id));

        console.log("%c[tsDebug] Attributes Comparison Results:", "color: #FFA500; font-weight: bold;");
        console.log(`  TanStack total: ${tsAttributes.length}`);
        console.log(`  Realm total: ${realmAttributes.length}`);
        console.log(`  In TanStack only: ${inTsOnly.length}`, inTsOnly.map(a => ({ id: a.id, invoiceNo: a.invoiceNo, _kf_payrollNo: a._kf_payrollNo })));
        console.log(`  In Realm only: ${inRealmOnly.length}`, inRealmOnly.map(a => ({ id: a.id, invoiceNo: a.invoiceNo, _kf_payrollNo: a._kf_payrollNo })));

        return { inTsOnly, inRealmOnly, tsCount: tsAttributes.length, realmCount: realmAttributes.length };
      },
    };
    // Note: The window.tsDebug object is silently updated on each poll.
    // The "loaded" message is only shown once (on mount) via the separate useEffect below.
  }, [projectListAfterMerge, projectsWithSharedResult, projectsList, awsgwAttributesResult, attributesList]);

  // Log tsDebug availability once on mount (separate from the update useEffect)
  useEffect(() => {
    console.log(
      "%c[tsDebug] Debug utilities loaded. Available commands:\n" +
      "  window.tsDebug.findInvoice(invoiceNo)\n" +
      "  window.tsDebug.findInstaller(payrollNo)\n" +
      "  window.tsDebug.getSharedInstallers()\n" +
      "  window.tsDebug.getMeta()\n" +
      "  window.tsDebug.compareProjects()\n" +
      "  window.tsDebug.getAllProjects()\n" +
      "  window.tsDebug.searchProjects(field, value)\n" +
      "  window.tsDebug.getAllAttributes() - returns {tanstack, realm}\n" +
      "  window.tsDebug.findAttribute(uuid) - compares both sources\n" +
      "  window.tsDebug.compareAttributes()",
      "color: #22c55e; font-weight: bold;"
    );
  }, []); // Empty dependency array - only runs once on mount

  // Set up window callback for triggering polling override from modelRW.context.js
  // This allows change stream events (in Realm) to trigger TanStack polling for verification
  useEffect(() => {
    window._triggerChangeStreamPollingOverride = (pollCount = 5) => {
      // Only activate if user is actually idle (otherwise normal polling handles it)
      if (isUserIdle && shouldPollProjects) {
        console.log(
          `%cModelTsContext - Change stream event received while idle, temporarily resuming polling for ${pollCount} polls`,
          "color: #9370DB; font-weight: bold; background-color: #1a1a1a; padding: 2px 4px;"
        );
        setChangeStreamPollingOverride({
          active: true,
          remainingPolls: pollCount,
          triggeredAt: new Date().toISOString(),
        });
      }
    };

    return () => {
      delete window._triggerChangeStreamPollingOverride;
    };
  }, [isUserIdle, shouldPollProjects]);

  // Set up window callback for triggering ATTRIBUTES polling override from modelRW.context.js
  // This allows attributes change stream events (in Realm) to trigger TanStack polling for verification
  useEffect(() => {
    window._triggerAttributesChangeStreamPollingOverride = (pollCount = 5) => {
      // Only activate if user is actually idle (otherwise normal polling handles it)
      if (isUserIdle && shouldPollAttributes) {
        console.log(
          `%cModelTsContext - Attributes change stream event received while idle, temporarily resuming polling for ${pollCount} polls`,
          "color: #9370DB; font-weight: bold; background-color: #1a1a1a; padding: 2px 4px;"
        );
        setAttributesChangeStreamPollingOverride({
          active: true,
          remainingPolls: pollCount,
          triggeredAt: new Date().toISOString(),
        });
      }
    };

    return () => {
      delete window._triggerAttributesChangeStreamPollingOverride;
    };
  }, [isUserIdle, shouldPollAttributes]);

  // - - - - - - - - - - - - - - - - -  CONTEXT RETURN  - - - - - - - - - - - - - - - - - -

  return (
    <ModelTsContext.Provider
      value={{
        shouldFetchUniqueLocations,
        shouldFetchOfficeMetaData,
        shouldFetchUser,
        shouldFetchUsers,
        shouldFetchProject,
        shouldFetchProjects,
        shouldFetchCandidates,
        shouldFetchCandidatesAdditional,
        shouldFetchProjectsAdditional,
        shouldFetchAttributes,
        shouldFetchLogs,

        setShouldFetchUniqueLocations,
        setShouldFetchOfficeMetaData,
        setShouldFetchUser,
        setShouldFetchUsers,
        setShouldFetchProject,
        setShouldFetchProjects,
        setShouldFetchCandidates,
        setShouldFetchCandidatesAdditional,
        setShouldFetchProjectsAdditional,
        setShouldFetchAttributes,
        setShouldFetchLogs,

        setAwsgwUniqueLocationsResult,
        setAwsgwOfficeMetaDataResult,
        setAwsgwUserResult,
        setAwsgwUsersResult,
        setAwsgwProjectResult,
        setAwsgwProjectsResult,
        setAwsgwCandidatesResult,
        setAwsgwCandidatesAdditionalResult,
        setAwsgwProjectsAdditionalResult,
        setAwsgwAttributesResult,
        setAwsgwLogsResult,

createAdditionalCandidateQueryList,
        awsgwUniqueLocationsResult,
        awsgwOfficeMetaDataResult,
        awsgwUserResult,
        awsgwUsersResult,
        awsgwProjectResult,
        awsgwProjectsResult,
        awsgwCandidatesResult,
        awsgwCandidatesAdditionalResult,
        awsgwProjectsAdditionalResult,
        awsgwAttributesResult,
        awsgwLogsResult,

        // Time Machine #114 Phase 4.5 — historical-state derived arrays
        // produced by in-memory replay. ScheduleContext branches on
        // isTimeTraveling to select these vs the live awsgw* arrays.
        // Offices intentionally omitted (rarely changes; live used).
        timeTravelProjectsResult,
        timeTravelCandidatesResult,
        timeTravelAttributesResult,
        // Merged sorted event list (Attributes + ActiveQueue only) for the
        // TimeSlider component to render tick marks. Recomputed when bundles
        // arrive; stable identity until the date or location changes.
        timeTravelSliderEvents,
        timeTravelMeta,
        timeTravelError,
        timeTravelLoading,
        // Time Machine #114 Phase 6 — Diff Pair right-state derivations + diff.
        // Right-state arrays are non-empty only when isDiffPairView is true.
        // timeTravelDiff is the structured projectDiffs+attributeDiffs object
        // computed by computeScheduleDiff, or null when not in DiffPair mode.
        timeTravelProjectsResultRight,
        timeTravelCandidatesResultRight,
        timeTravelAttributesResultRight,
        timeTravelDiff,

        awsgwGetUniqueLocations,
        awsgwGetOfficeMetaDataByLocation,
        awsgwGetUserById,
        awsgwGetUsers,
        awsgwGetProjectById,
        awsgwGetProjectsByCompany,
        awsgwGetCandidatesByLocation,
        awsgwGetCandidatesByPayrollNoList,
        awsgwGetProjectsByPayrollNoList,
        awsgwGetAttributesByLocation,
        awsgwGetLogs,
        awsgwGetLogsChartData,
        awsgwGetLogsUserActivityTime,
        setShouldFetchLogsChartData,
        setShouldFetchLogsActivityTime,

        logsChartDataResult,
        logsChartDataFetching,
        logsActivityTimeResult,
        logsActivityTimeFetching,
        logsUniqueUsersResult,

        // Attributes Performance Chart
        attributesPerformanceResult,
        attributesPerformanceFetching,
        setShouldFetchAttributesPerformance,
        setAttributesPerformanceParams,

        uniqueLocationsFetching,
        officeMetaDataFetching,
        userFetching,
        usersFetching,
        projectFetching,
        projectsFetching,
        candidatesFetching,
        candidatesAdditionalFetching,
        projectsAdditionalFetching,
        attributesFetching,
        logsFetching,
        queryExecutionTimes,

        setExpectedQueries,
        setCompletedQueries,
        setConsolidatedLogQueries,
        resetQueryCompletionTracking,
        setRealmRequestedProjectsList,
        
        logsUserFilter,
        logsDateFilter,
        logsDaysFilter,
        setLogsUserFilter,
        setLogsDateFilter,
        setLogsDaysFilter,
        
        logsPage,
        logsPageSize,
        logsSortColumn,
        logsSortDirection,
        logsTotalCount,
        setLogsPage,
        setLogsPageSize,
        setLogsSortColumn,
        setLogsSortDirection,

        // Polling control
        shouldPollProjects,
        setShouldPollProjects,
        shouldPollAttributes,
        setShouldPollAttributes,
        attributesPollingFetching,

        // Change stream polling override - allows temporarily resuming polling when
        // change stream events arrive while idle, for verification purposes
        triggerChangeStreamPollingOverride: (pollCount = 5) => {
          // Only activate if user is actually idle (otherwise normal polling handles it)
          if (isUserIdle && shouldPollProjects) {
            console.log(
              `%cModelTsContext - Change stream event received while idle, temporarily resuming polling for ${pollCount} polls`,
              "color: #9370DB; font-weight: bold; background-color: #1a1a1a; padding: 2px 4px;"
            );
            setChangeStreamPollingOverride({
              active: true,
              remainingPolls: pollCount,
              triggeredAt: new Date().toISOString(),
            });
          }
        },

        // Failed query tracking for UI feedback
        // failedQueries: Set of critical queries that failed after all retries
        // retryingQueries: Set of queries currently being retried
        failedQueries,
        retryingQueries,

        // Authentication (for future cutover from modelRW.context)
        mongoUser,
        userLoading,
        loginMsal,
        logoutUser,
        tsUserDocument,
        setTsUserDocument,

        // Mutations
        setUserById,
        setScheduleById,
        setSuppressionById,
        setAttributeById,
        createNewAttribute,

        // TanStack attribute wrapper functions (for tanstackOnly users)
        myJsonArraySort,
        tsCreateJobAttribute,
        tsCreateExtraCellAttribute,
        tsRemoveExtraCellAttribute,
        tsCreateCrewDefaultsAttribute,
        tsUpdateAttributeList,
        tsToggleCellLock,
        tsUpdateUnassignedAttributeList,

        // TanStack data-source location markers
        // (#79: awsgwAttributesResult / awsgwProjectsResult / awsgwCandidatesResult
        //  themselves are exposed earlier in this object — listing them again here
        //  silently overwrote the earlier entries with the same values, but ESLint
        //  flagged it as "duplicate key" because the language allows it without
        //  warning. Keeping only the *Location markers here, which are unique.)
        awsgwAttributesResultLocation, // Location that awsgwAttributesResult data belongs to
        awsgwCandidatesResultLocation, // Location that awsgwCandidatesResult data belongs to

        // TanStack project data (for UI switch when conversion="tanstackOnly")
        projectListAfterMerge,
        projectListAfterMergeLocation, // Location that projectListAfterMerge data belongs to

        // Loading states (for App.js spinner)
        locationsLoading: uniqueLocationsLoading,
        projectsLoading,
        candidatesLoading,
        attributesLoading,
      }}
    >
      {children}
    </ModelTsContext.Provider>
  );
};
